import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../client/lib/useAuth';
import { ProjectService } from '../client/lib/projects';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../client/components/ui/card';
import { Button } from '../client/components/ui/button';
import { FileText, Download, AlertCircle, CheckCircle, Clock, Play, Loader2, Eye, EyeOff, ArrowLeft, Trash2 } from 'lucide-react';
import { AdvancedPDFViewer } from '../components/AdvancedPDFViewer';
import { ResizablePanels } from '../components/ResizablePanels';
import CostEstimator from '../client/components/CostEstimator';
import PaymentModal from '../client/components/PaymentModal';
import StageC from '../components/StageC';
import { StageD } from '../components/StageD';

// Add CSS for bump effect
const highlightStyles = `
  .bump-effect {
    animation: bump 0.6s ease-in-out;
  }
  
  @keyframes bump {
    0% { 
      transform: scale(1);
    }
    25% { 
      transform: scale(1.05);
      background-color: #EFF6FF;
    }
    50% { 
      transform: scale(1.1);
      background-color: #DBEAFE;
    }
    75% { 
      transform: scale(1.05);
      background-color: #EFF6FF;
    }
    100% { 
      transform: scale(1);
    }
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.textContent = highlightStyles;
    document.head.appendChild(styleElement);
}

// Debug flag - set to false to disable debugging
const DEBUG_MEMORY = true;

// Memory monitoring utility
const memoryMonitor = {
    startTime: Date.now(),
    lastCheck: Date.now(),
    checkInterval: 30000, // 30 seconds
    
    logMemoryUsage() {
        if (!DEBUG_MEMORY) return;
        
        const now = Date.now();
        if (now - this.lastCheck < this.checkInterval) return;
        
        this.lastCheck = now;
        const runtime = Math.round((now - this.startTime) / 1000);
        
        if ((performance as any).memory) {
            const memory = (performance as any).memory;
            console.log(`[MEMORY-${runtime}s] Used: ${Math.round(memory.usedJSHeapSize / 1024 / 1024)}MB, Total: ${Math.round(memory.totalJSHeapSize / 1024 / 1024)}MB, Limit: ${Math.round(memory.jsHeapSizeLimit / 1024 / 1024)}MB`);
        }
        
        // Count DOM elements
        const elements = document.querySelectorAll('*').length;
        const canvases = document.querySelectorAll('canvas').length;
        const pdfs = document.querySelectorAll('[data-pdf-viewer]').length;
        
        console.log(`[DOM-${runtime}s] Elements: ${elements}, Canvases: ${canvases}, PDF Viewers: ${pdfs}`);
    }
};

// Hook to track component re-renders
const useRenderTracker = (componentName: string, props: any) => {
    const renderCount = useRef(0);
    const prevProps = useRef(props);
    
    renderCount.current++;
    
    if (DEBUG_MEMORY && renderCount.current % 10 === 0) {
        console.log(`[RENDER-COUNT] ${componentName}: ${renderCount.current} renders`);
        
        // Check for prop changes
        const changedProps = Object.keys(props).filter(
            key => props[key] !== prevProps.current[key]
        );
        
        if (changedProps.length > 0) {
            console.log(`[RENDER-PROPS] ${componentName} changed:`, changedProps);
        }
    }
    
    prevProps.current = props;
};

// Hook to track effect cleanup
const useEffectCleanupTracker = (name: string) => {
    useEffect(() => {
        if (DEBUG_MEMORY) console.log(`[EFFECT-MOUNT] ${name}`);
        return () => {
            if (DEBUG_MEMORY) console.log(`[EFFECT-CLEANUP] ${name}`);
        };
    }, [name]);
};

// Hook to track fetch requests
const useFetchTracker = () => {
    useEffect(() => {
        if (!DEBUG_MEMORY) return;
        
        const originalFetch = window.fetch;
        let requestCount = 0;
        
        window.fetch = async (...args) => {
            requestCount++;
            const url = args[0];
            console.log(`[FETCH-${requestCount}] ${url}`);
            
            try {
                const response = await originalFetch(...args);
                console.log(`[FETCH-${requestCount}] Response: ${response.status}`);
                return response;
            } catch (error) {
                console.log(`[FETCH-${requestCount}] Error:`, error);
                throw error;
            }
        };
        
        return () => {
            window.fetch = originalFetch;
        };
    }, []);
};

// Hook to track CSV data growth
const useCSVDataTracker = (csvData: any[]) => {
    const prevLength = useRef(0);
    
    useEffect(() => {
        if (!DEBUG_MEMORY) return;
        
        const currentLength = csvData?.length || 0;
        if (currentLength !== prevLength.current) {
            console.log(`[CSV-DATA] Length changed: ${prevLength.current} -> ${currentLength}`);
            
            if (currentLength > 10000) {
                console.warn(`[CSV-DATA] Large dataset detected: ${currentLength} rows`);
            }
        }
        
        prevLength.current = currentLength;
    }, [csvData]);
};

// Hook to track polling behavior
const usePollingTracker = (pollingIntervalId: NodeJS.Timeout | null, projectId: string) => {
    useEffect(() => {
        if (!DEBUG_MEMORY) return;
        
        if (pollingIntervalId) {
            console.log(`[POLLING] Started for project ${projectId}`);
        } else {
            console.log(`[POLLING] Stopped for project ${projectId}`);
        }
    }, [pollingIntervalId, projectId]);
};

// Hook to track component lifecycle
const useComponentLifecycle = (componentName: string) => {
    useEffect(() => {
        if (DEBUG_MEMORY) console.log(`[LIFECYCLE] ${componentName} mounted`);
        
        return () => {
            if (DEBUG_MEMORY) console.log(`[LIFECYCLE] ${componentName} unmounting`);
        };
    }, [componentName]);
    
    useEffect(() => {
        return () => {
            if (DEBUG_MEMORY) console.log(`[LIFECYCLE] ${componentName} cleanup on dependency change`);
        };
    });
};

interface ProjectStatus {
    status: string;
    files: string[];
    originalFilename: string;
    error?: string;
    reviewStage?: 'B' | 'C' | 'C1' | 'C2' | 'C3' | 'D'; // Added C3 for three-stage grouping
    reviewData?: {
        suggestions?: any[];
        uniqueTerms?: string[];
        availableTerms?: string[];
        // New Stage D context collection data
        termsWithContext?: any[];
        workflowId?: string;
        isLoading?: boolean;
    };
    pageRange?: { startPage?: number; endPage?: number; endnotesEnabled?: boolean; endnotesStartPage?: number }; // CRITICAL: Page range for endnotes logic
}

interface Project {
    id: string;
    name: string;
    description?: string;
    status: 'pending' | 'processing' | 'completed' | 'error';
    originalFilename: string;
    createdAt: string;
    updatedAt: string;
    userId: string;
    files: string[];
    error?: string;
    reviewStage?: 'B' | 'C' | 'C1' | 'C2' | 'C3' | 'D'; // Added C3 for three-stage grouping
}

interface Suggestion {
    originalTerm: string;
    suggestedTerm: string;
    page: number;
    context: any;
}

// Interface for added terms with complete information
interface AddedTerm {
    term: string;
    page: string;
    context: string;
}

// Add a CSV viewer component with safe error handling and proper search functionality
const CSVViewer: React.FC<{ 
    projectId: string; 
    filename: string; 
    reviewStage?: string; 
    reviewData?: any; 
    showHeader?: boolean;
    relationshipData?: {
        subentries: { [key: string]: string };
        crossReferences: { [key: string]: string[] };
        mergedTerms?: { [key: string]: string };
    } | null; 
    onTermUpdate?: (updates: Record<string, string>) => void; 
    onUniqueTermsCountChange?: (count: number) => void; 
    onTermClick?: (row: any) => void;
    onScrollToTermReady?: (scrollToTermFn: (termToFind: string) => void) => void; // New prop to expose enhanced scroll function
    currentStatus?: string; // Add current status prop for styling logic
    allowDelete?: boolean; // New prop to enable delete functionality
    allowAdd?: boolean; // New prop to enable add functionality
    onRowsDeleted?: (deletedRows: string[]) => void; // New prop for handling deleted rows
    onRowAdded?: (newRow: any) => void; // New prop for handling added rows
    onGetDeletedRows?: (getDeletedRowsFn: () => string[]) => void; // New prop to expose deleted rows function
    onGetModifiedCSVData?: (getModifiedCSVDataFn: () => any[]) => void; // New prop to expose modified CSV data function
    // New shared state props (like termUpdates)
    deletedRows?: string[]; // Shared deleted rows state from parent
    addedTerms?: string[]; // Shared added terms state from parent
    addedTermsDetails?: Record<string, AddedTerm>; // Complete details for added terms
    onDeletedRowsUpdate?: (deletedRows: string[]) => void; // Callback for deletion updates
    onAddedTermsUpdate?: (addedTerms: string[]) => void; // Callback for addition updates
    // New relationship data props
    c1Decisions?: any[]; // C1 decisions for determining relationships
    c2Decisions?: any[]; // C2 decisions for determining relationships
    c3Decisions?: any[]; // C3 decisions for determining relationships
    c1ParentMap?: Map<string, string>; // C1 subentry relationships
    c1CrossRefMap?: Map<string, string[]>; // C1 cross-reference relationships
    termUpdates?: Record<string, string>; // Shared termUpdates state from parent
    pageRange?: { startPage?: number; endPage?: number; endnotesEnabled?: boolean; endnotesStartPage?: number }; // CRITICAL: Page range for endnotes logic
    makeAuthenticatedRequest?: (url: string, options?: RequestInit, maxRetries?: number) => Promise<Response>; // For authenticated API calls
}> = ({ projectId, filename, reviewStage, reviewData, showHeader, relationshipData, onTermUpdate, onUniqueTermsCountChange, onTermClick, onScrollToTermReady, currentStatus, allowDelete, allowAdd, onRowsDeleted, onRowAdded, onGetDeletedRows, onGetModifiedCSVData, deletedRows = [], addedTerms = [], addedTermsDetails = {}, onDeletedRowsUpdate, onAddedTermsUpdate, c1Decisions = [], c2Decisions = [], c3Decisions = [], c1ParentMap, c1CrossRefMap, termUpdates: parentTermUpdates = {}, pageRange, makeAuthenticatedRequest }) => {
    const [csvData, setCsvData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingTerm, setEditingTerm] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 25;
    const [termColumnWidth, setTermColumnWidth] = useState(200);
    const [pageColumnWidth, setPageColumnWidth] = useState(80);
    const [contextColumnWidth, setContextColumnWidth] = useState(800);
    const [deleteColumnWidth, setDeleteColumnWidth] = useState(50);
    const [subentryColumnWidth, setSubentryColumnWidth] = useState(120);
    const [crossRefColumnWidth, setCrossRefColumnWidth] = useState(130);
    const [isResizing, setIsResizing] = useState(false);
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    
    // Remove local termUpdates state - use parent's instead
    // const [termUpdates, setTermUpdates] = useState<Record<string, string>>({});
    const termUpdates = parentTermUpdates; // Use parent's termUpdates directly
    
    // For the setTermUpdatesWithDebug function in CSVViewer, we'll use onTermUpdate callback
    const setTermUpdatesWithDebug = useCallback((updates: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => {
        if (typeof updates === 'function') {
            const newUpdates = updates(termUpdates);
            console.log(`[CSVViewer-setTermUpdates] Function update - Previous:`, termUpdates);
            console.log(`[CSVViewer-setTermUpdates] Function update - New:`, newUpdates);
            console.trace('[CSVViewer-setTermUpdates] Function update call stack');
            if (onTermUpdate) {
                onTermUpdate(newUpdates);
            }
        } else {
            console.log(`[CSVViewer-setTermUpdates] Direct update - Setting to:`, updates);
            console.log(`[CSVViewer-setTermUpdates] Direct update - Previous was:`, termUpdates);
            console.trace('[CSVViewer-setTermUpdates] Direct update call stack');
            if (onTermUpdate) {
                onTermUpdate(updates);
            }
        }
    }, [termUpdates, onTermUpdate]);
    
    const csvTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    // Add missing lastRowClickRef
    const lastRowClickRef = useRef<number>(0);
    
    // Add ref to track input focus time to prevent immediate blur
    const inputFocusTimeRef = useRef<number>(0);
    
    // Pagination state
    // const [currentPage, setCurrentPage] = useState(1); // DUPLICATE - already declared above
    // const rowsPerPage = 15; // DUPLICATE - already set above to 25
    
    // Delete and Add functionality state - now using shared state from parent
    // const [deletedRows, setDeletedRows] = useState<Set<string>>(new Set()); // REMOVED - using shared state
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [newTerm, setNewTerm] = useState('');
    const [newPage, setNewPage] = useState('');
    const [newContext, setNewContext] = useState('');
    const [addError, setAddError] = useState('');
    // const [newlyAddedTerms, setNewlyAddedTerms] = useState<Set<string>>(new Set()); // REMOVED - using shared state
    const [lastDeleteClickIndex, setLastDeleteClickIndex] = useState<number | null>(null); // Track last clicked delete row for shift selection
    const [isShiftClicking, setIsShiftClicking] = useState(false); // Track if we're in the middle of a shift-click operation
    
    // Convert shared state arrays to Sets for efficient lookups
    const deletedRowsSet = useMemo(() => new Set(deletedRows), [deletedRows]);
    const addedTermsSet = useMemo(() => new Set(addedTerms), [addedTerms]);
    
    // DEBUG: Add debugging hooks for CSVViewer
    useRenderTracker('CSVViewer', { projectId, filename, reviewStage });
    useCSVDataTracker(csvData);
    useComponentLifecycle('CSVViewer');
    useEffectCleanupTracker('CSVViewer-main');
    
    // Simplified helper function for getting base term (term that can be edited)
    const getBaseTerm = useCallback((row: any): string => {
        try {
            if (!row) {
                console.log('[DEBUG] getBaseTerm: row is null/undefined');
                return '';
            }
            
            // Always use the most appropriate term field available, prioritizing standardized terms
            // but falling back to any available term to ensure all rows are editable
            let baseTerm = '';
            
            // Stage-aware base term selection
            // For Stage B (and Stage B Complete), prefer the original extracted term so reverting is frictionless
            if (reviewStage === 'B' || currentStatus === 'Stage B Complete') {
                baseTerm = row.term || row.Term || row.term_standard || row.standardized_term;
            } else {
                // Try standardized terms first (these are the preferred editing targets)
                baseTerm = row.term_standard || row.standardized_term;
            }
            
            // If no preferred term found, fall back to any available term
            if (!baseTerm) {
                baseTerm = row.collected_term || row.term || row.Term || '';
            }
            
            // Ensure we always have something to edit - if all else fails, use the display value
            if (!baseTerm && row) {
                // Find any non-empty string field that looks like a term
                const possibleTermFields = ['term_standard', 'standardized_term', 'collected_term', 'term', 'Term'];
                for (const field of possibleTermFields) {
                    if (row[field] && typeof row[field] === 'string' && row[field].trim()) {
                        baseTerm = row[field].trim();
                        break;
                    }
                }
            }
            
            // Enhanced debugging for problematic terms
            const finalBaseTerm = baseTerm || '';
            if (!finalBaseTerm || finalBaseTerm.trim() === '') {
                console.warn('[DEBUG] getBaseTerm: No valid base term found for row:', {
                    row,
                    availableFields: Object.keys(row),
                    fieldValues: {
                        term_standard: row.term_standard,
                        standardized_term: row.standardized_term,
                        collected_term: row.collected_term,
                        term: row.term,
                        Term: row.Term
                    }
                });
            }
            
            return finalBaseTerm;
        } catch (error) {
            console.error('Error getting base term:', error);
            return '';
        }
    }, []);
    
    // Memoized helper functions to prevent re-computation and infinite loops
    const getDisplayTerm = useCallback((row: any): string => {
        try {
            const baseTerm = getBaseTerm(row);
            // 1) If user has an override, show it
            if (baseTerm && termUpdates[baseTerm]) return termUpdates[baseTerm];
            // 2) In Stage B (or Stage B Complete), prefer standardized value if present
            if ((reviewStage === 'B' || currentStatus === 'Stage B Complete')) {
                const standardized = row.term_standard || row.standardized_term;
                if (typeof standardized === 'string' && standardized.trim()) {
                    return standardized.trim();
                }
            }
            // 3) Fallback to base term
            return baseTerm || '';
        } catch (error) {
            console.error('Error getting display term:', error);
            return '';
        }
    }, [getBaseTerm, termUpdates, reviewStage, currentStatus]);

    // Get the original term that was extracted from the PDF (for search highlighting)
    const getOriginalTerm = useCallback((row: any): string => {
        try {
            if (!row) {
                console.log('[DEBUG] getOriginalTerm: row is null/undefined');
                return '';
            }
            // The 'term' field contains the original extracted term that appears in the PDF
            const originalTerm = row.term || row.Term || '';
            console.log('[DEBUG] getOriginalTerm: row fields:', Object.keys(row));
            console.log('[DEBUG] getOriginalTerm: row.term =', row.term);
            console.log('[DEBUG] getOriginalTerm: row.Term =', row.Term);
            console.log('[DEBUG] getOriginalTerm: returning =', originalTerm);
            return originalTerm;
        } catch (error) {
            console.error('Error getting original term:', error);
            return '';
        }
    }, []);

    // Helper function to generate unique row ID for delete functionality
    const getRowId = useCallback((row: any): string => {
        try {
            if (!row) return '';
            // Create a unique ID based on term and page
            const term = getDisplayTerm(row);
            const page = row.page || row.Page || '';
            return `${term}-${page}`;
        } catch (error) {
            console.error('Error generating row ID:', error);
            return '';
        }
    }, [getDisplayTerm]);

    // Handle row deletion - mark for deletion instead of immediate removal
    const handleRowDelete = useCallback((row: any, isChecked: boolean, rowIndex?: number, shiftKey?: boolean) => {
        const rowId = getRowId(row);
        if (!rowId) return;

        // If shift is held and we have a previous delete click, select range
        if (shiftKey && lastDeleteClickIndex !== null && rowIndex !== undefined) {
            const startIndex = Math.min(lastDeleteClickIndex, rowIndex);
            const endIndex = Math.max(lastDeleteClickIndex, rowIndex);
            
            console.log(`[DEBUG] Shift-click detected: selecting range ${startIndex} to ${endIndex}`);
            
            // Get all rows in the range and toggle their deletion status
            const newDeletedRows = new Set(deletedRowsSet);
            
            // Calculate paginated data for current page to determine range
            const startRowIndex = (currentPage - 1) * rowsPerPage;
            const endRowIndex = startRowIndex + rowsPerPage;
            const currentPageData = csvData.slice(startRowIndex, endRowIndex);
            
            for (let i = startIndex; i <= endIndex; i++) {
                if (i < currentPageData.length) {
                    const rangeRow = currentPageData[i];
                    const rangeRowId = getRowId(rangeRow);
                    
                    if (rangeRowId) {
                        if (isChecked) {
                            newDeletedRows.add(rangeRowId);
                        } else {
                            newDeletedRows.delete(rangeRowId);
                        }
                    }
                }
            }
            
            // Update shared state if callback is provided
            if (onDeletedRowsUpdate) {
                onDeletedRowsUpdate(Array.from(newDeletedRows));
            }
            
            console.log(`[DEBUG] Range selection complete: ${isChecked ? 'marked' : 'unmarked'} ${endIndex - startIndex + 1} rows for deletion`);
        } else {
            // Normal single row deletion
            const newDeletedRows = new Set(deletedRowsSet);
            
            if (isChecked) {
                newDeletedRows.add(rowId);
            } else {
                newDeletedRows.delete(rowId);
            }
            
            // Update shared state if callback is provided
            if (onDeletedRowsUpdate) {
                onDeletedRowsUpdate(Array.from(newDeletedRows));
            }
        }
        
        // Update last click index for future shift selections
        if (rowIndex !== undefined) {
            setLastDeleteClickIndex(rowIndex);
        }
        
    }, [getRowId, deletedRowsSet, onDeletedRowsUpdate, lastDeleteClickIndex, csvData, currentPage, rowsPerPage]);

    // Handle adding new row
    const handleAddRow = useCallback(() => {
        console.log(`[CSVViewer] handleAddRow called! newTerm: "${newTerm}", newPage: "${newPage}"`);
        setAddError('');
        
        // Validate inputs
        if (!newTerm.trim()) {
            console.log(`[CSVViewer] handleAddRow failed: empty term`);
            setAddError('Term is required');
            return;
        }
        if (!newPage.trim()) {
            console.log(`[CSVViewer] handleAddRow failed: empty page`);
            setAddError('Page is required');
            return;
        }
        
        console.log(`[CSVViewer] handleAddRow validation passed, checking for duplicates...`);
        console.log(`[CSVViewer] csvData length:`, csvData.length);
        console.log(`[CSVViewer] deletedRowsSet size:`, deletedRowsSet.size);
        
        // Check if term/page combination already exists
        const termPageCombo = `${newTerm.trim()}-${newPage.trim()}`;
        console.log(`[CSVViewer] Looking for duplicate of:`, termPageCombo);
        
        const existsInData = csvData.some(row => {
            const existingCombo = getRowId(row);
            const isDeleted = deletedRowsSet.has(existingCombo);
            console.log(`[CSVViewer] Checking row: ${existingCombo}, deleted: ${isDeleted}`);
            return existingCombo === termPageCombo && !isDeleted;
        });
        
        if (existsInData) {
            console.log(`[CSVViewer] handleAddRow failed: duplicate found for ${termPageCombo}`);
            setAddError('This term/page combination already exists in the table');
            return;
        }
        
        console.log(`[CSVViewer] handleAddRow duplicate check passed, creating new row...`);
        
        // Create new row object
        const newRow = {
            term: newTerm.trim(),
            page: newPage.trim(),
            context: newContext.trim() || '',
            // Add other fields that might be expected
            term_standard: newTerm.trim(),
            standardized_term: newTerm.trim(),
            collected_term: newTerm.trim()
        };
        
        // Add to CSV data and sort it
        setCsvData(prev => {
            const newData = [...prev, newRow];
            console.log(`[CSVViewer] Before sorting - data length: ${newData.length}`);
            console.log(`[CSVViewer] New term being added: "${newTerm.trim()}"`);
            
            // Sort the data alphabetically by term, then by page
            const sortedData = newData.sort((a, b) => {
                try {
                    const termA = (a.term_standard || a.standardized_term || a.term || a.Term || '').toLowerCase();
                    const termB = (b.term_standard || b.standardized_term || b.term || b.Term || '').toLowerCase();
                    
                    if (termA !== termB) {
                        return termA.localeCompare(termB);
                    }
                    
                    // Sort by page number if terms are the same
                    const pageA = parseInt((a.page || a.Page || '0').toString()) || 0;
                    const pageB = parseInt((b.page || b.Page || '0').toString()) || 0;
                    return pageA - pageB;
                } catch (sortError) {
                    console.warn('[CSVViewer] Error sorting rows after add:', sortError);
                    return 0;
                }
            });
            
            console.log(`[CSVViewer] After sorting - first 5 terms:`, sortedData.slice(0, 5).map(row => 
                row.term_standard || row.standardized_term || row.term || row.Term || ''
            ));
            
            return sortedData;
        });
        
        // Track this as a newly added term
        if (onAddedTermsUpdate) {
            const updatedAddedTerms = [...addedTerms, newTerm.trim()];
            console.log(`[CSVViewer] Updating addedTerms: ${JSON.stringify(addedTerms)} -> ${JSON.stringify(updatedAddedTerms)}`);
            onAddedTermsUpdate(updatedAddedTerms);
        }
        console.log(`[CSVViewer] Added new term to tracking: ${newTerm.trim()}`);
        
        // Notify parent component to store term details
        if (onRowAdded) {
            console.log(`[CSVViewer] Notifying parent about new row:`, newRow);
            onRowAdded(newRow);
        }
        
        console.log(`[CSVViewer] handleAddRow completed successfully for term: "${newTerm.trim()}"`);
        console.log(`[CSVViewer] Current addedTerms after addition:`, addedTerms);
        console.log(`[CSVViewer] Current addedTermsDetails after addition:`, addedTermsDetails);
        
        // Reset form and close dialog
        setNewTerm('');
        setNewPage('');
        setNewContext('');
        setShowAddDialog(false);
    }, [newTerm, newPage, newContext, csvData, deletedRowsSet, getRowId, getDisplayTerm, onRowAdded, onScrollToTermReady, rowsPerPage, addedTerms, onAddedTermsUpdate]);

    // Get unique terms count - memoized to prevent excessive recalculation
    const uniqueTermsCount = useMemo(() => {
        try {
            if (!csvData || csvData.length === 0) return 0;
            
            // Use the same logic as color mapping for consistency
            const allUniqueTerms = new Set<string>();
            csvData.forEach(row => {
                const rowId = getRowId(row);
                // For Stage B, include deleted rows in count to maintain consistency with display
                // For other stages, exclude deleted rows from count
                const shouldIncludeRow = (reviewStage === 'B' || currentStatus === 'Stage B Complete') ? true : !deletedRowsSet.has(rowId);
                
                if (shouldIncludeRow) {
                    const term = getDisplayTerm(row);
                    if (term) {
                        // Check if this term was merged into another term
                        const baseTerm = getBaseTerm(row);
                        const mergedInto = relationshipData?.mergedTerms?.[term] || relationshipData?.mergedTerms?.[baseTerm];
                        
                        if (mergedInto) {
                            // If this term was merged, count the main term instead
                            allUniqueTerms.add(mergedInto);
                        } else {
                            // Otherwise count the term itself
                            allUniqueTerms.add(term);
                        }
                    }
                }
            });
            
            return allUniqueTerms.size;
        } catch (error) {
            console.error('Error calculating unique terms:', error);
            return 0;
        }
    }, [csvData, getDisplayTerm, getBaseTerm, deletedRowsSet, getRowId, reviewStage, currentStatus, relationshipData]);

    // Update parent component with unique terms count
    useEffect(() => {
        try {
            if (onUniqueTermsCountChange) {
                onUniqueTermsCountChange(uniqueTermsCount);
            }
        } catch (error) {
            console.error('Error updating unique terms count:', error);
        }
    }, [uniqueTermsCount, onUniqueTermsCountChange]);

    // Expose deleted rows function to parent component
    // REMOVED: This was causing infinite loops with shared state approach
    // useEffect(() => {
    //     if (onGetDeletedRows) {
    //         onGetDeletedRows(() => Array.from(deletedRowsSet));
    //     }
    // }, [deletedRowsSet]); // Removed onGetDeletedRows from dependencies since it's now stable

    // Expose modified CSV data function to parent component
    useEffect(() => {
        if (onGetModifiedCSVData) {
            onGetModifiedCSVData(() => {
                // Start with original CSV data excluding deleted rows and with term updates applied
                const modifiedOriginalData = csvData
                    .filter(row => !deletedRowsSet.has(getRowId(row)))
                    .map(row => {
                        // Apply term updates to each row
                        const baseTerm = getBaseTerm(row);
                        if (baseTerm && termUpdates[baseTerm]) {
                            // Create a copy of the row with the updated term
                            const updatedRow = { ...row };
                            
                            // Update the appropriate term field(s) with the new value
                            // We need to update the field that's used for display and processing
                            if (row.term_standard !== undefined) {
                                updatedRow.term_standard = termUpdates[baseTerm];
                            }
                            if (row.standardized_term !== undefined) {
                                updatedRow.standardized_term = termUpdates[baseTerm];
                            }
                            // Also update the base term field if it exists
                            if (row.term !== undefined) {
                                updatedRow.term = termUpdates[baseTerm];
                            }
                            
                            console.log(`[CSVViewer] Applied term update: ${baseTerm} -> ${termUpdates[baseTerm]}`);
                            return updatedRow;
                        }
                        return row;
                    });

                // Create CSV rows for newly added terms that are NOT already in csvData
                const addedTermRows: any[] = [];
                if (addedTerms && addedTerms.length > 0) {
                    console.log(`[CSVViewer] Checking ${addedTerms.length} added terms for inclusion:`, addedTerms);
                    
                    // Get the structure from the first existing row to match CSV format
                    const sampleRow = csvData.length > 0 ? csvData[0] : {};
                    const csvHeaders = Object.keys(sampleRow);
                    
                    // Get all terms currently in csvData (including those marked as added)
                    const existingTerms = new Set<string>();
                    csvData.forEach(row => {
                        const term = row.term_standard || row.standardized_term || row.term || '';
                        if (term) {
                            existingTerms.add(term.toLowerCase().trim());
                        }
                    });
                    
                    addedTerms.forEach(termName => {
                        // Only create a CSV row if this term is NOT already in csvData
                        if (!existingTerms.has(termName.toLowerCase().trim())) {
                            // Create a new row with the same structure as existing CSV rows
                            const newRow: any = {};
                            
                            // Initialize all fields to empty strings to match CSV structure
                            csvHeaders.forEach(header => {
                                newRow[header] = '';
                            });
                            
                            // Set the term fields with the added term name
                            if (newRow.hasOwnProperty('term_standard')) {
                                newRow.term_standard = termName;
                            }
                            if (newRow.hasOwnProperty('standardized_term')) {
                                newRow.standardized_term = termName;
                            }
                            if (newRow.hasOwnProperty('term')) {
                                newRow.term = termName;
                            }
                            
                            // Set default values for required fields, but use stored details if available
                            const termDetails = addedTermsDetails[termName];
                            if (newRow.hasOwnProperty('page')) {
                                newRow.page = termDetails?.page || '0'; // Use stored page or default
                            }
                            if (newRow.hasOwnProperty('context')) {
                                newRow.context = termDetails?.context || 'User-added term'; // Use stored context or default
                            }
                            
                            // Mark this as a newly added term for identification
                            newRow._isAddedTerm = true;
                            
                            addedTermRows.push(newRow);
                            console.log(`[CSVViewer] Created CSV row for added term "${termName}" (not in csvData)`);
                        } else {
                            console.log(`[CSVViewer] Skipped creating CSV row for added term "${termName}" (already in csvData)`);
                        }
                    });
                }

                // Combine original modified data with added term rows
                const finalData = [...modifiedOriginalData, ...addedTermRows];
                
                console.log(`[CSVViewer] Final modified CSV data: ${modifiedOriginalData.length} original rows + ${addedTermRows.length} added rows = ${finalData.length} total rows`);
                
                return finalData;
            });
        }
    }, [csvData, deletedRowsSet, termUpdates, getBaseTerm, getRowId, addedTerms, addedTermsDetails]); // Added addedTerms to dependencies

    // CSV loading logic with better error handling and safer edit preservation
    useEffect(() => {
        const fetchCSV = async () => {
            try {
                setIsLoading(true);
                setError(null);
                
                if (!projectId || !filename) {
                    throw new Error('Missing project ID or filename');
                }

                const { getApiUrl } = await import('../client/lib/config');
                const encoded = encodeURIComponent(filename);
                console.log(`[CSVViewer] Fetching CSV: ${getApiUrl(`/uploads/${projectId}/${encoded}`)}`);
                
                const response = await fetch(getApiUrl(`/uploads/${projectId}/${encoded}`));
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
                }
                
                const csvText = await response.text();
                const lines = csvText.split('\n').filter(line => line.trim());
                
                if (lines.length < 2) {
                    console.log('[CSVViewer] CSV file is empty or has no data rows');
                    setCsvData([]);
                    return;
                }
                
                // Parse CSV headers
                const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
                console.log('[CSVViewer] CSV headers:', headers);
                
                // Parse CSV data with robust parsing
                const data: any[] = [];
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (!line) continue;
                    
                    try {
                        // Simple CSV parsing - handle quoted fields
                        const values: string[] = [];
                        let current = '';
                        let inQuotes = false;
                        
                        for (let j = 0; j < line.length; j++) {
                            const char = line[j];
                            if (char === '"') {
                                inQuotes = !inQuotes;
                            } else if (char === ',' && !inQuotes) {
                                values.push(current.trim());
                                current = '';
                            } else {
                                current += char;
                            }
                        }
                        values.push(current.trim()); // Add the last value
                        
                        // Create row object
                        const row: any = {};
                        headers.forEach((header, index) => {
                            const value = values[index] || '';
                            // Convert page to number if it's numeric
                            if (header.toLowerCase() === 'page' && /^\d+$/.test(value)) {
                                row[header] = parseInt(value, 10);
                            } else {
                                row[header] = value;
                            }
                        });
                        
                        // APPLY ENDNOTES LOGIC: Create displayPage field for early stages
                        if (typeof row.page === 'number' && !row.displayPage) {
                            const pr = pageRange || { startPage: 1 };
                            // DEBUG: Uncomment to troubleshoot endnotes logic
                            // console.log(`[CSV Parse] ENDNOTES DEBUG for term "${row.term}":`, { pageNum: row.page, pageRange: pr });
                            
                            if (pr.endnotesEnabled && typeof pr.endnotesStartPage === 'number') {
                                const startPage = pr.startPage || 1;
                                const pdfPageNum = row.page + (startPage - 1);
                                if (pdfPageNum >= pr.endnotesStartPage) {
                                    row.displayPage = `${row.page}n`;
                                    // console.log(`[CSV Parse] ✅ CREATED displayPage with 'n': ${row.displayPage}`);
                                } else {
                                    row.displayPage = row.page.toString();
                                    // console.log(`[CSV Parse] ❌ CREATED displayPage without 'n': ${row.displayPage}`);
                                }
                            } else {
                                row.displayPage = row.page.toString();
                                // console.log(`[CSV Parse] ❌ ENDNOTES NOT ENABLED, displayPage: ${row.displayPage}`);
                            }
                        }
                        
                        // Only add row if it has some non-empty values
                        if (Object.values(row).some(val => val)) {
                            data.push(row);
                        }
                    } catch (parseError) {
                        console.warn(`[CSVViewer] Error parsing line ${i + 1}:`, parseError);
                        continue;
                    }
                }

                console.log(`[CSVViewer] Parsed ${data.length} data rows from CSV`);
                
                // Sort data safely
                try {
                    const sortedData = data.sort((a, b) => {
                        try {
                            const termA = (a.term_standard || a.standardized_term || a.term || a.Term || '').toLowerCase();
                            const termB = (b.term_standard || b.standardized_term || b.term || b.Term || '').toLowerCase();
                            
                            if (termA !== termB) {
                                return termA.localeCompare(termB);
                            }
                            
                            // Sort by page number if terms are the same
                            const pageA = parseInt((a.page || a.Page || '0').toString()) || 0;
                            const pageB = parseInt((b.page || b.Page || '0').toString()) || 0;
                            return pageA - pageB;
                        } catch (sortError) {
                            console.warn('[CSVViewer] Error sorting rows:', sortError);
                            return 0;
                        }
                    });
                    
                    setCsvData(sortedData);
                    console.log(`[CSVViewer] CSV data loaded and sorted successfully`);
                    
                    // REMOVED: No longer reload term edits after CSV load since auto-save handles this
                    // This was causing the appearance of page reloads during auto-save
                    } catch (sortError) {
                        console.error('[CSVViewer] Error sorting data:', sortError);
                        setCsvData(data); // Use unsorted data if sorting fails
                    }
                
            } catch (err: any) {
                console.error('[CSVViewer] Error loading CSV:', err);
                setError(`Failed to load CSV data: ${err.message}`);
                setCsvData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCSV();
    }, [projectId, filename]); // Removed onTermUpdate since it's not being passed to CSVViewer anyway
    
    // NEW: Merge added terms into csvData when addedTerms or addedTermsDetails change
    useEffect(() => {
        if (csvData.length > 0 && addedTerms.length > 0) {
            console.log(`[CSVViewer] Merging ${addedTerms.length} added terms into csvData`);
            
            // Get existing terms to avoid duplicates
            const existingTerms = new Set<string>();
            csvData.forEach(row => {
                const term = row.term_standard || row.standardized_term || row.term || '';
                if (term) {
                    existingTerms.add(term.toLowerCase().trim());
                }
            });
            
            // Create rows for added terms that aren't already in csvData
            const addedTermRows: any[] = [];
            const sampleRow = csvData[0]; // Use first row as template for structure
            const csvHeaders = Object.keys(sampleRow);
            
            addedTerms.forEach(termName => {
                if (!existingTerms.has(termName.toLowerCase().trim())) {
                    // Create a new row with the same structure as existing CSV rows
                    const newRow: any = {};
                    
                    // Initialize all fields to empty strings to match CSV structure
                    csvHeaders.forEach(header => {
                        newRow[header] = '';
                    });
                    
                    // Set the term fields with the added term name
                    if (newRow.hasOwnProperty('term_standard')) {
                        newRow.term_standard = termName;
                    }
                    if (newRow.hasOwnProperty('standardized_term')) {
                        newRow.standardized_term = termName;
                    }
                    if (newRow.hasOwnProperty('term')) {
                        newRow.term = termName;
                    }
                    
                    // Set values from stored details if available
                    const termDetails = addedTermsDetails[termName];
                    if (newRow.hasOwnProperty('page')) {
                        newRow.page = termDetails?.page || '0';
                    }
                    if (newRow.hasOwnProperty('context')) {
                        newRow.context = termDetails?.context || 'User-added term';
                    }
                    
                    // Mark this as a newly added term for identification
                    newRow._isAddedTerm = true;
                    
                    addedTermRows.push(newRow);
                    console.log(`[CSVViewer] Created csvData row for added term "${termName}"`);
                }
            });
            
            if (addedTermRows.length > 0) {
                setCsvData(prevData => {
                    const mergedData = [...prevData, ...addedTermRows];
                    
                    // Sort the merged data
                    const sortedMergedData = mergedData.sort((a, b) => {
                        try {
                            const termA = (a.term_standard || a.standardized_term || a.term || a.Term || '').toLowerCase();
                            const termB = (b.term_standard || b.standardized_term || b.term || b.Term || '').toLowerCase();
                            
                            if (termA !== termB) {
                                return termA.localeCompare(termB);
                            }
                            
                            // Sort by page number if terms are the same
                            const pageA = parseInt((a.page || a.Page || '0').toString()) || 0;
                            const pageB = parseInt((b.page || b.Page || '0').toString()) || 0;
                            return pageA - pageB;
                        } catch (sortError) {
                            console.warn('[CSVViewer] Error sorting merged data:', sortError);
                            return 0;
                        }
                    });
                    
                    console.log(`[CSVViewer] Merged and sorted ${addedTermRows.length} added terms into csvData. Total rows: ${sortedMergedData.length}`);
                    return sortedMergedData;
                });
            }
        }
    }, [csvData.length, addedTerms, addedTermsDetails]); // Re-run when addedTerms or addedTermsDetails change

    // Cleanup CSV timeout on unmount
    useEffect(() => {
        return () => {
            if (csvTimeoutRef.current) {
                clearTimeout(csvTimeoutRef.current);
            }
        };
    }, []);

    // Handle resize functionality for all columns
    const handleMouseDown = useCallback((columnType: 'delete' | 'term' | 'page' | 'subentry' | 'crossref' | 'context') => {
        return (e: React.MouseEvent) => {
            e.preventDefault();
            setIsResizing(true);
            
            const startX = e.clientX;
            let startWidth: number;
            let setColumnWidth: (width: number) => void;
            let minWidth: number;
            let maxWidth: number;
            
            // Configure based on column type
            switch (columnType) {
                case 'delete':
                    startWidth = deleteColumnWidth;
                    setColumnWidth = setDeleteColumnWidth;
                    minWidth = 40;
                    maxWidth = 120;
                    break;
                case 'term':
                    startWidth = termColumnWidth;
                    setColumnWidth = setTermColumnWidth;
                    minWidth = 120;
                    maxWidth = 600;
                    break;
                case 'page':
                    startWidth = pageColumnWidth;
                    setColumnWidth = setPageColumnWidth;
                    minWidth = 60;
                    maxWidth = 200;
                    break;
                case 'subentry':
                    startWidth = subentryColumnWidth;
                    setColumnWidth = setSubentryColumnWidth;
                    minWidth = 80;
                    maxWidth = 200;
                    break;
                case 'crossref':
                    startWidth = crossRefColumnWidth;
                    setColumnWidth = setCrossRefColumnWidth;
                    minWidth = 80;
                    maxWidth = 250;
                    break;
                case 'context':
                    startWidth = contextColumnWidth;
                    setColumnWidth = setContextColumnWidth;
                    minWidth = 200;
                    maxWidth = 1200;
                    break;
                default:
                    return;
            }
            
            const handleMouseMove = (e: MouseEvent) => {
                const deltaX = e.clientX - startX;
                const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));
                setColumnWidth(newWidth);
            };
            
            const handleMouseUp = () => {
                setIsResizing(false);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
            
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        };
    }, [deleteColumnWidth, termColumnWidth, pageColumnWidth, subentryColumnWidth, crossRefColumnWidth, contextColumnWidth]);

    // Handle term editing
    const handleTermEdit = useCallback((rowId: string, newTerm: string, forceClose: boolean = false) => {
        try {
            // Extract the original term from the rowId using a more robust approach
            // The rowId format is now: {originalTermLength}:|{originalTerm}|{index}|{page}|{context}
            const parts = rowId.split('|');
            if (parts.length < 2) {
                console.error('[DEBUG] Invalid rowId format:', rowId);
                return;
            }
            
            // Extract term length and use it to get the exact original term from rowId
            const termLengthMatch = parts[0].match(/^(\d+):$/);
            if (!termLengthMatch) {
                console.error('[DEBUG] Could not parse term length from rowId:', rowId);
                return;
            }
            
            const termLength = parseInt(termLengthMatch[1]);
            const originalTermFromRowId = parts[1]; // This is now the original term, not baseTerm
            
            // Validate that the extracted original term matches the expected length
            if (originalTermFromRowId.length !== termLength) {
                console.error('[DEBUG] Term length mismatch in rowId parsing:', {
                    expected: termLength,
                    actual: originalTermFromRowId.length,
                    term: originalTermFromRowId,
                    rowId
                });
                return;
            }
            
            console.log('[DEBUG] handleTermEdit called:', { rowId, originalTermFromRowId, newTerm, forceClose });
            
            // Handle term updates properly:
            // - If newTerm equals ORIGINAL extracted term (row.term/row.Term when available), user is reverting to original -> remove from termUpdates
            // - Else if newTerm equals current displayTerm override, keep it
            // - Else add/update override
            let updatedTerms = { ...termUpdates };
            
            // CRITICAL FIX: Since rowId now contains original term, use it directly
            const originalTerm = originalTermFromRowId; // This is the true original term
            const termUpdatesKey = originalTermFromRowId; // Use original term as the key for termUpdates
            
            console.log(`[DEBUG] SIMPLIFIED LOGIC: originalTerm="${originalTerm}", termUpdatesKey="${termUpdatesKey}"`);

            console.log(`[DEBUG] REVERSION CHECK: newTerm="${newTerm}" vs originalTerm="${originalTerm}"`);
            console.log(`[DEBUG] REVERSION CHECK: Comparison result: ${newTerm === originalTerm}`);
            console.log(`[DEBUG] REVERSION CHECK: newTerm length: ${newTerm.length}, originalTerm length: ${originalTerm.length}`);
            console.log(`[DEBUG] REVERSION CHECK: termUpdatesKey="${termUpdatesKey}"`);
            console.log(`[DEBUG] REVERSION CHECK: Current termUpdates:`, updatedTerms);
            
            if (newTerm === originalTerm) {
                // User is reverting to original value - remove from updates
                if (updatedTerms[termUpdatesKey]) {
                    delete updatedTerms[termUpdatesKey];
                    console.log(`[DEBUG] ✅ REVERSION SUCCESS: "${termUpdatesKey}" -> "${originalTerm}" (removed from updates)`);
                    console.log(`[DEBUG] ✅ REVERSION SUCCESS: Updated termUpdates:`, updatedTerms);
                } else {
                    console.log(`[DEBUG] ✅ REVERSION SUCCESS: Term already at original value: "${termUpdatesKey}" (no change needed)`);
                }
            } else {
                // User is changing to a different value - add/update in termUpdates
                updatedTerms[termUpdatesKey] = newTerm;
                console.log(`[DEBUG] ❌ MODIFICATION: "${termUpdatesKey}" -> "${newTerm}" (original was "${originalTerm}")`);
                console.log(`[DEBUG] ❌ MODIFICATION: Updated termUpdates:`, updatedTerms);
            }
            
            // Always update the state (even if removing an entry)
            setTermUpdatesWithDebug(updatedTerms);
            
            if (onTermUpdate) {
                onTermUpdate(updatedTerms);
            }
            
            // CRITICAL FIX: If we just reverted a term to original value, 
            // trigger immediate save to clear it from the database
            // This handles both first edits and subsequent reversions
            if (newTerm === originalTerm) {
                console.log('[DEBUG] 🚀 TRIGGERING IMMEDIATE SAVE for term reversion');
                // Trigger immediate auto-save for reversion using authenticated request
                (async () => {
                    if (!projectId) return;
                    try {
                        console.log(`[ProjectDetail] REVERSION SAVE: Saving empty termUpdates to clear database`);
                        console.log(`[ProjectDetail] REVERSION SAVE: Using makeAuthenticatedRequest:`, !!makeAuthenticatedRequest);
                        const { getApiUrl: _getApiUrl } = await import('../client/lib/config');
                        
                        // CRITICAL FIX: Use makeAuthenticatedRequest if available, otherwise fall back to fetch
                        let response;
                        if (makeAuthenticatedRequest) {
                            response = await makeAuthenticatedRequest(_getApiUrl(`/api/projects/${projectId}/save-term-edits`), {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ 
                                    termUpdates: updatedTerms,
                                    deletedRows: [],
                                    addedTerms: []
                                })
                            });
                        } else {
                            // Fallback to raw fetch (will likely fail with 403, but better than crashing)
                            response = await fetch(_getApiUrl(`/api/projects/${projectId}/save-term-edits`), {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                },
                                body: JSON.stringify({ 
                                    termUpdates: updatedTerms,
                                    deletedRows: [],
                                    addedTerms: []
                                })
                            });
                        }
                        
                        if (response.ok) {
                            console.log('[ProjectDetail] REVERSION SAVE: ✅ Successfully cleared database');
                        } else {
                            const errorData = await response.json().catch(() => ({}));
                            console.error('[ProjectDetail] REVERSION SAVE: ❌ Failed to save:', response.status, response.statusText, errorData);
                            console.error('[ProjectDetail] REVERSION SAVE: ❌ Request URL:', _getApiUrl(`/api/projects/${projectId}/save-term-edits`));
                            console.error('[ProjectDetail] REVERSION SAVE: ❌ Request body:', JSON.stringify({ termUpdates: updatedTerms, deletedRows: [], addedTerms: [] }));
                        }
                    } catch (error) {
                        console.error('[ProjectDetail] REVERSION SAVE: ❌ Error:', error);
                    }
                })();
            }
            
            // Always clear editing state
            setEditingTerm(null);
            console.log('[DEBUG] Editing state cleared');
        } catch (error) {
            console.error('Error editing term:', error);
            setEditingTerm(null);
        }
    }, [termUpdates, onTermUpdate, csvData, getOriginalTerm, getBaseTerm, projectId, makeAuthenticatedRequest]);

    // Handle term click for editing - simplified to always work
    const handleTermClick = useCallback((row: any, event: React.MouseEvent, rowId: string) => {
        try {
            // Always prevent event propagation to avoid conflicts
            event.stopPropagation();
            event.preventDefault();
            
            // CRITICAL FIX: Also trigger PDF navigation when clicking terms
            console.log('[DEBUG] handleTermClick called with row:', row);
            console.log('[DEBUG] 🔍 TERM CLICK - Also triggering navigation');
            console.warn('🚨 TERM CLICK NAVIGATION TRIGGER 🚨', { row, hasCallback: !!onTermClick });
            if (onTermClick) {
                onTermClick(row);
            } else {
                console.error('❌ NO onTermClick CALLBACK PROVIDED TO TERM HANDLER');
            }
            console.log('[DEBUG] handleTermClick event details:', {
                target: event.target,
                currentTarget: event.currentTarget,
                type: event.type,
                bubbles: event.bubbles,
                rowId: rowId
            });
            
            // Check if editing is available
            if (!onTermUpdate) {
                console.log('[DEBUG] Term editing not available - onTermUpdate not provided');
                return;
            }
            
            // Get the base term - now guaranteed to exist for all rows
            const baseTerm = getBaseTerm(row);
            console.log('[DEBUG] handleTermClick baseTerm result:', {
                baseTerm,
                baseTermLength: baseTerm?.length,
                baseTermTrimmed: baseTerm?.trim(),
                isValidBaseTerm: !!(baseTerm && baseTerm.trim() !== ''),
                rowKeys: Object.keys(row),
                rowValues: Object.values(row),
                rowId: rowId
            });
            
            // All terms should now be editable, but add safety check
            if (!baseTerm || baseTerm.trim() === '') {
                console.error('[DEBUG] Cannot edit term - no valid term data found in row:', {
                    row,
                    baseTerm,
                    availableFields: Object.keys(row)
                });
                return;
            }
            
            console.log('[DEBUG] Setting editing term to rowId:', rowId);
            console.log('[DEBUG] Current editingTerm state before setting:', editingTerm);
            setEditingTerm(rowId);
            console.log(`[DEBUG] Successfully started editing term: "${baseTerm}" with rowId: "${rowId}"`);
            
            // Additional debugging - check if state actually changed
            setTimeout(() => {
                console.log('[DEBUG] editingTerm state after setTimeout:', editingTerm);
            }, 100);
        } catch (error) {
            console.error('[DEBUG] Error handling term click:', error);
        }
    }, [getBaseTerm, onTermUpdate, editingTerm, onTermClick]);

    // Handle row click for PDF navigation - call parent's onTermClick callback
    const handleRowClick = useCallback((row: any) => {
        try {
            console.warn('[CSVViewer] 🔍 ROW CLICK DEBUG:', {
                row,
                hasOnTermClick: !!onTermClick,
                pageRange,
                rowPage: row.page,
                rowDisplayPage: row.displayPage
            });
            console.warn('[CSVViewer] 🔍 PAGERANGE DEBUG:', {
                hasPageRange: !!pageRange,
                pageRangeValue: pageRange,
                pageRangeType: typeof pageRange,
                pageRangeKeys: pageRange ? Object.keys(pageRange) : 'no pageRange'
            });
            console.warn('[CSVViewer] 🔍 PAGERANGE SOURCE DEBUG - Check what was passed as prop');
            
            // DEBUG: Log pageRange availability for troubleshooting
            console.warn(`[CSVViewer] 🔍 PAGERANGE AVAILABILITY:`, {
                hasPageRange: !!pageRange,
                pageRangeValue: pageRange,
                rowPage: row.page,
                willPassToParent: true
            });
            
            // CRITICAL FIX: Pass pageRange info to parent to ensure consistent navigation
            if (onTermClick) {
                // Add pageRange info to the row data so ProjectDetail can use the correct pageRange
                const rowWithPageRange = {
                    ...row,
                    _csvViewerPageRange: pageRange, // Pass the pageRange from CSVViewer
                    _csvViewerHasPageRange: !!pageRange // Boolean flag for quick check
                };
                
                console.warn(`[CSVViewer] 🎯 PASSING ROW WITH PAGERANGE INFO:`, {
                    originalPage: row.page,
                    pageRangeFromProp: pageRange,
                    hasPageRange: !!pageRange,
                    willUseCSVViewerPageRange: !!pageRange
                });
                
                try {
                    onTermClick(rowWithPageRange);
                    console.warn('[CSVViewer] ✅ onTermClick call completed successfully');
                } catch (error) {
                    console.error('[CSVViewer] ❌ Error calling onTermClick:', error);
                }
            } else {
                console.error('[CSVViewer] ❌ No onTermClick callback provided');
            }
        } catch (error) {
            console.error('[CSVViewer] Error handling row click:', error);
        }
    }, [onTermClick, pageRange]);

    // Helper functions for display
    const getPageNumber = useCallback((row: any): string => {
        try {
            // NEW: Use displayPage if available (already formatted with 'n' suffix)
            if (row.displayPage) {
                console.log(`[getPageNumber] Using displayPage: ${row.displayPage} for term: ${row.term}`);
                return row.displayPage;
            }
            
            // FALLBACK: For stages without displayPage (A, B, C1-C3), apply endnotes formatting
            const pageRaw = row.page || row.Page || '';
            if (typeof pageRaw === 'string') return pageRaw;
            if (!pageRaw && pageRaw !== 0) return '';
            
            const pageNum = parseInt(pageRaw.toString());
            if (isNaN(pageNum)) return pageRaw.toString();
            
            // Apply endnotes logic using pageRange prop instead of window global
            const pr = pageRange || { startPage: 1 };
            console.log(`[getPageNumber] ENDNOTES DEBUG for term "${row.term}":`, {
                pageNum,
                pageRange: pr,
                endnotesEnabled: pr.endnotesEnabled,
                endnotesStartPage: pr.endnotesStartPage,
                startPage: pr.startPage || 1,
                pdfPageNum: pageNum + ((pr.startPage || 1) - 1)
            });
            
            if (pr.endnotesEnabled && typeof pr.endnotesStartPage === 'number') {
                const startPage = pr.startPage || 1;
                const pdfPageNum = pageNum + (startPage - 1);
                if (pdfPageNum >= pr.endnotesStartPage) {
                    console.log(`[getPageNumber] ✅ ADDING 'n' SUFFIX: ${pageNum}n (PDF page ${pdfPageNum} >= endnotes start ${pr.endnotesStartPage})`);
                    return `${pageNum}n`;
                } else {
                    console.log(`[getPageNumber] ❌ NO 'n' SUFFIX: PDF page ${pdfPageNum} < endnotes start ${pr.endnotesStartPage}`);
                }
            } else {
                console.log(`[getPageNumber] ❌ ENDNOTES NOT ENABLED or missing endnotesStartPage`);
            }
            
            return pageNum.toString();
        } catch (error) {
            console.error('Error getting page number:', error);
            return '';
        }
    }, [pageRange]);

    const getContext = useCallback((row: any): string => {
        try {
            // Prefer condensed context from Stage D (includes per-page subentries), fall back to raw context
            return row.context_condensed || row.context || row.Context || '';
        } catch (error) {
            console.error('Error getting context:', error);
            return '';
        }
    }, []);

    // Helper function to get subentry relationship
    const getSubentryRelationship = useCallback((row: any): string => {
        try {
            const displayTerm = getDisplayTerm(row);
            if (!displayTerm) return '';
            
            console.log(`[DEBUG-Subentry] Checking term: "${displayTerm}", reviewStage: "${reviewStage}"`);
            
            // For C1 stage, always return empty (as per requirements)
            if (reviewStage === 'C1') return '';
            
            // For C2 and C3 stages, check if this term has a subentry relationship
            if (reviewStage === 'C2' || reviewStage === 'C3') {
                console.log(`[DEBUG-Subentry] ${reviewStage} stage - c1ParentMap has ${c1ParentMap?.size || 0} entries`);
                console.log(`[DEBUG-Subentry] c1ParentMap contents:`, c1ParentMap);
                
                // Check if this term is a subentry of another term from C1 decisions
                if (c1ParentMap && c1ParentMap.has(displayTerm)) {
                    const parentTerm = c1ParentMap.get(displayTerm);
                    console.log(`[DEBUG-Subentry] Found parent for "${displayTerm}": "${parentTerm}"`);
                    return `Subentry of ${parentTerm}`;
                }
                
                // Check C2 decisions for subentry relationships
                if (c2Decisions) {
                    console.log(`[DEBUG-Subentry] Checking ${c2Decisions.length} C2 decisions`);
                    for (const decision of c2Decisions) {
                        if (decision.relationships) {
                            for (const [term, relationship] of Object.entries(decision.relationships)) {
                                if (term === displayTerm && relationship === 'subentry' && decision.main_term) {
                                    console.log(`[DEBUG-Subentry] Found C2 subentry: "${displayTerm}" -> "${decision.main_term}"`);
                                    return `Subentry of ${decision.main_term}`;
                                }
                            }
                        }
                    }
                }
                
                // For C3 stage, also check C3 decisions for subentry relationships
                if (reviewStage === 'C3' && c3Decisions) {
                    console.log(`[DEBUG-Subentry] Checking ${c3Decisions.length} C3 decisions`);
                    for (const decision of c3Decisions) {
                        if (decision.relationships) {
                            for (const [term, relationship] of Object.entries(decision.relationships)) {
                                if (term === displayTerm && relationship === 'subentry' && decision.main_term) {
                                    console.log(`[DEBUG-Subentry] Found C3 subentry: "${displayTerm}" -> "${decision.main_term}"`);
                                    return `Subentry of ${decision.main_term}`;
                                }
                            }
                        }
                    }
                }
            }
            
            return '';
        } catch (error) {
            console.error('Error getting subentry relationship:', error);
            return '';
        }
    }, [reviewStage, c1ParentMap, c2Decisions, c3Decisions, getDisplayTerm]);

    // Helper function to get cross-reference relationship
    const getCrossRefRelationship = useCallback((row: any): string => {
        try {
            const displayTerm = getDisplayTerm(row);
            if (!displayTerm) return '';
            
            console.log(`[DEBUG-CrossRef] Checking term: "${displayTerm}", reviewStage: "${reviewStage}"`);
            
            // For C1 stage, always return empty (as per requirements)
            if (reviewStage === 'C1') return '';
            
            // For C2 and C3 stages, check if this term has cross-reference relationships
            if (reviewStage === 'C2' || reviewStage === 'C3') {
                const crossRefs = new Set<string>();
                
                console.log(`[DEBUG-CrossRef] ${reviewStage} stage - c1CrossRefMap has ${c1CrossRefMap?.size || 0} entries`);
                console.log(`[DEBUG-CrossRef] c1CrossRefMap contents:`, c1CrossRefMap);
                
                // Check C1 cross-reference relationships
                if (c1CrossRefMap && c1CrossRefMap.has(displayTerm)) {
                    const refs = c1CrossRefMap.get(displayTerm) || [];
                    console.log(`[DEBUG-CrossRef] Found C1 cross-refs for "${displayTerm}":`, refs);
                    refs.forEach(ref => crossRefs.add(ref));
                }
                
                // Check C2 decisions for cross-reference relationships
                if (c2Decisions) {
                    console.log(`[DEBUG-CrossRef] Checking ${c2Decisions.length} C2 decisions`);
                    for (const decision of c2Decisions) {
                        if (decision.relationships) {
                            for (const [term, relationship] of Object.entries(decision.relationships)) {
                                if (term === displayTerm && relationship === 'cross_reference' && decision.main_term) {
                                    console.log(`[DEBUG-CrossRef] Found C2 cross-ref: "${displayTerm}" -> "${decision.main_term}"`);
                                    crossRefs.add(decision.main_term);
                                }
                                // Also check if this term is the main term and others reference it
                                if (decision.main_term === displayTerm && relationship === 'cross_reference') {
                                    console.log(`[DEBUG-CrossRef] Found C2 reverse cross-ref: "${term}" -> "${displayTerm}"`);
                                    crossRefs.add(term);
                                }
                            }
                        }
                    }
                }
                
                // For C3 stage, also check C3 decisions for cross-reference relationships
                if (reviewStage === 'C3' && c3Decisions) {
                    console.log(`[DEBUG-CrossRef] Checking ${c3Decisions.length} C3 decisions`);
                    for (const decision of c3Decisions) {
                        if (decision.relationships) {
                            for (const [term, relationship] of Object.entries(decision.relationships)) {
                                if (term === displayTerm && relationship === 'cross_reference' && decision.main_term) {
                                    console.log(`[DEBUG-CrossRef] Found C3 cross-ref: "${displayTerm}" -> "${decision.main_term}"`);
                                    crossRefs.add(decision.main_term);
                                }
                                // Also check if this term is the main term and others reference it
                                if (decision.main_term === displayTerm && relationship === 'cross_reference') {
                                    console.log(`[DEBUG-CrossRef] Found C3 reverse cross-ref: "${term}" -> "${displayTerm}"`);
                                    crossRefs.add(term);
                                }
                            }
                        }
                    }
                }
                
                if (crossRefs.size > 0) {
                    const result = `See also: ${Array.from(crossRefs).join(', ')}`;
                    console.log(`[DEBUG-CrossRef] Returning result for "${displayTerm}": "${result}"`);
                    return result;
                }
            }
            
            return '';
        } catch (error) {
            console.error('Error getting cross-reference relationship:', error);
            return '';
        }
    }, [reviewStage, c1CrossRefMap, c2Decisions, c3Decisions, getDisplayTerm]);

    // Enhanced scrollToTerm function that can navigate to the correct page
    const enhancedScrollToTerm = useCallback((termToFind: string) => {
        console.log(`[CSVViewer] Enhanced scroll to term: "${termToFind}"`);
        
        if (!csvData || csvData.length === 0) {
            console.log('[CSVViewer] No CSV data available for search');
            return;
        }
        
        // Find the term in the full dataset
        let foundRowIndex = -1;
        let foundRow: any = null;
        
        for (let i = 0; i < csvData.length; i++) {
            const row = csvData[i];
            const displayTerm = getDisplayTerm(row);
            
            if (displayTerm && displayTerm.toLowerCase() === termToFind.toLowerCase()) {
                foundRowIndex = i;
                foundRow = row;
                break;
            }
        }
        
        if (foundRowIndex === -1) {
            console.log(`[CSVViewer] Term "${termToFind}" not found in CSV data`);
            return;
        }
        
        console.log(`[CSVViewer] Found term "${termToFind}" at row index ${foundRowIndex}`);
        
        // Calculate which page the term is on
        const targetPage = Math.ceil((foundRowIndex + 1) / rowsPerPage);
        console.log(`[CSVViewer] Term is on page ${targetPage} (current page: ${currentPage})`);
        
        // Navigate to the correct page if needed
        if (targetPage !== currentPage) {
            console.log(`[CSVViewer] Navigating from page ${currentPage} to page ${targetPage}`);
            setCurrentPage(targetPage);
        }
        
        // Wait for page change to complete, then scroll to and highlight the term
        setTimeout(() => {
            const termCells = document.querySelectorAll('[data-term]');
            
            for (const cell of termCells) {
                const cellTerm = (cell as HTMLElement).dataset.term;
                if (cellTerm && cellTerm.toLowerCase() === termToFind.toLowerCase()) {
                    console.log(`[CSVViewer] Scrolling to term cell: "${cellTerm}"`);
                    
                    // Scroll the cell into view
                    cell.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center',
                        inline: 'nearest'
                    });
                    
                    // Add a temporary highlight effect
                    cell.classList.add('bump-effect');
                    setTimeout(() => {
                        cell.classList.remove('bump-effect');
                    }, 600);
                    
                    break;
                }
            }
        }, targetPage !== currentPage ? 300 : 50); // Wait longer if we changed pages
        
    }, [csvData, getDisplayTerm, currentPage, setCurrentPage, rowsPerPage]);
    
    // Expose the enhanced scrollToTerm function to parent component
    useEffect(() => {
        if (onScrollToTermReady && csvData.length > 0) {
            onScrollToTermReady(enhancedScrollToTerm);
        }
    }, [onScrollToTermReady, enhancedScrollToTerm, csvData.length]);

    // Component loading states
    if (isLoading) {
        return <div className="text-sm text-muted-foreground">Loading terms table...</div>;
    }
    
    if (error) {
        return <div className="text-sm text-red-500">Error loading terms: {error}</div>;
    }
    
    if (csvData.length === 0) {
        return <div className="text-sm text-muted-foreground">No terms found</div>;
    }

    // Main render
    try {
        // Group data by display terms (after edits) and create pagination
        const termGroups: { [key: string]: any[] } = {};
        const uniqueDisplayTermsList: string[] = [];
        
        // First pass: group rows by their final display terms (after edits)
        csvData.forEach(row => {
            const displayTerm = getDisplayTerm(row);
            if (displayTerm) {
                if (!termGroups[displayTerm]) {
                    termGroups[displayTerm] = [];
                    uniqueDisplayTermsList.push(displayTerm);
                }
                termGroups[displayTerm].push(row);
            }
        });
        
        // Sort unique display terms for consistent ordering
        // For Stage C, C1, C2, and C3, put grouped terms first, then ungrouped terms alphabetically
        if ((reviewStage === 'C' || reviewStage === 'C1' || reviewStage === 'C2' || reviewStage === 'C3') && reviewData?.suggestions) {
            console.log(`[DEBUG] Stage ${reviewStage} sorting - reviewData.suggestions:`, reviewData.suggestions);
            const suggestions = reviewData.suggestions;
            
            // Create a mapping from term to group index for proper sorting
            const termToGroupIndex: { [key: string]: number } = {};
            const groupedTerms = new Set<string>();
            
            // First pass: map each term to its group index
            suggestions.forEach((suggestion: any, groupIndex: number) => {
                if (suggestion.terms && Array.isArray(suggestion.terms)) {
                    console.log(`[DEBUG] Processing group ${groupIndex + 1} with terms:`, suggestion.terms);
                    suggestion.terms.forEach((groupTerm: string) => {
                        // Find matching display terms (handle both original and edited versions)
                        uniqueDisplayTermsList.forEach(displayTerm => {
                            const isDirectMatch = groupTerm.toLowerCase() === displayTerm.toLowerCase();
                            const isEditedMatch = Object.entries(termUpdates || {}).some(([orig, edited]) => 
                                (orig.toLowerCase() === groupTerm.toLowerCase() && edited.toLowerCase() === displayTerm.toLowerCase()) ||
                                (edited.toLowerCase() === groupTerm.toLowerCase() && orig.toLowerCase() === displayTerm.toLowerCase())
                            );
                            
                            if (isDirectMatch || isEditedMatch) {
                                termToGroupIndex[displayTerm] = groupIndex;
                                groupedTerms.add(displayTerm);
                                console.log(`[DEBUG] Mapped term "${displayTerm}" to group ${groupIndex + 1}`);
                            }
                        });
                    });
                }
            });
            
            console.log('[DEBUG] Total grouped terms found:', groupedTerms.size);
            console.log('[DEBUG] Term to group mapping:', termToGroupIndex);
            console.log('[DEBUG] uniqueDisplayTermsList before sort:', uniqueDisplayTermsList.slice(0, 10));
            
            // Advanced sort: group by group index first, then alphabetically within each group
            uniqueDisplayTermsList.sort((a, b) => {
                const aGroupIndex = termToGroupIndex[a];
                const bGroupIndex = termToGroupIndex[b];
                const aIsGrouped = aGroupIndex !== undefined;
                const bIsGrouped = bGroupIndex !== undefined;
                
                // First priority: grouped terms come before ungrouped terms
                if (aIsGrouped && !bIsGrouped) return -1;
                if (!aIsGrouped && bIsGrouped) return 1;
                
                // If both are grouped, sort by group index first
                if (aIsGrouped && bIsGrouped) {
                    if (aGroupIndex !== bGroupIndex) {
                        return aGroupIndex - bGroupIndex;
                    }
                    // Within the same group, sort alphabetically
                    return a.toLowerCase().localeCompare(b.toLowerCase());
                }
                
                // If both are ungrouped, sort alphabetically
                return a.toLowerCase().localeCompare(b.toLowerCase());
            });
            
            console.log('[DEBUG] uniqueDisplayTermsList after group-based sort:', uniqueDisplayTermsList.slice(0, 15));
            
            // IMPORTANT: Also sort the actual csvData to match the uniqueDisplayTermsList order
            // This ensures the table displays rows in the correct order
            csvData.sort((rowA, rowB) => {
                const termA = getDisplayTerm(rowA);
                const termB = getDisplayTerm(rowB);
                
                const aGroupIndex = termToGroupIndex[termA];
                const bGroupIndex = termToGroupIndex[termB];
                const aIsGrouped = aGroupIndex !== undefined;
                const bIsGrouped = bGroupIndex !== undefined;
                
                // Convert group indices to "Group ID" concept (1-based, with ungrouped being 9999 for sorting)
                const groupIdA = aIsGrouped ? aGroupIndex + 1 : 9999;
                const groupIdB = bIsGrouped ? bGroupIndex + 1 : 9999;
                
                // Primary sort: Group ID (1, 2, 3, ..., then ungrouped)
                if (groupIdA !== groupIdB) {
                    return groupIdA - groupIdB;
                }
                
                // Secondary sort: Term (alphabetical)
                const termComparison = termA.toLowerCase().localeCompare(termB.toLowerCase());
                if (termComparison !== 0) {
                    return termComparison;
                }
                
                // Tertiary sort: Page Number
                const pageA = parseInt(getPageNumber(rowA).toString()) || 0;
                const pageB = parseInt(getPageNumber(rowB).toString()) || 0;
                return pageA - pageB;
            });
            
            console.log(`[DEBUG] csvData sorted for Stage ${reviewStage} with enhanced group-based sorting (Group ID -> Term -> Page) - first 10 rows:`, 
                csvData.slice(0, 10).map(row => {
                    const term = getDisplayTerm(row);
                    const groupIndex = termToGroupIndex[term];
                    const page = getPageNumber(row);
                    const groupId = groupIndex !== undefined ? groupIndex + 1 : 'NA';
                    return `${term} (Group ID: ${groupId}, Page: ${page})`;
                })
            );
        } else {
            console.log(`[DEBUG] Not Stage C/C1 or no suggestions - reviewStage:`, reviewStage, 'suggestions:', reviewData?.suggestions?.length);
            // For other stages, sort alphabetically
            uniqueDisplayTermsList.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        }
        
        // ==== ENHANCED COLOR CALCULATION FOR STAGE C1 AND C2 ====
        
        // Create term to color mapping based on final display terms
        const termColorMap: { [key: string]: string } = {};
        
        // Get all unique terms including any newly added ones
        // Use base terms (original terms) for stable sorting, not display terms
        const allUniqueBaseTerms = new Set<string>();
        const displayTermToBaseTerm: { [key: string]: string } = {};
        
        csvData.forEach(row => {
            const rowId = getRowId(row);
            // For Stage B, include deleted rows in color assignment to maintain stable colors
            // For other stages, exclude deleted rows from color assignment
            const shouldIncludeRow = (reviewStage === 'B' || currentStatus === 'Stage B Complete') ? true : !deletedRowsSet.has(rowId);
            
            if (shouldIncludeRow) {
                const baseTerm = getBaseTerm(row);
                const displayTerm = getDisplayTerm(row);
                
                if (baseTerm && displayTerm) {
                    allUniqueBaseTerms.add(baseTerm);
                    displayTermToBaseTerm[displayTerm] = baseTerm;
                }
            }
        });

        // For Stage C, C1,  C2: Use group-based coloring logic
        if ((reviewStage === 'C' || reviewStage === 'C1' || reviewStage === 'C2' || reviewStage === 'C3') && reviewData?.suggestions) {
            console.log(`[CSVViewer] Applying Stage ${reviewStage} group-based color assignment`);
            
            // Predefined color classes for groups (up to 8 groups) - dark theme
            const groupColors = [
                'bg-red-500/20 border-l-2 border-red-500/40',      // Group 1 - red
                'bg-blue-500/20 border-l-2 border-blue-500/40',     // Group 2 - blue  
                'bg-green-500/20 border-l-2 border-green-500/40',    // Group 3 - green
                'bg-yellow-500/20 border-l-2 border-yellow-500/40',   // Group 4 - yellow
                'bg-purple-500/20 border-l-2 border-purple-500/40',   // Group 5 - purple
                'bg-pink-500/20 border-l-2 border-pink-500/40',     // Group 6 - pink
                'bg-indigo-500/20 border-l-2 border-indigo-500/40',   // Group 7 - indigo
                'bg-orange-500/20 border-l-2 border-orange-500/40'    // Group 8 - orange
            ];
            
            // First, identify all terms that are in groups
            const groupedTerms = new Set<string>();
            const termToGroupColor: { [key: string]: string } = {};
            const groupParents = new Map<string, string>(); // term -> group suggested_main (if provided)

            reviewData.suggestions.forEach((suggestion: any, groupIndex: number) => {
                const groupColorClass = groupColors[groupIndex % groupColors.length];
                if (suggestion.terms && Array.isArray(suggestion.terms)) {
                    suggestion.terms.forEach((groupTerm: string) => {
                        const key = groupTerm.toLowerCase();
                        groupedTerms.add(key);
                        termToGroupColor[key] = groupColorClass;
                        if (suggestion.suggested_main) {
                            groupParents.set(key, String(suggestion.suggested_main));
                        }
                    });
                }
            });
            
            // Collect all ungrouped unique DISPLAY terms for alternating color assignment
            const ungroupedDisplayTerms: string[] = [];
            Object.entries(displayTermToBaseTerm).forEach(([displayTerm, baseTerm]) => {
                // Check if this term is in any group
                const isGrouped = groupedTerms.has(displayTerm.toLowerCase()) || 
                                 groupedTerms.has(baseTerm.toLowerCase()) ||
                                 Object.entries(termUpdates || {}).some(([orig, edited]) => 
                                     (groupedTerms.has(orig.toLowerCase()) && edited.toLowerCase() === displayTerm.toLowerCase()) ||
                                     (groupedTerms.has(edited.toLowerCase()) && orig.toLowerCase() === displayTerm.toLowerCase())
                                 );
                
                if (!isGrouped && !addedTermsSet.has(displayTerm)) {
                    ungroupedDisplayTerms.push(displayTerm);
                }
            });
            
            // Sort ungrouped UNIQUE terms alphabetically for consistent alternating colors
            const uniqueUngroupedTerms = Array.from(new Set(ungroupedDisplayTerms)).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            
            console.log(`[CSVViewer] Found ${uniqueUngroupedTerms.length} unique ungrouped terms for alternating colors`);
            console.log(`[CSVViewer] Found ${groupedTerms.size} grouped terms with group colors`);
            console.log(`[CSVViewer] Unique ungrouped terms (first 10):`, uniqueUngroupedTerms.slice(0, 10));
            console.log(`[CSVViewer] Grouped terms:`, Array.from(groupedTerms).slice(0, 10));
            
            // Now assign colors to all display terms
            Object.entries(displayTermToBaseTerm).forEach(([displayTerm, baseTerm]) => {
                // First, check if this term is in a group (regardless of whether it's a new term or not)
                let groupColor = null;
                
                // Direct match
                if (termToGroupColor[displayTerm.toLowerCase()]) {
                    groupColor = termToGroupColor[displayTerm.toLowerCase()];
                }
                // Base term match
                else if (termToGroupColor[baseTerm.toLowerCase()]) {
                    groupColor = termToGroupColor[baseTerm.toLowerCase()];
                }
                // Edited term match
                else {
                    for (const [orig, edited] of Object.entries(termUpdates || {})) {
                        if ((termToGroupColor[orig.toLowerCase()] && edited.toLowerCase() === displayTerm.toLowerCase()) ||
                            (termToGroupColor[edited.toLowerCase()] && orig.toLowerCase() === displayTerm.toLowerCase())) {
                            groupColor = termToGroupColor[orig.toLowerCase()] || termToGroupColor[edited.toLowerCase()];
                            break;
                        }
                    }
                }
                
                // Inherit group color from prior subentry/merge relationships
                if (!groupColor && relationshipData) {
                    const subParent = relationshipData.subentries?.[displayTerm] || relationshipData.subentries?.[baseTerm];
                    if (subParent) {
                        const parentKey = String(subParent).toLowerCase();
                        groupColor = termToGroupColor[parentKey] || termToGroupColor[(groupParents.get(parentKey) || '').toLowerCase()];
                    }
                    if (!groupColor && relationshipData.mergedTerms) {
                        const mergedInto = relationshipData.mergedTerms[displayTerm] || relationshipData.mergedTerms[baseTerm];
                        if (mergedInto) {
                            const mk = String(mergedInto).toLowerCase();
                            groupColor = termToGroupColor[mk] || termToGroupColor[(groupParents.get(mk) || '').toLowerCase()];
                        }
                    }
                }
                
                if (groupColor) {
                    // Term is in a group - use group color (regardless of whether it's new or existing)
                    termColorMap[displayTerm] = groupColor;
                    if (addedTermsSet.has(displayTerm)) {
                        console.log(`[CSVViewer] New term "${displayTerm}" assigned group color: ${groupColor} (correctly grouped)`);
                    }
                } else if (addedTermsSet.has(displayTerm)) {
                    // New terms that are NOT in any group get light green background
                    termColorMap[displayTerm] = 'bg-green-500/10 border-l-2 border-green-500/30';
                    console.log(`[CSVViewer] New term "${displayTerm}" assigned new-term color (not grouped)`);
                } else {
                    // Existing terms not in a group - use alternating color based on position in sorted UNIQUE ungrouped list
                    const ungroupedIndex = uniqueUngroupedTerms.findIndex(term => term === displayTerm);
                    if (ungroupedIndex >= 0) {
                        termColorMap[displayTerm] = ungroupedIndex % 2 === 0 ? 'bg-muted/30' : 'bg-muted/10';
                        console.log(`[CSVViewer] Ungrouped term "${displayTerm}" at index ${ungroupedIndex} gets color: ${termColorMap[displayTerm]}`);
                    } else {
                        // Fallback to white
                        termColorMap[displayTerm] = 'bg-muted/20';
                        console.log(`[CSVViewer] Term "${displayTerm}" not found in ungrouped list, using fallback: bg-white`);
                    }
                }
            });
            
            console.log(`[CSVViewer] Stage ${reviewStage} color assignment complete - ${Object.keys(termColorMap).length} terms colored`);
        } else {
            // For non-Stage C stages, use simple alternating colors based on base terms
            console.log(`[CSVViewer] Applying standard alternating colors for stage ${reviewStage || 'unknown'}`);
            
            const sortedUniqueBaseTerms = Array.from(allUniqueBaseTerms).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            const baseTermToColorIndex: { [key: string]: number } = {};
            sortedUniqueBaseTerms.forEach((baseTerm, index) => {
                baseTermToColorIndex[baseTerm] = index;
            });
            
            Object.entries(displayTermToBaseTerm).forEach(([displayTerm, baseTerm]) => {
                const colorIndex = baseTermToColorIndex[baseTerm];
                
                if (addedTermsSet.has(displayTerm)) {
                    termColorMap[displayTerm] = 'bg-green-500/10 border-l-2 border-green-500/30';
                } else {
                    termColorMap[displayTerm] = colorIndex % 2 === 0 ? 'bg-muted/30' : 'bg-muted/50';
                }
            });
            
            console.log(`[CSVViewer] Standard color assignment complete - ${Object.keys(termColorMap).length} terms colored`);
        }
        
        if (addedTermsSet.size > 0) {
            console.log(`[CSVViewer] New terms with green background:`, Array.from(addedTermsSet));
        }
        
        // Calculate pagination for rows (not unique terms) - DO NOT filter out deleted rows, just mark them visually
        const totalRows = csvData.length; // Show all rows, including marked for deletion
        const totalPages = Math.ceil(totalRows / rowsPerPage);
        const startRowIndex = (currentPage - 1) * rowsPerPage;
        const endRowIndex = Math.min(startRowIndex + rowsPerPage, totalRows);
        const paginatedData = csvData.slice(startRowIndex, endRowIndex); // Show all rows
        
        const handlePageChange = (newPage: number) => {
            if (newPage >= 1 && newPage <= totalPages) {
                setCurrentPage(newPage);
            }
        };

        const handlePageInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = parseInt(event.target.value);
            if (!isNaN(value) && value >= 1 && value <= totalPages) {
                setCurrentPage(value);
            }
        };

        return (
            <div className="space-y-4">
                {showHeader && allowAdd && (
                    <div className="flex justify-end mb-4">
                        <Button
                            onClick={() => setShowAddDialog(true)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 shadow-sm"
                            size="sm"
                        >
                            + Add Term
                        </Button>
                    </div>
                )}
                
                {/* Add Term Button - only show if showHeader is false but allowAdd is true */}
                {!showHeader && allowAdd && (
                    <div className="flex justify-end mb-2">
                        <Button
                            onClick={() => setShowAddDialog(true)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1"
                            size="sm"
                        >
                            + Add Term
                        </Button>
                    </div>
                )}
                
                {/* Main table container */}
                <div className="rounded">
                    {/* Table with fixed header */}
                    <div 
                        className="scroll-smooth" 
                        style={{ 
                            height: totalPages > 1 ? 'calc(100vh - 320px)' : 'calc(100vh - 250px)', 
                            minHeight: totalPages > 1 ? '540px' : '600px', 
                            maxHeight: totalPages > 1 ? '70vh' : '80vh',
                            overflowY: 'auto',
                            overflowX: 'scroll'
                        }}
                    >
                        <table 
                            className="text-xs w-full border-separate border-spacing-0 border border-border/30 rounded-lg overflow-hidden" 
                            style={{ 
                                tableLayout: 'fixed',
                                boxSizing: 'border-box',
                                fontVariantNumeric: 'tabular-nums',
                                textRendering: 'optimizeSpeed'
                            }}
                        >
                            <thead className="bg-muted/70 sticky top-0 z-40 shadow-sm" style={{ position: 'sticky', top: 0, zIndex: 40 }}>
                                <tr className="border-b border-border/30">
                                    {allowDelete && (
                                        <th 
                                            className="px-2 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border/30 bg-muted/70 relative"
                                            style={{ 
                                                width: `${deleteColumnWidth}px`, 
                                                minWidth: `${deleteColumnWidth}px`, 
                                                maxWidth: `${deleteColumnWidth}px`,
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <Trash2 className="w-3 h-3 text-foreground" />
                                            <div 
                                                className={`absolute top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''} z-50`}
                                                onMouseDown={handleMouseDown('delete')}
                                                style={{ cursor: 'col-resize', right: '-0.5px' }}
                                            />
                                        </th>
                                    )}
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border/30 bg-muted/70 relative"
                                        style={{ 
                                            width: `${termColumnWidth}px`, 
                                            minWidth: `${termColumnWidth}px`, 
                                            maxWidth: `${termColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Term
                                        <div 
                                            className={`absolute top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''} z-50`}
                                            onMouseDown={handleMouseDown('term')}
                                            style={{ cursor: 'col-resize', right: '-0.5px' }}
                                        />
                                    </th>
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border/30 bg-muted/70 relative"
                                        style={{ 
                                            width: `${pageColumnWidth}px`, 
                                            minWidth: `${pageColumnWidth}px`, 
                                            maxWidth: `${pageColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Page
                                        <div 
                                            className={`absolute top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''} z-50`}
                                            onMouseDown={handleMouseDown('page')}
                                            style={{ cursor: 'col-resize', right: '-0.5px' }}
                                        />
                                    </th>
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border/30 bg-muted/70 relative"
                                        style={{ 
                                            width: `${subentryColumnWidth}px`, 
                                            minWidth: `${subentryColumnWidth}px`, 
                                            maxWidth: `${subentryColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Subentry?
                                        <div 
                                            className={`absolute top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''} z-50`}
                                            onMouseDown={handleMouseDown('subentry')}
                                            style={{ cursor: 'col-resize', right: '-0.5px' }}
                                        />
                                    </th>
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border/30 bg-muted/70 relative"
                                        style={{ 
                                            width: `${crossRefColumnWidth}px`, 
                                            minWidth: `${crossRefColumnWidth}px`, 
                                            maxWidth: `${crossRefColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Cross-reference?
                                        <div 
                                            className={`absolute top-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''} z-50`}
                                            onMouseDown={handleMouseDown('crossref')}
                                            style={{ cursor: 'col-resize', right: '-0.5px' }}
                                        />
                                    </th>
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground bg-muted/70 relative"
                                        style={{ 
                                            width: `${contextColumnWidth}px`, 
                                            minWidth: `${contextColumnWidth}px`, 
                                            maxWidth: `${contextColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Context
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    // Track unique terms for alternating colors in Stage D
                                    let uniqueTermIndex = 0;
                                    const seenTerms = new Set<string>();
                                    
                                    return paginatedData.map((row, index) => {
                                        try {
                                            const displayTerm = getDisplayTerm(row);
                                            const originalTerm = getOriginalTerm(row);
                                            const baseTerm = getBaseTerm(row);
                                            // Create a unique identifier for this specific row using a robust delimiter approach
                                            // Format: {originalTermLength}:|{originalTerm}|{index}|{page}|{context}
                                            // CRITICAL FIX: Use original term for rowId, not modified baseTerm
                                            const rowIdTerm = originalTerm || baseTerm; // fallback to baseTerm if no original
                                            const rowId = `${rowIdTerm.length}:|${rowIdTerm}|${startRowIndex + index}|${getPageNumber(row)}|${getContext(row).substring(0, 20)}`;
                                            // Prevent editing state confusion for empty baseTerms - only allow ONE specific row to be edited
                                            const isEditing = editingTerm && baseTerm && editingTerm === rowId;
                                            
                                            // Check if this is the start of a new unique term
                                            const prevRow = index > 0 ? paginatedData[index - 1] : null;
                                            const prevDisplayTerm = prevRow ? getDisplayTerm(prevRow) : null;
                                            const isNewUniqueTerm = !prevDisplayTerm || displayTerm !== prevDisplayTerm;
                                            
                                            // Track unique terms for alternating colors
                                            if (isNewUniqueTerm && !seenTerms.has(displayTerm)) {
                                                seenTerms.add(displayTerm);
                                                uniqueTermIndex++;
                                            }
                                            
                                            // Apply alternating colors for Stage D, otherwise use existing color logic
                                            let rowColor;
                                            if (reviewStage === 'D' || currentStatus === 'Stage D Complete') {
                                                // Alternating colors for Stage D based on unique terms
                                                rowColor = uniqueTermIndex % 2 === 0 ? 'bg-muted/30' : 'bg-muted/10';
                                            } else {
                                                // Use existing color mapping for other stages
                                                rowColor = termColorMap[displayTerm] || 'bg-muted/20';
                                            }
                                            
                                            const isMarkedForDeletion = deletedRowsSet.has(getRowId(row));
                                            const shouldShowStrikethrough = isMarkedForDeletion && !(reviewStage === 'B' || currentStatus === 'Stage B Complete');
                                            // For deleted rows, override the color to ensure red background shows
                                            const finalRowColor = isMarkedForDeletion ? 'bg-destructive/20' : rowColor;
                                        
                                        // Debug editing state calculation
                                        // Debug editing state calculation
                                        if (displayTerm && (displayTerm.includes('Dartmouth') || displayTerm.includes('Brooks'))) {
                                            console.log('[DEBUG] Render state for term:', {
                                                displayTerm,
                                                baseTerm,
                                                editingTerm,
                                                isEditing,
                                                editingTermEqualsBaseTerm: editingTerm === baseTerm
                                            });
                                        }
                                        
                                        // Debug color assignment for new terms
                                        if (displayTerm && (displayTerm.includes('test') || displayTerm.includes('second'))) {
                                        }
                                        
                                        return (
                                            <tr 
                                                key={`row-${startRowIndex + index}`}
                                                className={`border-b border-border/20 ${finalRowColor} ${onTermClick && !isEditing ? 'cursor-pointer hover:bg-purple-500/10' : ''} ${isMarkedForDeletion ? 'bg-red-500/20 border-l-4 border-red-500' : ''} transition-colors`}
                                                onClick={(e) => {
                                                    handleRowClick(row);
                                                }}
                                                title={onTermClick && !isEditing ? `Click to navigate to page ${getPageNumber(row)} and highlight "${originalTerm}"` : ''}
                                            >
                                                {/* Delete Checkbox Column */}
                                                {allowDelete && (
                                                    <td 
                                                        className="px-2 py-2 border-r border-border/20"
                                                        style={{ 
                                                            width: `${deleteColumnWidth}px`, 
                                                            minWidth: `${deleteColumnWidth}px`, 
                                                            maxWidth: `${deleteColumnWidth}px`,
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={deletedRowsSet.has(getRowId(row))}
                                                            className="w-3 h-3 border-gray-300 rounded focus:ring-red-500 focus:ring-2 transition-colors accent-red-600"
                                                            style={{
                                                                accentColor: '#dc2626', // Red-600 color for checked state
                                                            }}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                // Only handle normal clicks if not in the middle of a shift operation
                                                                if (!isShiftClicking) {
                                                                    handleRowDelete(row, e.target.checked, index, false);
                                                                }
                                                                // Reset shift clicking state after any change
                                                                setIsShiftClicking(false);
                                                            }}
                                                            onMouseDown={(e) => {
                                                                // Detect shift key on mouse down and handle range selection
                                                                if (e.shiftKey) {
                                                                    e.preventDefault(); // Prevent default checkbox behavior
                                                                    setIsShiftClicking(true); // Mark that we're doing a shift operation
                                                                    const isCurrentlyChecked = deletedRowsSet.has(getRowId(row));
                                                                    handleRowDelete(row, !isCurrentlyChecked, index, true);
                                                                }
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            title={deletedRowsSet.has(getRowId(row)) 
                                                                ? "Marked for deletion - will be removed when you click Continue" 
                                                                : "Mark this row for deletion (Hold Shift and click another row to select range)"}
                                                        />
                                                    </td>
                                                )}
                                                {/* Term Column */}
                                                <td 
                                                                                                            className="px-3 py-2 font-medium text-foreground border-r border-border/20"
                                                    data-term-cell="true"
                                                    style={{ 
                                                        width: `${termColumnWidth}px`, 
                                                        minWidth: `${termColumnWidth}px`, 
                                                        maxWidth: `${termColumnWidth}px`,
                                                        overflow: 'hidden',
                                                        boxSizing: 'border-box'
                                                    }}
                                                >
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            defaultValue={displayTerm}
                                                            className="w-full px-1 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary bg-card text-foreground"
                                                            ref={(input) => {
                                                                if (input) {
                                                                    console.log('[DEBUG] Input ref callback called for term:', displayTerm);
                                                                    // Focus the input when it's created, but don't select all text
                                                                    setTimeout(() => {
                                                                        console.log('[DEBUG] Attempting to focus input for term:', displayTerm);
                                                                        input.focus();
                                                                        // Removed input.select() to allow normal editing behavior
                                                                        console.log('[DEBUG] Input focused for term:', displayTerm);
                                                                    }, 0);
                                                                }
                                                            }}
                                                            onBlur={(e) => {
                                                                console.log('[DEBUG] 🎯 FIRST TERM INPUT onBlur called for term:', displayTerm, 'value:', e.target.value, 'baseTerm:', baseTerm);
                                                                console.log('[DEBUG] 🎯 FIRST TERM Input onBlur - about to call handleTermEdit with rowId:', rowId);
                                                                console.log('[DEBUG] 🎯 FIRST TERM csvData.length:', csvData.length, 'termUpdates keys:', Object.keys(termUpdates).length);
                                                                // Simple blur handling - always close and save
                                                                handleTermEdit(rowId, e.target.value, true);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    console.log('[DEBUG] Input Enter pressed for term:', displayTerm, 'value:', e.currentTarget.value, 'baseTerm:', baseTerm);
                                                                    console.log('[DEBUG] Input Enter - about to call handleTermEdit with rowId:', rowId);
                                                                    // Force close on Enter - user wants to save and close
                                                                    handleTermEdit(rowId, e.currentTarget.value, true);
                                                                } else if (e.key === 'Escape') {
                                                                    console.log('[DEBUG] Input Escape pressed for term:', displayTerm);
                                                                    // Force close on Escape without saving
                                                                    setEditingTerm(null);
                                                                }
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onFocus={(e) => {
                                                                console.log('[DEBUG] Input onFocus called for term:', displayTerm);
                                                            }}
                                                        />
                                                    ) : (
                                                        <div 
                                                            className={`px-2 py-2 rounded truncate min-h-[28px] flex items-center border border-transparent transition-colors select-none ${
                                                                onTermUpdate && baseTerm && baseTerm.trim() !== '' 
                                                                    ? 'cursor-pointer hover:bg-muted/30 hover:border-border/50' 
                                                                    : (reviewStage === 'C' || reviewStage === 'C1' || reviewStage === 'C2' || reviewStage === 'C3' || 
                                                                       (currentStatus && currentStatus.includes('Processing Stage C1')) || 
                                                                       (currentStatus && currentStatus.includes('Processing Stage C2')) ||
                                                                       (currentStatus && currentStatus.includes('Processing Stage C3')) ||
                                                                       (currentStatus && currentStatus.includes('Duplicate Detection')) ||
                                                                       (currentStatus && currentStatus.includes('Relationship Analysis')))
                                                                        ? 'cursor-default' // For processing stages, use normal styling instead of red
                                                                        : 'cursor-not-allowed bg-red-50 border-red-200'
                                                            }`}
                                                            onClick={onTermUpdate && baseTerm && baseTerm.trim() !== '' ? (e) => {
                                                                console.log('[DEBUG] 🎯 FIRST TERM Term cell clicked!', {
                                                                    displayTerm,
                                                                    baseTerm,
                                                                    onTermUpdate: !!onTermUpdate,
                                                                    eventType: e.type,
                                                                    target: e.target,
                                                                    currentTarget: e.currentTarget,
                                                                    csvDataLength: csvData.length,
                                                                    termUpdatesKeys: Object.keys(termUpdates).length
                                                                });
                                                                
                                                                // More aggressive event stopping
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                (e.nativeEvent as Event).stopImmediatePropagation?.();
                                                                
                                                                console.log('[DEBUG] 🎯 FIRST TERM About to call handleTermClick');
                                                                handleTermClick(row, e, rowId);
                                                            } : (e) => {
                                                                console.log('[DEBUG] Term cell clicked but not editable!', {
                                                                    displayTerm,
                                                                    baseTerm,
                                                                    onTermUpdate: !!onTermUpdate,
                                                                    baseTermValid: !!(baseTerm && baseTerm.trim() !== '')
                                                                });
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                            }}
                                                            data-term-edit={onTermUpdate && baseTerm && baseTerm.trim() !== '' ? "true" : "false"}
                                                            data-term={displayTerm}
                                                            title={
                                                                onTermUpdate && baseTerm && baseTerm.trim() !== '' 
                                                                    ? `Click to edit: ${displayTerm} (baseTerm: "${baseTerm}")` 
                                                                    : (reviewStage === 'C' || reviewStage === 'C1' || reviewStage === 'C2' || 
                                                                       (currentStatus && currentStatus.includes('Processing Stage C1')) || 
                                                                       (currentStatus && currentStatus.includes('Processing Stage C2')) ||
                                                                       (currentStatus && currentStatus.includes('Duplicate Detection')) ||
                                                                       (currentStatus && currentStatus.includes('Relationship Analysis')))
                                                                        ? `${displayTerm}` // Simple title for processing stages
                                                                        : `NOT EDITABLE: ${displayTerm} (baseTerm: "${baseTerm || 'EMPTY'}")`
                                                            }
                                                        >
                                                            {termUpdates[baseTerm] ? (
                                                                <span className={`${(termUpdates[baseTerm] === (row.term || row.Term || '').trim()) ? '' : 'text-blue-600 font-semibold'} ${shouldShowStrikethrough ? 'line-through' : ''}`}>{displayTerm}</span>
                                                            ) : (
                                                                <span className={
                                                                    // For processing stages, don't highlight terms in red - they should appear normal
                                                                    (reviewStage === 'C' || reviewStage === 'C1' || reviewStage === 'C2' || reviewStage === 'C3' || 
                                                                     (currentStatus && currentStatus.includes('Processing Stage C1')) || 
                                                                     (currentStatus && currentStatus.includes('Processing Stage C2')) ||
                                                                     (currentStatus && currentStatus.includes('Processing Stage C3')) ||
                                                                     (currentStatus && currentStatus.includes('Duplicate Detection')) ||
                                                                     (currentStatus && currentStatus.includes('Relationship Analysis'))) ? (shouldShowStrikethrough ? 'line-through' : '') :
                                                                    onTermUpdate && baseTerm && baseTerm.trim() !== '' 
                                                                        ? (shouldShowStrikethrough ? 'line-through' : '') 
                                                                        : `text-red-600 italic ${shouldShowStrikethrough ? 'line-through' : ''}`
                                                                }>{displayTerm || '[No term data]'}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                {/* Page Column */}
                                                <td 
                                                                                                                className="px-3 py-2 text-muted-foreground whitespace-nowrap min-h-[28px] align-middle border-r border-border/20"
                                                    style={{ 
                                                        width: `${pageColumnWidth}px`, 
                                                        minWidth: `${pageColumnWidth}px`, 
                                                        maxWidth: `${pageColumnWidth}px`,
                                                        boxSizing: 'border-box'
                                                    }}
                                                >
                                                    <span className={shouldShowStrikethrough ? 'line-through' : ''}>{getPageNumber(row)}</span>
                                                </td>
                                                {/* Subentry Column */}
                                                <td 
                                                    className="px-3 py-2 text-muted-foreground min-h-[28px] align-middle border-r border-border/20"
                                                    style={{ 
                                                        width: `${subentryColumnWidth}px`, 
                                                        minWidth: `${subentryColumnWidth}px`, 
                                                        maxWidth: `${subentryColumnWidth}px`,
                                                        boxSizing: 'border-box'
                                                    }}
                                                >
                                                    <div className={`truncate min-h-[20px] leading-6 ${shouldShowStrikethrough ? 'line-through' : ''}`} title={getSubentryRelationship(row)}>
                                                        {getSubentryRelationship(row)}
                                                    </div>
                                                </td>
                                                {/* Cross-reference Column */}
                                                <td 
                                                    className="px-3 py-2 text-muted-foreground min-h-[28px] align-middle border-r border-border/20"
                                                    style={{ 
                                                        width: `${crossRefColumnWidth}px`, 
                                                        minWidth: `${crossRefColumnWidth}px`, 
                                                        maxWidth: `${crossRefColumnWidth}px`,
                                                        boxSizing: 'border-box'
                                                    }}
                                                >
                                                    <div className={`truncate min-h-[20px] leading-6 ${shouldShowStrikethrough ? 'line-through' : ''}`} title={getCrossRefRelationship(row)}>
                                                        {getCrossRefRelationship(row)}
                                                    </div>
                                                </td>
                                                {/* Context Column */}
                                                <td 
                                                    className="px-3 py-2 text-muted-foreground min-h-[28px] align-middle"
                                                    style={{ 
                                                        width: `${contextColumnWidth}px`, 
                                                        minWidth: `${contextColumnWidth}px`, 
                                                        maxWidth: `${contextColumnWidth}px`,
                                                        boxSizing: 'border-box'
                                                    }}
                                                >
                                                    <div className={`truncate min-h-[20px] leading-6 ${shouldShowStrikethrough ? 'line-through' : ''}`} title={getContext(row)}>
                                                        {getContext(row)}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    } catch (rowError) {
                                        console.error(`[CSVViewer] Error rendering row ${startRowIndex + index}:`, rowError);
                                        return (
                                            <tr key={`error-row-${startRowIndex + index}`} className="border-b border-gray-200">
                                                <td colSpan={allowDelete ? 6 : 5} className="px-3 py-2 text-red-500 text-xs">
                                                    Error rendering row {startRowIndex + index + 1}
                                                </td>
                                            </tr>
                                        );
                                    }
                                });
                                })()}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination Controls - Outside scrollable area */}
                    {totalPages > 1 && (
                        <div className="bg-muted/30 border-t border-border/30 px-4 py-3">
                            <div className="flex items-center justify-between">
                                <div className="text-xs text-muted-foreground font-medium">
                                    Showing {startRowIndex + 1}-{endRowIndex} of {totalRows} rows
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                        className="px-2 py-1 text-xs bg-muted/50 hover:bg-muted/70 disabled:opacity-50 disabled:cursor-not-allowed rounded border border-border/30 text-foreground transition-colors"
                                    >
                                        ←
                                    </button>
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <span>Page</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max={totalPages}
                                            value={currentPage}
                                            onChange={handlePageInputChange}
                                            className="w-8 px-1 py-1 text-center border border-border/30 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 bg-background text-foreground [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                        <span>of {totalPages}</span>
                                    </div>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage >= totalPages}
                                        className="px-2 py-1 text-xs bg-muted/50 hover:bg-muted/70 disabled:opacity-50 disabled:cursor-not-allowed rounded border border-border/30 text-foreground transition-colors"
                                    >
                                        →
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Add Term Dialog */}
                {showAddDialog && (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className="bg-background border border-border/30 rounded-xl p-6 w-96 max-w-90vw shadow-2xl">
                            <h3 className="text-lg font-semibold text-foreground mb-4">Add New Term</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Term *
                                    </label>
                                    <input
                                        type="text"
                                        value={newTerm}
                                        onChange={(e) => setNewTerm(e.target.value)}
                                        className="w-full px-3 py-2 border border-border/30 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                                        placeholder="Enter term"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Page *
                                    </label>
                                    <input
                                        type="text"
                                        value={newPage}
                                        onChange={(e) => setNewPage(e.target.value)}
                                        className="w-full px-3 py-2 border border-border/30 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                                        placeholder="Enter page number"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Context
                                    </label>
                                    <textarea
                                        value={newContext}
                                        onChange={(e) => setNewContext(e.target.value)}
                                        className="w-full px-3 py-2 border border-border/30 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
                                        placeholder="Enter context (optional)"
                                        rows={3}
                                    />
                                </div>
                                {addError && (
                                    <div className="text-red-500 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{addError}</div>
                                )}
                                <div className="flex justify-end space-x-3 pt-2">
                                    <Button
                                        onClick={() => {
                                            setShowAddDialog(false);
                                            setNewTerm('');
                                            setNewPage('');
                                            setNewContext('');
                                            setAddError('');
                                        }}
                                        variant="outline"
                                        size="sm"
                                        className="border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/20"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleAddRow}
                                        className="bg-green-600 hover:bg-green-700 text-white shadow-lg"
                                        size="sm"
                                    >
                                        Add Term
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    } catch (renderError: any) {
        console.error('[CSVViewer] Critical error in render:', renderError);
        return (
            <div className="text-sm text-red-500 p-4 border border-red-200 rounded">
                <p>Error rendering terms table: {renderError.message}</p>
                <p className="text-xs mt-2">Check the console for more details.</p>
            </div>
        );
    }
};

// Filter generated files to exclude the original uploaded file
const getGeneratedFiles = (files: string[], originalFilename: string) => {
    return files.filter(file => file !== originalFilename);
};

// Get the appropriate CSV file for the current stage/context
const getAppropriateCSV = (files: string[], currentStage?: string, reviewStage?: string) => {
    const csvFiles = files.filter(file => file.endsWith('.csv')).sort();
    
    console.log(`[getAppropriateCSV] currentStage: ${currentStage}, reviewStage: ${reviewStage}`);
    console.log(`[getAppropriateCSV] available CSV files:`, csvFiles);
    
    let selectedFile: string | undefined;
    
    // If we're in a review stage, show the output from the previous processing stage
    if (reviewStage === 'B') {
        // Stage B review: show extracted terms (raw terms from Stage A)
        selectedFile = csvFiles.find(f => f.includes('extracted') || f.includes('stage_a')) || csvFiles[0];
        console.log(`[getAppropriateCSV] Stage B review - selected file:`, selectedFile);
    } else if (reviewStage === 'C') {
        // Stage C review: show standardized terms (input to grouping) - prioritize updated terms if they exist
        selectedFile = csvFiles.find(f => f.includes('standardized_terms_updated')) || 
                     csvFiles.find(f => f.includes('standardized') || f.includes('stage_b')) || 
                     csvFiles[csvFiles.length - 2] || csvFiles[0];
        console.log(`[getAppropriateCSV] Stage C review - selected file:`, selectedFile);
    } else if (reviewStage === 'C1') {
        // Stage C1 review: show standardized terms for duplicate detection
        selectedFile = csvFiles.find(f => f.includes('standardized_terms_updated')) || 
                     csvFiles.find(f => f.includes('standardized') || f.includes('stage_b')) || 
                     csvFiles[csvFiles.length - 2] || csvFiles[0];
        console.log(`[getAppropriateCSV] Stage C1 review - selected file:`, selectedFile);
    } else if (reviewStage === 'C2') {
        // Stage C2 review: show deduplicated terms from C1
        selectedFile = csvFiles.find(f => f.includes('c2_deduplicated')) || 
                     csvFiles.find(f => f.includes('standardized_terms_updated')) || 
                     csvFiles.find(f => f.includes('standardized') || f.includes('stage_b')) || 
                     csvFiles[csvFiles.length - 1];
        console.log(`[getAppropriateCSV] Stage C2 review - selected file:`, selectedFile);
    } else if (currentStage) {
        // Handle completed stages and processing stages by status
        if (currentStage.includes('Stage B Complete') || currentStage.includes('Standardizing Terms')) {
            // Show standardized terms - prioritize updated terms if they exist
            selectedFile = csvFiles.find(f => f.includes('standardized_terms_updated')) || 
                          csvFiles.find(f => f.includes('standardized')) || 
                          csvFiles[csvFiles.length - 1];
            console.log(`[getAppropriateCSV] Stage B complete - selected file:`, selectedFile);
        } else if (currentStage.includes('Processing Stage C1') || currentStage.includes('Duplicate Detection')) {
            // Show standardized terms for C1 processing - prioritize updated terms if they exist
            selectedFile = csvFiles.find(f => f.includes('standardized_terms_updated')) || 
                          csvFiles.find(f => f.includes('standardized')) || 
                          csvFiles[csvFiles.length - 1];
            console.log(`[getAppropriateCSV] Stage C1 processing - selected file:`, selectedFile);
        } else if (currentStage.includes('Processing Stage C2') || currentStage.includes('Relationship Analysis')) {
            // Show deduplicated terms for C2 processing - prioritize C2 deduplicated, then updated terms
            selectedFile = csvFiles.find(f => f.includes('c2_deduplicated')) || 
                          csvFiles.find(f => f.includes('standardized_terms_updated')) || 
                          csvFiles.find(f => f.includes('standardized')) || 
                          csvFiles[csvFiles.length - 1];
            console.log(`[getAppropriateCSV] Stage C2 processing - selected file:`, selectedFile);
        } else if (currentStage.includes('Stage C Complete') || currentStage.includes('Collecting Terms') || currentStage.includes('Grouping Terms')) {
            // Show collected terms
            selectedFile = csvFiles.find(f => f.includes('collected')) || csvFiles[csvFiles.length - 1];
            console.log(`[getAppropriateCSV] Stage C complete - selected file:`, selectedFile);
        } else if (currentStage.includes('Extracting Terms')) {
            // Show extracted terms
            selectedFile = csvFiles.find(f => f.includes('extracted')) || csvFiles[0];
            console.log(`[getAppropriateCSV] Stage A processing - selected file:`, selectedFile);
        } else {
            // For other processing stages or completed state, show the latest CSV
            selectedFile = csvFiles[csvFiles.length - 1];
            console.log(`[getAppropriateCSV] Default/completed stage - selected file:`, selectedFile);
        }
    } else {
        // Fallback: show the latest CSV
        selectedFile = csvFiles[csvFiles.length - 1];
        console.log(`[getAppropriateCSV] Fallback - selected file:`, selectedFile);
    }
    
    return selectedFile;
};

// Helper function to determine if we should show the terms table
const shouldShowTermsTable = (status: ProjectStatus) => {
    // Don't show table during initial extraction, standardizing terms, C1 processing, or C2 processing
    if (status.status.includes('Extracting Terms') || 
        status.status.includes('Standardizing Terms') ||
        status.status.includes('Processing Stage C1') ||
        status.status.includes('Processing Stage C2')) {
        return false;
    }
    
    // Show table if we're in review stage or if processing is complete
    if (status.reviewStage || 
        status.status.includes('Stage B Complete') ||
        status.status.includes('Stage C Complete') ||
        status.status.includes('Collecting Terms') ||
        status.status.includes('Grouping Terms') ||
        status.status.includes('Duplicate Detection') ||
        (status.status.includes('Relationship Analysis') && !status.status.includes('Processing Stage C2')) ||
        status.status === 'completed') {
        return true;
    }
    
    return false;
};

function ProjectDetail() {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [project, setProject] = useState<Project | null>(null);
    const [status, setStatus] = useState<ProjectStatus | null>(null);
const [startLoadingStickyUntil, setStartLoadingStickyUntil] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [firstNetworkErrorTime, setFirstNetworkErrorTime] = useState<number | null>(null);
    const [pollingIntervalId, setPollingIntervalId] = useState<NodeJS.Timeout | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [networkError, setNetworkError] = useState<string | null>(null);
    const [isStartingWorkflow, setIsStartingWorkflow] = useState<boolean>(false);
    const [isResettingStatus, setIsResettingStatus] = useState<boolean>(false);
    const [termsTableReady, setTermsTableReady] = useState<boolean>(false);
    const [startPage, setStartPage] = useState<number>(1);
    const [endPage, setEndPage] = useState<number | undefined>(undefined);
    const [endnotesEnabled, setEndnotesEnabled] = useState<boolean>(false);
    const [endnotesStartPage, setEndnotesStartPage] = useState<number | undefined>(undefined);
    
    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
    const [paymentData, setPaymentData] = useState<{
        paymentIntentId: string;
        paymentIntentClientSecret: string;
        costEstimate: any;
        billingBreakdown: any;
    } | null>(null);
    const [groupingAttempts, setGroupingAttempts] = useState<number>(0);
    const [isLoadingAdditionalGroups, setIsLoadingAdditionalGroups] = useState<boolean>(false);
    const [termUpdates, setTermUpdates] = useState<Record<string, string>>({});
    const [hasJustReverted, setHasJustReverted] = useState<boolean>(false);
    
    // Wrap setTermUpdates to add debugging
    const setTermUpdatesWithDebug = useCallback((updates: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => {
        if (typeof updates === 'function') {
            setTermUpdates(prev => {
                const newUpdates = updates(prev);
                console.log(`[setTermUpdates] Function update - Previous:`, prev);
                console.log(`[setTermUpdates] Function update - New:`, newUpdates);
                console.trace('[setTermUpdates] Function update call stack');
                
                // CRITICAL FIX: Detect reversion (had updates, now empty)
                console.log(`[setTermUpdates] 🔍 REVERSION CHECK: prev.length=${Object.keys(prev).length}, newUpdates.length=${Object.keys(newUpdates).length}`);
                if (Object.keys(prev).length > 0 && Object.keys(newUpdates).length === 0) {
                    console.log(`[setTermUpdates] 🚩 REVERSION DETECTED: Setting hasJustReverted flag`);
                    setHasJustReverted(true);
                } else {
                    console.log(`[setTermUpdates] ❌ No reversion detected - conditions not met`);
                }
                
                return newUpdates;
            });
        } else {
            console.log(`[setTermUpdates] Direct update - Setting to:`, updates);
            console.trace('[setTermUpdates] Direct update call stack');
            
            // CRITICAL FIX: Detect reversion (had updates, now empty)
            console.log(`[setTermUpdates] 🔍 DIRECT REVERSION CHECK: termUpdates.length=${Object.keys(termUpdates).length}, updates.length=${Object.keys(updates).length}`);
            if (Object.keys(termUpdates).length > 0 && Object.keys(updates).length === 0) {
                console.log(`[setTermUpdates] 🚩 DIRECT REVERSION DETECTED: Setting hasJustReverted flag`);
                setHasJustReverted(true);
            } else {
                console.log(`[setTermUpdates] ❌ No direct reversion detected - conditions not met`);
            }
            
            setTermUpdates(updates);
        }
    }, [termUpdates]);
    
    const [uniqueTermsCount, setUniqueTermsCount] = useState<number>(0);
    const [highlightedTerm, setHighlightedTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isSavingTermEdits, setIsSavingTermEdits] = useState<boolean>(false);
    const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
    const [isPdfMinimized, setIsPdfMinimized] = useState(false);
    const [enhancedScrollToTerm, setEnhancedScrollToTerm] = useState<((termToFind: string) => void) | null>(null);
    const [deletedRows, setDeletedRows] = useState<string[]>([]);
    const [addedTerms, setAddedTerms] = useState<string[]>([]);
    const [addedTermsDetails, setAddedTermsDetails] = useState<Record<string, AddedTerm>>({});
    const getModifiedCSVDataRef = useRef<(() => any[]) | null>(null);
    const rowClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastRowClickRef = useRef<number>(0);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isNavigatingRef = useRef<boolean>(false); // Add ref to control beforeunload behavior

    // Effect to detect when terms table is ready and reset workflow state
    useEffect(() => {
        if (status && shouldShowTermsTable(status)) {
            // Check if we have data ready to display (either CSV files or review data)
            const hasDataToShow = (status.files && status.files.length > 0) || 
                                (status.reviewData && !status.reviewData.isLoading);
            
            if (isStartingWorkflow && hasDataToShow) {
                console.log('🎯 [TABLE READY] Terms table is ready - resetting workflow state to hide loading screen');
                setIsStartingWorkflow(false);
                setTermsTableReady(true);
            }
        } else {
            setTermsTableReady(false);
        }
    }, [status, isStartingWorkflow]);
    
    // Add state for Stage C decisions
    const [stageCDecisions, setStageCDecisions] = useState<any[]>([]);
    const [stageC1Decisions, setStageC1Decisions] = useState<any[]>([]); // Store current C1 decisions
    const [stageC2Decisions, setStageC2Decisions] = useState<any[]>([]); // Store current C2 decisions
    const [stageC3Decisions, setStageC3Decisions] = useState<any[]>([]); // Store current C3 decisions
    
    // Add state for transition notification
    const [transitionNotification, setTransitionNotification] = useState<{ message: string; type: 'info' | 'success' } | null>(null);

    // NEW: Dedicated relationship data fetching
    const [relationshipData, setRelationshipData] = useState<{
        subentries: { [key: string]: string };
        crossReferences: { [key: string]: string[] };
        mergedTerms?: { [key: string]: string };
    } | null>(null);

    // Function to fetch relationship data from dedicated endpoint
    const fetchRelationshipData = async (projectId: string) => {
        try {
            console.log(`🔗 Fetching relationship data for project ${projectId}`);
            const { getApiUrl } = await import('../client/lib/config');
            const response = await makeAuthenticatedRequest(getApiUrl(`/api/projects/${projectId}/relationships`));
            
            if (response.ok) {
                const data = await response.json();
                console.log(`🔗 Received relationship data:`, data);
                setRelationshipData({
                    subentries: data.subentries || {},
                    crossReferences: data.crossReferences || {},
                    mergedTerms: data.mergedTerms || {}
                });
                return data;
            } else {
                console.error('Failed to fetch relationship data:', response.statusText);
                // Set empty relationship data on error to prevent null access issues
                setRelationshipData({
                    subentries: {},
                    crossReferences: {},
                    mergedTerms: {}
                });
                return null;
            }
        } catch (error) {
            console.error('Error fetching relationship data:', error);
            // Set empty relationship data on error to prevent null access issues
            setRelationshipData({
                subentries: {},
                crossReferences: {},
                mergedTerms: {}
            });
            return null;
        }
    };

    // Fetch relationship data when project loads and is in C2 or C3 stage
    useEffect(() => {
        if (projectId && status && (status.reviewStage === 'C2' || status.reviewStage === 'C3')) {
            fetchRelationshipData(projectId);
        }
    }, [projectId, status?.reviewStage]);

    // DEBUG: Add debugging hooks
    useRenderTracker('ProjectDetail', { projectId, status: status?.status, isLoading, error });
    useEffectCleanupTracker('ProjectDetail-main');
    useFetchTracker();
    usePollingTracker(pollingIntervalId, projectId || '');
    useComponentLifecycle('ProjectDetail');

    // DEBUG: Memory and timer monitoring
    useEffect(() => {
        if (!DEBUG_MEMORY) return;
        
        const interval = setInterval(() => {
            memoryMonitor.logMemoryUsage();
            
            // Log active timers and intervals
            const activeTimers = {
                pollingInterval: !!pollingIntervalId,
                rowClickTimeout: !!rowClickTimeoutRef.current,
                saveTimeout: !!saveTimeoutRef.current
            };
            
            console.log('[TIMERS]', activeTimers);
            
            // Log state sizes
            const stateSizes = {
                termUpdatesCount: Object.keys(termUpdates).length,
                statusFilesCount: status?.files?.length || 0,
                stageCDecisionsCount: stageCDecisions.length,
                stageC1DecisionsCount: stageC1Decisions.length,
                stageC2DecisionsCount: stageC2Decisions.length,
                stageC3DecisionsCount: stageC3Decisions.length
            };
            
            console.log('[STATE-SIZES]', stateSizes);
            
            // Log component mount status
            console.log('[COMPONENT] ProjectDetail mounted, projectId:', projectId);
            
        }, 30000);
        
        return () => clearInterval(interval);
    }, [pollingIntervalId, termUpdates, status, stageCDecisions, stageC1Decisions, stageC2Decisions, stageC3Decisions, projectId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (DEBUG_MEMORY) console.log('[CLEANUP] ProjectDetail unmounting, cleaning up all timers and intervals');
            
            // Clear polling interval
            if (pollingIntervalId) {
                if (DEBUG_MEMORY) console.log('[CLEANUP] Clearing polling interval');
                clearInterval(pollingIntervalId);
            }
            
            // Clear all timeout refs
            if (rowClickTimeoutRef.current) {
                if (DEBUG_MEMORY) console.log('[CLEANUP] Clearing row click timeout');
                clearTimeout(rowClickTimeoutRef.current);
            }
            
            if (saveTimeoutRef.current) {
                if (DEBUG_MEMORY) console.log('[CLEANUP] Clearing save timeout');
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [pollingIntervalId]);

    const projectService = ProjectService.getInstance();

    // Add token refresh functionality
    const refreshAuthToken = async (): Promise<string | null> => {
        try {
            const currentToken = localStorage.getItem('orca_auth_token');
            if (!currentToken) {
                return null;
            }

            const { getApiUrl } = await import('../client/lib/config');
            const response = await fetch(getApiUrl('/api/auth/refresh'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${currentToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Store both tokens
                localStorage.setItem('orca_auth_token', data.token);
                localStorage.setItem('orca_processing_token', data.processingToken);
                console.log('[AUTH] Tokens refreshed successfully');
                return data.token;
            } else {
                console.error('[AUTH] Token refresh failed:', response.status);
                return null;
            }
        } catch (error) {
            console.error('[AUTH] Token refresh error:', error);
            return null;
        }
    };

    // Enhanced API call wrapper with token refresh and retry
    const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}, maxRetries: number = 3): Promise<Response> => {
        let retryCount = 0;
        
        while (retryCount < maxRetries) {
            try {
                const token = await refreshAuthToken();
                if (!token) {
                    throw new Error('Authentication failed - please log in again');
                }

                const response = await fetch(url, {
                    ...options,
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        ...options.headers
                    }
                });

                if (response.status === 401 || response.status === 403) {
                    // Auth failed, try refresh
                    localStorage.removeItem('orca_auth_token');
                    const newToken = await refreshAuthToken();
                    if (!newToken) {
                        throw new Error('Authentication failed - please log in again');
                    }
                    
                    // Retry with new token
                    retryCount++;
                    continue;
                }

                return response;
            } catch (error: any) {
                retryCount++;
                console.error(`[ProjectDetail] Request attempt ${retryCount} failed:`, error);
                
                if (retryCount >= maxRetries) {
                    throw error;
                }
                
                // Wait before retry
                await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            }
        }
        
        throw new Error('Max retries exceeded');
    };

    // Auto-save functionality for term edits, deletions, and additions
    const autoSaveAllChanges = useCallback(async (updates: Record<string, string>, deletedRowIds: string[], addedTerms: string[]) => {
        if (!projectId) return;
        
        // Only save if there are actual changes
        const hasUpdates = Object.keys(updates).length > 0;
        const hasDeletions = deletedRowIds.length > 0;
        const hasAdditions = addedTerms.length > 0;
        
        if (!hasUpdates && !hasDeletions && !hasAdditions) return;
        
        try {
            console.log(`[ProjectDetail] Auto-saving changes: ${Object.keys(updates).length} edits, ${deletedRowIds.length} deletions, ${addedTerms.length} additions`);
            
            const { getApiUrl: _getApiUrl } = await import('../client/lib/config');
            const response = await makeAuthenticatedRequest(_getApiUrl(`/api/projects/${projectId}/save-term-edits`), {
                method: 'POST',
                body: JSON.stringify({ 
                    termUpdates: updates,
                    deletedRows: deletedRowIds,
                    addedTerms: addedTerms
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('[ProjectDetail] Auto-save failed:', errorData);
                return;
            }

            console.log('[ProjectDetail] All changes auto-saved successfully');
        } catch (error) {
            console.error('[ProjectDetail] Auto-save error:', error);
            
            // If authentication failed, don't immediately redirect during auto-save
            // Just log the error and let the user continue working
            if (error instanceof Error && error.message?.includes('Authentication failed')) {
                console.warn('[ProjectDetail] Auto-save failed due to expired session - user can continue working and will be prompted to re-authenticate when they navigate');
                // Could show a non-intrusive notification here if desired
            }
            // Don't show error to user for auto-save failures, just log them
        }
    }, [projectId]);

    // Legacy auto-save for term edits only (keeping for backward compatibility)
    const autoSaveTermEdits = useCallback(async (updates: Record<string, string>) => {
        if (!projectId || Object.keys(updates).length === 0) return;
        
        try {
            console.log(`[ProjectDetail] Auto-saving ${Object.keys(updates).length} term edits...`);
            
            const { getApiUrl: _getApiUrl2 } = await import('../client/lib/config');
            const response = await makeAuthenticatedRequest(_getApiUrl2(`/api/projects/${projectId}/save-term-edits`), {
                method: 'POST',
                body: JSON.stringify({ termUpdates: updates })
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('[ProjectDetail] Auto-save failed:', errorData);
                return;
            }

            console.log('[ProjectDetail] Term edits auto-saved successfully');
        } catch (error: any) {
            console.error('[ProjectDetail] Auto-save error:', error);
            // Don't show error to user for auto-save failures, just log them
        }
    }, [projectId]);

    // Stable callback functions to prevent infinite re-renders in CSVViewer
    // REMOVED: handleGetDeletedRows - no longer needed with shared state approach
    // const handleGetDeletedRows = useCallback((getDeletedRowsFn: () => string[]) => {
    //     getDeletedRowsRef.current = getDeletedRowsFn;
    //     const currentDeletedRows = getDeletedRowsFn();
    //     setDeletedRows(currentDeletedRows);
    // }, []);

    const handleGetModifiedCSVData = useCallback((getModifiedCSVDataFn: () => any[]) => {
        getModifiedCSVDataRef.current = getModifiedCSVDataFn;
    }, []);

    const handleRowsDeleted = useCallback((deletedRowIds: string[]) => {
        console.log('Rows deleted:', deletedRowIds);
        setDeletedRows(deletedRowIds);
        // Don't trigger auto-save here - let the useEffect handle it
    }, []);

    const handleRowAdded = useCallback((newRow: any) => {
        console.log('Row added:', newRow);
        const newTermName = newRow.term || newRow.term_standard || '';
        if (newTermName) {
            // Don't update addedTerms here - that's handled by onAddedTermsUpdate from CSVViewer
            // Only store complete term details
            const termDetails: AddedTerm = {
                term: newTermName,
                page: newRow.page || '0',
                context: newRow.context || 'User-added term'
            };
            setAddedTermsDetails(prev => ({
                ...prev,
                [newTermName]: termDetails
            }));
            
            console.log(`[ProjectDetail] Stored details for added term: ${newTermName}`, termDetails);
            // Don't trigger auto-save here - let the useEffect handle it
        }
    }, []); // Removed addedTerms dependency since we're not updating it here

    const handleScrollToTermReady = useCallback((scrollToTermFn: (termToFind: string) => void) => {
        // Create an enhanced scroll function that ALSO updates the PDF viewer
        const enhancedScrollWithPDFUpdate = (termToFind: string) => {
            console.log(`[ProjectDetail] Enhanced scroll called for term: "${termToFind}"`);
            
            // First, scroll within the CSV table
            scrollToTermFn(termToFind);
            
            // Then, update PDF viewer to highlight the same term
            if (termToFind && termToFind.trim()) {
                console.log(`[ProjectDetail] Setting PDF highlightedTerm to: "${termToFind}"`);
                setHighlightedTerm(termToFind.trim());
                
                // Also try to find the page number for the term in CSV data to navigate PDF
                // This requires access to CSV data, but we can make a best effort
                // The PDF viewer will handle term searching across pages
            }
        };
        
        setEnhancedScrollToTerm(() => enhancedScrollWithPDFUpdate);
    }, []);

    // New handlers for shared state updates (like term updates)
    const handleDeletedRowsUpdate = useCallback((updatedDeletedRows: string[]) => {
        console.log('Deleted rows updated:', updatedDeletedRows);
        setDeletedRows(updatedDeletedRows);
        // Don't trigger auto-save here - let the useEffect handle it
    }, []);

    const handleAddedTermsUpdate = useCallback((updatedAddedTerms: string[]) => {
        console.log(`[ProjectDetail] Added terms updated from CSVViewer:`, updatedAddedTerms);
        console.log(`[ProjectDetail] Previous addedTerms:`, addedTerms);
        setAddedTerms(updatedAddedTerms);
        // Don't trigger auto-save here - let the useEffect handle it
    }, [addedTerms]); // Keep addedTerms dependency for logging comparison

    // Debounced auto-save effect - comprehensive save for all changes
    useEffect(() => {
        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Only auto-save if we have any changes and component is mounted
        const hasUpdates = Object.keys(termUpdates).length > 0;
        const hasDeletions = deletedRows.length > 0;
        const hasAdditions = addedTerms.length > 0;
        
        // Always auto-save if we have changes OR if we need to clear the database
        // This ensures that when termUpdates becomes empty (user reverted all changes), 
        // we still save to clear the persisted edits from the database
        if (hasUpdates || hasDeletions || hasAdditions) {
            saveTimeoutRef.current = setTimeout(async () => {
                if (!projectId) return;
                
                try {
                    console.log(`[ProjectDetail] Auto-saving changes: ${Object.keys(termUpdates).length} edits, ${deletedRows.length} deletions, ${addedTerms.length} additions`);
                    
                    const { getApiUrl: _getApiUrl3 } = await import('../client/lib/config');
                    const response = await makeAuthenticatedRequest(_getApiUrl3(`/api/projects/${projectId}/save-term-edits`), {
                        method: 'POST',
                        body: JSON.stringify({ 
                            termUpdates: termUpdates,
                            deletedRows: deletedRows,
                            addedTerms: addedTerms,
                            addedTermsDetails: addedTermsDetails
                        })
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        console.error('[ProjectDetail] Auto-save failed:', errorData);
                        return;
                    }

                    console.log('[ProjectDetail] All changes auto-saved successfully');
                } catch (error: any) {
                    console.error('[ProjectDetail] Auto-save error:', error);
                    
                    // If authentication failed, don't immediately redirect during auto-save
                    // Just log the error and let the user continue working
                    if (error instanceof Error && error.message?.includes('Authentication failed')) {
                        console.warn('[ProjectDetail] Auto-save failed due to expired session - user can continue working and will be prompted to re-authenticate when they navigate');
                        // Could show a non-intrusive notification here if desired
                    }
                    // Don't crash the component on auto-save failure
                }
            }, 1000); // Increased from 500ms to 1000ms to reduce auto-save frequency
        }

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [termUpdates, deletedRows, addedTerms, addedTermsDetails, projectId]);

    // Save all changes when user navigates away
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            // Don't block navigation if we're intentionally navigating
            if (isNavigatingRef.current) {
                return;
            }
            
            const hasUpdates = Object.keys(termUpdates).length > 0;
            const hasDeletions = deletedRows.length > 0;
            const hasAdditions = addedTerms.length > 0;
            
            if (hasUpdates || hasDeletions || hasAdditions) {
                // Show warning to user (don't attempt synchronous save as it causes infinite loops)
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [termUpdates, deletedRows, addedTerms, addedTermsDetails]); // Added addedTermsDetails dependency

    // Load existing changes when component mounts
    const loadAllChanges = useCallback(async () => {
        if (!projectId) {
            console.log(`[ProjectDetail] loadAllChanges: No projectId, skipping`);
            return;
        }

        try {
            console.log(`[ProjectDetail] loadAllChanges: Loading existing changes for project ${projectId}...`);

            const { getApiUrl } = await import('../client/lib/config');
            const response = await makeAuthenticatedRequest(
                getApiUrl(`/api/projects/${projectId}/term-edits`)
            );

            console.log(`[ProjectDetail] loadAllChanges: Response status: ${response.status}`);

            if (response.ok) {
                const data = await response.json();
                console.log(`[ProjectDetail] loadAllChanges: Received data from server:`, data);
                
                // Load term updates
                if (data.termUpdates && Object.keys(data.termUpdates).length > 0) {
                    console.log(`[ProjectDetail] loadAllChanges: About to call setTermUpdatesWithDebug with:`, data.termUpdates);
                    setTermUpdatesWithDebug(data.termUpdates);
                    console.log(`[ProjectDetail] loadAllChanges: ✅ Loaded ${Object.keys(data.termUpdates).length} existing term edits`);
                } else {
                    console.log(`[ProjectDetail] loadAllChanges: No term updates found in response:`, {
                        hasTermUpdates: !!data.termUpdates,
                        termUpdatesType: typeof data.termUpdates,
                        termUpdatesKeys: data.termUpdates ? Object.keys(data.termUpdates).length : 'N/A'
                    });
                }
                
                // Load deleted rows
                if (data.deletedRows && Array.isArray(data.deletedRows) && data.deletedRows.length > 0) {
                    setDeletedRows(data.deletedRows);
                    console.log(`[ProjectDetail] loadAllChanges: ✅ Loaded ${data.deletedRows.length} deleted rows`);
                } else {
                    console.log(`[ProjectDetail] loadAllChanges: No deleted rows found`);
                }
                
                // Load added terms
                if (data.addedTerms && Array.isArray(data.addedTerms) && data.addedTerms.length > 0) {
                    setAddedTerms(data.addedTerms);
                    console.log(`[ProjectDetail] loadAllChanges: ✅ Loaded ${data.addedTerms.length} added terms`);
                } else {
                    console.log(`[ProjectDetail] loadAllChanges: No added terms found`);
                }
                
                // Load added terms details
                if (data.addedTermsDetails && typeof data.addedTermsDetails === 'object') {
                    setAddedTermsDetails(data.addedTermsDetails);
                    console.log(`[ProjectDetail] loadAllChanges: ✅ Loaded ${Object.keys(data.addedTermsDetails).length} added term details`);
                } else {
                    console.log(`[ProjectDetail] loadAllChanges: No added term details found`);
                }
                
            } else if (response.status !== 404) {
                // 404 is expected if no changes exist yet
                console.error(`[ProjectDetail] loadAllChanges: Failed to load changes, status: ${response.status}`);
                const errorText = await response.text();
                console.error(`[ProjectDetail] loadAllChanges: Error response:`, errorText);
            } else {
                console.log(`[ProjectDetail] loadAllChanges: 404 response (no saved changes yet)`);
            }

        } catch (error: any) {
            console.error(`[ProjectDetail] loadAllChanges: Error loading changes:`, error);
        }
    }, [projectId]);

    // Load term edits on component mount - do this early and for any stage
    useEffect(() => {
        if (projectId) {
            loadAllChanges();
        }
    }, [projectId, loadAllChanges]);

    // Additional effect to reload term edits when status changes to Stage B Complete
    // CRITICAL FIX: Don't reload when termUpdates is empty due to reversion
    useEffect(() => {
        if (projectId && status?.status === 'Stage B Complete' && Object.keys(termUpdates).length === 0 && !hasJustReverted) {
            console.log('[ProjectDetail] Stage B Complete detected, reloading term edits if none exist');
            loadAllChanges();
        }
        
        // Reset the reversion flag after a delay
        if (hasJustReverted) {
            const timer = setTimeout(() => {
                setHasJustReverted(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [projectId, status?.status, termUpdates, loadAllChanges, hasJustReverted]);

    // Optimized row click handler with better debouncing and cancellation
    const handleRowClick = useCallback((row: any) => {
        try {

            console.log('[ProjectDetail] Row clicked:', row);
            const originalTerm = row.term || row.Term || '';
            
            if (originalTerm) {
                console.log(`[ProjectDetail] Setting highlightedTerm to: "${originalTerm}"`);
                setHighlightedTerm(originalTerm);
            }
            
            // UNIFIED PAGE CONVERSION: All stages store adjusted page numbers (1, 2, 3...)
            // Convert adjusted page to PDF page for navigation using status.pageRange
            const backendPage = row.page || row.Page;
            console.warn(`[ProjectDetail] 🔍 NAVIGATION DEBUG:`, {
                backendPage,
                pageRange: status?.pageRange,
                statusObject: status,
                rowData: row
            });
            console.warn(`[ProjectDetail] 🔍 STATUS DEBUG:`, {
                hasStatus: !!status,
                statusKeys: status ? Object.keys(status) : 'no status',
                pageRangeValue: status?.pageRange,
                pageRangeType: typeof status?.pageRange
            });
            
            // CRITICAL DEBUG: Check if pageRange is missing and log the issue
            if (!status?.pageRange) {
                console.error(`🚨 [CRITICAL] pageRange is missing from status! This will cause page offset issues.`);
                console.error(`🚨 [CRITICAL] Status object:`, status);
                console.error(`🚨 [CRITICAL] This means navigation will use default startPage=1 instead of actual startPage`);
            }
            
            if (typeof backendPage === 'number' && backendPage > 0) {
                // CRITICAL FIX: Use pageRange from CSVViewer if available, otherwise fall back to status
                const csvViewerPageRange = (row as any)._csvViewerPageRange;
                const hasCsvViewerPageRange = (row as any)._csvViewerHasPageRange;
                
                let effectivePageRange;
                let pageRangeSource;
                
                if (hasCsvViewerPageRange && csvViewerPageRange) {
                    effectivePageRange = csvViewerPageRange;
                    pageRangeSource = 'CSVViewer';
                } else {
                    effectivePageRange = status?.pageRange || { startPage: 1 };
                    pageRangeSource = status?.pageRange ? 'status' : 'default';
                }
                
                const startPage = effectivePageRange.startPage || 1;
                const pdfPageNum = backendPage + (startPage - 1);
                
                console.warn(`[ProjectDetail] 🎯 NAVIGATION (${pageRangeSource}): Adjusted page ${backendPage} + (startPage ${startPage} - 1) = PDF page ${pdfPageNum}`);
                console.warn(`[ProjectDetail] 🔍 PageRange source: ${pageRangeSource}`, effectivePageRange);
                console.warn(`🚨 CALLING setCurrentPage(${pdfPageNum}) 🚨`);
                setCurrentPage(pdfPageNum);
            } else {
                console.error(`[ProjectDetail] ❌ NAVIGATION FAILED: Invalid backendPage:`, backendPage);
            }
        } catch (error) {
            console.error('[ProjectDetail] Error handling row click:', error);
        }
    }, [status?.pageRange]);
    
    // Add timeout cleanup on unmount
    useEffect(() => {
        return () => {
            if (rowClickTimeoutRef.current) {
                clearTimeout(rowClickTimeoutRef.current);
            }
        };
    }, []);

    // Add retry throttling state to prevent infinite loops
    const [retryCount, setRetryCount] = useState<number>(0);
    const [lastRetryTime, setLastRetryTime] = useState<number>(0);
    const maxRetries = 5; // Maximum retries before giving up
    const retryThrottleMs = 5000; // Minimum time between retries (5 seconds)

    // Memoized fetch project function to prevent unnecessary re-creation
    const fetchProject = useCallback(async (id: string, isRetry: boolean = false) => {
        try {
            // CRITICAL: Add retry throttling to prevent infinite loops
            if (isRetry) {
                const now = Date.now();
                if (retryCount >= maxRetries) {
                    console.log(`[ProjectDetail] Maximum retries (${maxRetries}) reached for project ${id}. Stopping retries.`);
                    setNetworkError("Unable to connect after multiple attempts. Please check your network connection and refresh the page.");
                    return;
                }
                
                if (now - lastRetryTime < retryThrottleMs) {
                    console.log(`[ProjectDetail] Retry throttled. Waiting ${retryThrottleMs}ms between retries.`);
                    return;
                }
                
                setRetryCount(prev => prev + 1);
                setLastRetryTime(now);
                console.log(`[ProjectDetail] Retry attempt ${retryCount + 1}/${maxRetries} for project ${id}`);
            } else {
                // Reset retry counter for non-retry requests
                setRetryCount(0);
                setLastRetryTime(0);
            }

            console.warn(`[ProjectDetail] 🚨 CALLING projectService.getProject(${id})`);
            const result = await projectService.getProject(id);
            console.warn(`[ProjectDetail] 🚨 PROJECT SERVICE RESULT:`, {
                success: result.success,
                hasProject: !!result.project,
                projectKeys: result.project ? Object.keys(result.project) : 'no project',
                pageRange: result.project?.pageRange,
                pageRangeTypes: result.project?.pageRange ? {
                    startPage: typeof result.project.pageRange.startPage,
                    endPage: typeof result.project.pageRange.endPage,
                    endnotesEnabled: typeof result.project.pageRange.endnotesEnabled,
                    endnotesStartPage: typeof result.project.pageRange.endnotesStartPage
                } : 'no pageRange'
            });
            if (result.success && result.project) {
                // Reset retry counter on success
                setRetryCount(0);
                setLastRetryTime(0);
                
                setProject(result.project);
                
                // CRITICAL: Don't update status if we're in the middle of resetting it
                // This prevents race conditions during payment modal dismissal
                if (!isResettingStatus) {
                    // Convert project to status format expected by the UI
                    const projectStatus: ProjectStatus = {
                        status: result.project.status,
                        files: result.project.files,
                        originalFilename: result.project.originalFilename,
                        error: result.project.error,
                        reviewStage: result.project.reviewStage,
                        reviewData: undefined, // Will be fetched separately if needed
                        pageRange: result.project.pageRange // CRITICAL FIX: Copy pageRange from project
                    };
                    
                    setStatus(projectStatus);
                    
                    // CRITICAL: Reset isStartingWorkflow when processing is truly complete and table is ready
                    // Keep loading screen active during completion transitions to prevent flash
                    if (isStartingWorkflow && result.project.status !== 'pending') {
                        // Only reset workflow state when we're ready to show the final interface
                        const shouldResetWorkflow = result.project.status.includes('Processing') && 
                                                  !result.project.status.includes('Stage B Complete') && 
                                                  !result.project.status.includes('Stage C Complete');
                        
                        if (shouldResetWorkflow) {
                            console.log('✅ [FETCH] Server confirms processing started, resetting workflow button state');
                            setIsStartingWorkflow(false);
                        } else if (result.project.status.includes('Stage B Complete') || result.project.status.includes('Stage C Complete')) {
                            console.log('🎯 [FETCH] Stage complete - keeping workflow state to prevent flash until table loads');
                            // Keep isStartingWorkflow=true to maintain loading screen during completion transition
                        }
                    }
                } else {
                    console.log('💳 [FETCH] Skipping status update during reset process');
                }
                setIsLoading(false);
                setError(null);
                setNetworkError(null); // Clear network error on success
                setFirstNetworkErrorTime(null);

                // If project is in review stage, fetch review data
                if (result.project.reviewStage) {
                    await fetchReviewData(id, result.project.reviewStage);
                    
                    // Reset grouping attempts when first entering Stage C or when not in Stage C
                    if (result.project.reviewStage === 'C') {
                        // Only reset if we don't already have attempts recorded for this session
                        if (groupingAttempts === 0) {
                            setGroupingAttempts(0);
                        }
                    } else {
                        // Reset attempts when not in Stage C
                        setGroupingAttempts(0);
                    }
                } else {
                    // Reset attempts when not in any review stage
                    setGroupingAttempts(0);
                }

                // Stop polling if workflow completed, failed, or in any review stage
                const shouldStopPolling = result.project.status.toLowerCase() === 'completed' || 
                    result.project.status.toLowerCase() === 'error' || 
                    result.project.status.startsWith('Pending Review') ||
                    result.project.reviewStage;  // Also check if reviewStage is set
                
                if (shouldStopPolling) {
                    if (pollingIntervalId) {
                        clearInterval(pollingIntervalId);
                        setPollingIntervalId(null);
                        console.log(`[ProjectDetail] Stopped polling - project in review stage or completed`);
                    }
                } else {
                    // FIXED: Only start polling if not already polling and project is still processing
                    // AND we're not currently in a manual review stage
                    const isInManualReview = result.project.reviewStage || 
                                             result.project.status.includes('Review') ||
                                             result.project.status.includes('Pending');
                    if (!pollingIntervalId && !shouldStopPolling && !isInManualReview) {
                        console.log(`[ProjectDetail] Starting polling for active project`);
                        const intervalId = setInterval(() => fetchProject(id, false), 10000); // NOT a retry, so pass false
                        setPollingIntervalId(intervalId);
                    }
                }
            } else {
                throw new Error(result.error || 'Failed to fetch project');
            }
        } catch (err: any) {
            console.error("[ProjectDetail] fetchProject caught error:", err);
            
            const isNetworkError = err.message === 'Failed to fetch' || 
                                   err.name === 'TypeError' || 
                                   err.message.includes('NetworkError') ||
                                   err.message.includes('Network');
            
            const isAuthError = err.message.includes('401') || 
                               err.message.includes('403') || 
                               err.message.includes('Unauthorized') ||
                               err.message.includes('Access denied');
            
            if (isAuthError) {
                console.log('[ProjectDetail] Authentication error detected, but not auto-logging out');
                // Don't automatically logout - let the user try to refresh or navigate
                setNetworkError("Authentication issue detected. Please try refreshing the page or logging in again.");
                return;
            }
            
            if (isNetworkError) {
                const now = Date.now();
                
                if (firstNetworkErrorTime === null) {
                    setFirstNetworkErrorTime(now);
                } else {
                    const errorDuration = now - firstNetworkErrorTime;
                    const isInReviewStage = status && (status.reviewStage || status.status.startsWith('Pending Review'));
                    const errorThreshold = isInReviewStage ? 20000 : 10000;
                    
                    if (errorDuration > errorThreshold) {
                        // Set network error instead of blocking error
                        if (status && status.reviewStage === 'B') {
                            setNetworkError("Network connection issue during term review. You can still continue working - the app will try to reconnect.");
                        } else {
                            setNetworkError("Network connection issue. Your work is saved - the app will try to reconnect automatically.");
                        }
                        
                        // CRITICAL FIX: Remove the infinite retry loop for Stage B
                        // The old code was: setTimeout(() => fetchProject(id), 500);
                        // This caused infinite retries every 500ms, leading to resource exhaustion
                        if (status && status.reviewStage === 'B') {
                            console.log(`[ProjectDetail] Stage B network error detected. Will retry with throttling.`);
                            // Use throttled retry instead of immediate retry
                            setTimeout(() => fetchProject(id, true), Math.max(retryThrottleMs, 2000)); // At least 2 seconds delay
                        }
                    }
                }
                return; // Don't set critical error for network issues
            }
            
            // Only set critical error for non-network, non-auth issues
            setError(err.message);      
            setIsLoading(false);
            if (pollingIntervalId) {
                clearInterval(pollingIntervalId);
                setPollingIntervalId(null);
            }
        }
    }, [pollingIntervalId, firstNetworkErrorTime, projectService, groupingAttempts, isResettingStatus]); // CRITICAL: Added isResettingStatus to prevent race conditions

    const fetchReviewData = async (projectId: string, stage: string) => {
        try {
            console.log(`[ProjectDetail] Fetching review data for stage ${stage}`);
            const { getApiUrl: __getApiUrl } = await import('../client/lib/config');
            const response = await fetch(__getApiUrl(`/api/projects/${projectId}/review-data/${stage}`), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('orca_auth_token')}`,
                },
            });

            if (response.ok) {
                const reviewData = await response.json();
                console.log(`[ProjectDetail] Received review data for stage ${stage}:`, reviewData);
                
                // More tolerant validation for Stage C
                if (stage === 'C') {
                    // Always try to set the data, even if it's not perfect
                    let normalizedData: {
                        suggestions: any[];
                        uniqueTerms: string[];
                    } = {
                        suggestions: [],
                        uniqueTerms: []
                    };
                    
                    // Handle different data formats more flexibly
                    if (reviewData && typeof reviewData === 'object') {
                        // Extract suggestions array
                        if (Array.isArray(reviewData.suggestions)) {
                            normalizedData.suggestions = reviewData.suggestions;
                        } else if (Array.isArray(reviewData)) {
                            normalizedData.suggestions = reviewData;
                        } else {
                            // Try to find suggestions in the data
                            const possibleSuggestions = Object.values(reviewData).find(val => Array.isArray(val));
                            if (possibleSuggestions) {
                                normalizedData.suggestions = possibleSuggestions as any[];
                            }
                        }
                        
                        // Extract unique terms
                        if (Array.isArray(reviewData.uniqueTerms)) {
                            normalizedData.uniqueTerms = reviewData.uniqueTerms;
                        }
                        
                        // Clean up suggestions - remove invalid ones but don't reject all data
                        if (normalizedData.suggestions.length > 0) {
                            const validSuggestions = normalizedData.suggestions.filter((s: any) => 
                                s && 
                                (s.terms && Array.isArray(s.terms) && s.terms.length > 0) && 
                                (s.suggested_main || (s.terms && s.terms.length > 0))
                            );
                            
                            // Ensure each suggestion has required fields
                            normalizedData.suggestions = validSuggestions.map((s: any) => ({
                                type: s.type || 'merge',
                                terms: s.terms || [],
                                suggested_main: s.suggested_main || (s.terms && s.terms[0]) || '',
                                reason: s.reason || 'No reason provided'
                            }));
                            
                            console.log(`[ProjectDetail] Processed ${normalizedData.suggestions.length} valid suggestions for Stage C`);
                        }
                    }
                    
                    // Always set the data, even if suggestions is empty (show "no suggestions" UI)
                    setStatus(prev => prev ? { 
                        ...prev, 
                        reviewData: normalizedData
                    } : null);
                    
                    console.log(`[ProjectDetail] Stage C data set successfully:`, normalizedData);
                } else {
                    // For other stages, use simpler validation
                    setStatus(prev => prev ? { ...prev, reviewData } : null);
                }
            } else if (response.status === 404) {
                console.log(`[ProjectDetail] No review data found for stage ${stage} yet, will retry...`);
                // For Stage C, set empty data structure so UI can show "no suggestions" message
                if (stage === 'C') {
                    setStatus(prev => prev ? { 
                        ...prev, 
                        reviewData: { 
                            suggestions: [], 
                            uniqueTerms: [] 
                        } 
                    } : null);
                }
            } else {
                console.error(`[ProjectDetail] Failed to fetch review data for stage ${stage}:`, response.status);
                // Only set error for serious issues, not temporary ones
                if (response.status >= 500) {
                    setError(`Failed to load ${stage} review data. Please try refreshing the page.`);
                }
            }
        } catch (err: any) {
            console.error("Error fetching review data:", err);
            // For Stage C, provide fallback empty data so UI doesn't break
            if (stage === 'C') {
                setStatus(prev => prev ? { 
                    ...prev, 
                    reviewData: { 
                        suggestions: [], 
                        uniqueTerms: [] 
                    } 
                } : null);
            }
            // Only set error for network issues if they persist
            if (!err.message.includes('fetch')) {
                setError(`Error loading review interface: ${err.message}`);
            }
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            console.log('[ProjectDetail] User not authenticated, redirecting to home');
            navigate('/');
            return;
        }

        if (!projectId) {
            console.error('[ProjectDetail] No project ID provided');
            setError("No project ID provided.");
            setIsLoading(false);
            return;
        }
        
        console.log('[ProjectDetail] Starting component initialization with stable state');

        console.log(`[ProjectDetail] Component mounting/updating for project: ${projectId}`);

            // CRITICAL: Clear any existing polling interval BEFORE starting new operations
        if (pollingIntervalId) {
            console.log(`[ProjectDetail] Clearing existing polling interval before initialization`);
            clearInterval(pollingIntervalId);
            setPollingIntervalId(null);
        }

        // Reset network error when component initializes
        setFirstNetworkErrorTime(null);
        setNetworkError(null);
        // Note: Retry counters are managed within fetchProject to avoid infinite loops

        setIsLoading(true);
        console.warn(`[ProjectDetail] 🚨 STARTING fetchProject for projectId: ${projectId}`);
        fetchProject(projectId, false); // Initialize with fetchProject, not a retry

        // Comprehensive cleanup when component unmounts or projectId changes
        return () => {
            console.log(`[ProjectDetail] Cleanup triggered for project: ${projectId}`);
            if (pollingIntervalId) {
                console.log(`[ProjectDetail] Clearing polling interval during cleanup`);
                clearInterval(pollingIntervalId);
                setPollingIntervalId(null);
            }
            // Note: Retry counters are managed within fetchProject to avoid infinite loops
        };
    }, [projectId, isAuthenticated]); // Keep dependencies minimal and stable - removed navigate to prevent re-renders

    // CRITICAL FIX: Update window.__PAGE_RANGE__ when status/pageRange changes
    useEffect(() => {
        if (status?.pageRange) {
            (window as any).__PAGE_RANGE__ = status.pageRange;
            console.log('[ProjectDetail] Updated window.__PAGE_RANGE__:', status.pageRange);
        }
    }, [status?.pageRange]);

    // ENHANCED: Separate useEffect to handle stopping polling during manual review
    useEffect(() => {
        // Stop polling immediately if we're in a manual review stage
        if (status?.reviewStage && pollingIntervalId) {
            console.log(`[ProjectDetail] Stopping polling due to manual review stage: ${status.reviewStage}`);
            clearInterval(pollingIntervalId);
            setPollingIntervalId(null);
        }
        
        // Also stop polling if we're in Stage B Complete to prevent interference
        if (status?.status === 'Stage B Complete' && pollingIntervalId) {
            console.log(`[ProjectDetail] Stopping polling due to Stage B Complete status`);
            clearInterval(pollingIntervalId);
            setPollingIntervalId(null);
        }

        // ADDED: Stop polling if we have reached max retries to prevent resource exhaustion
        if (retryCount >= maxRetries && pollingIntervalId) {
            console.log(`[ProjectDetail] Stopping polling due to max retries reached (${maxRetries})`);
            clearInterval(pollingIntervalId);
            setPollingIntervalId(null);
        }
    }, [status?.reviewStage, status?.status, pollingIntervalId, retryCount, maxRetries]);

    const handleReviewComplete = () => {
        console.log("Review complete signal received, restarting polling...");
        
        // ENHANCED: Comprehensive state reset
        setError(null);
        setIsLoading(true);
        setFirstNetworkErrorTime(null);
        setNetworkError(null);
        
        // CRITICAL: Reset retry counters to prevent carrying over failed state
        setRetryCount(0);
        setLastRetryTime(0);

        if (projectId) {
            // CRITICAL: Ensure we clear any existing polling before starting fresh
            if (pollingIntervalId) {
                console.log("[ProjectDetail] Clearing existing polling before review complete restart");
                clearInterval(pollingIntervalId);
                setPollingIntervalId(null);
            }
            
            // Clear review state first
            setStatus(prev => prev ? { ...prev, reviewStage: undefined, reviewData: undefined } : null);
            
            // Fetch project status immediately (not a retry, so pass false)
            fetchProject(projectId, false);
            
            // Polling will be restarted automatically in fetchProject if needed
        }
    };

    const renderStatusIcon = (currentStatus: string) => {
        if (currentStatus.startsWith('Pending Review')) return <AlertCircle className="h-5 w-5 text-yellow-500" />;
        if (currentStatus.toLowerCase() === 'completed') return <CheckCircle className="h-5 w-5 text-green-500" />;
        if (currentStatus.toLowerCase() === 'error') return <AlertCircle className="h-5 w-5 text-red-500" />;
        // Remove spinning loader - just return null for processing states
        return null;
    };

    const shouldShowProcessingStatus = () => {
        return false; // Disabled to prevent unwanted status box from appearing
    };

    const getUserFriendlyStatus = (currentStatus: string) => {
        // Convert technical status names to user-friendly descriptions
        const statusMap: Record<string, string> = {
            'Processing Stage A: Extracting Terms': 'Extracting Terms',
            'Processing Stage B: Standardizing Terms': 'Standardizing Terms', 
            'Processing Stage C: Collecting Terms': 'Grouping Terms',
            'Processing Stage C1: Duplicate Detection': 'Duplicate Detection & Relationship Review',
            'Processing Stage C2: Relationship Analysis': 'Duplicate Detection & Relationship Review',
            'Processing Stage C3: Final Relationship Review': 'Duplicate Detection & Relationship Review',
            'Processing Stage D: Collecting Context': 'Collecting Context',
            'Processing Stage E: Creating Index': 'Creating Index',
            'Pending Review B': 'Term Standardization Review',
            'Pending Review C': 'Term Grouping Review',
            'Pending Review C1': 'Duplicate Detection Review',
            'Pending Review C2': 'Relationship Analysis Review',
            'Pending Review D': 'Final Index Review',
            'Stage D Complete - Ready for Export': 'Ready for Export',
            'completed': 'Index Complete',
            'error': 'Processing Error',
            'processing': 'Processing'
        };
        
        return statusMap[currentStatus] || currentStatus;
    };

    // Unified Loading Screen System
    const getLoadingScreenTitle = (currentStatus: string): string => {
        const titleMap: Record<string, string> = {
            'Processing Stage A: Extracting Terms': 'Processing...',
            'Processing Stage B: Standardizing Terms': 'Processing...', 
            'Processing Stage C: Collecting Terms': 'Processing...',
            'Processing Stage C1: Duplicate Detection': 'Processing...',
            'Processing Stage C2: Relationship Review': 'Processing...',
            'Processing Stage C2: Relationship Analysis': 'Processing...',
            'Processing Stage C3: Final Relationship Review': 'Processing...',
            'Processing Stage D: Collecting Context': 'Processing...',
            'Processing Stage E: Creating Index': 'Processing...'
        };
        
        return titleMap[currentStatus] || 'Processing';
    };

    const renderUnifiedLoadingScreen = (isDesktop: boolean = true) => {
        // Allow rendering even before first status arrives
        const currentStatus = status?.status || (isStartingWorkflow ? 'Processing Stage A: Extracting Terms' : 'Processing');
        console.log('🎬 [RENDER] renderUnifiedLoadingScreen - currentStatus:', currentStatus, 'isStartingWorkflow:', isStartingWorkflow);
        const title = getLoadingScreenTitle(currentStatus);
        
        // Calculate progress based on processing stage
        const getProcessingProgress = (currentStatus: string) => {
            const stageMap: Record<string, number> = {
                'Processing Stage A: Extracting Terms': 20,
                'Processing Stage B: Standardizing Terms': 40,
                'Processing Stage C: Collecting Terms': 50,
                'Processing Stage C1: Duplicate Detection': 60,
                'Processing Stage C2: Relationship Review': 70,
                'Processing Stage C2: Relationship Analysis': 70,
                'Processing Stage C3: Final Relationship Review': 80,
                'Processing Stage D: Collecting Context': 90,
                'Processing Stage E: Creating Index': 95
            };
            return stageMap[currentStatus] || 10;
        };

        const progress = getProcessingProgress(currentStatus);
        
        const previewFilename = status?.originalFilename || project?.originalFilename || '';
        const pdfPanel = (
            <div className={(isDesktop ? "pl-3 " : "") + "h-full flex flex-col"}>
                {previewFilename ? (
                    <AdvancedPDFViewer 
                        projectId={projectId || ''} 
                        filename={previewFilename} 
                        highlightedTerm={highlightedTerm} 
                        currentPage={currentPage} 
                        title="Document Display"
                        onClearHighlight={handleClearHighlight}
                        termUpdates={termUpdates}
                        isMinimized={isPdfMinimized}
                        onToggleMinimize={handleTogglePdfMinimize}
                    />
                ) : (
                    <Card className="bg-muted/10 border border-border/30">
                        <CardContent>
                            <div className="py-10 text-center text-sm text-muted-foreground">
                                Loading document preview...
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        );

        const loadingContent = (
            <div className="space-y-6 pr-3 h-full flex items-center justify-center pointer-events-none select-none overflow-hidden relative">
                 <Card className="bg-muted/10 border border-border/30 w-[560px] max-w-[80vw]">
                     <CardContent>
                         <div className="py-20 text-center">
                             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                             <div className="text-sm text-muted-foreground">{title}</div>
                         </div>
                     </CardContent>
                 </Card>
             </div>
         );

        if (isDesktop) {
            return (
                <ResizablePanels
                    leftPanel={loadingContent}
                    rightPanel={pdfPanel}
                    defaultLeftWidth={65}
                    minLeftWidth={40}
                    maxLeftWidth={80}
                    isRightPanelMinimized={isPdfMinimized}
                    onToggleRightPanel={handleTogglePdfMinimize}
                    stickyTopOffset={120}
                />
            );
        } else {
            // Mobile: Stack vertically
            return (
                <div className="space-y-6">
                    {loadingContent}
                    <Card>
                        <CardContent>
                            {pdfPanel}
                        </CardContent>
                    </Card>
                </div>
            );
        }
    };

    const shouldShowUnifiedLoadingScreen = () => {
        // During initial mount before status arrives, show loader if we are loading or starting
        if (!status) {
            return !!isLoading || !!isStartingWorkflow;
        }
        
        // Sticky window after start to bridge A->B quick switch
        if (startLoadingStickyUntil && Date.now() < startLoadingStickyUntil) {
            return true;
        }
        
        // Show the unified loader during any processing or while a workflow start is in-flight
        const isProcessing = (
            !!status.status && (
                status.status.includes('Processing Stage') ||
                status.status.includes('Extracting Terms') ||
                status.status.includes('Standardizing Terms') ||
                status.status.includes('Collecting Terms') ||
                status.status.includes('Duplicate Detection') ||
                status.status.includes('Relationship Analysis') ||
                status.status.includes('Collecting Context') ||
                status.status.includes('Creating Index')
            )
        );

        // Also keep spinner when a review stage is set but review data has not arrived yet
        const waitingForReview = !!status.reviewStage && (!status.reviewData || (status.reviewData as any).isLoading === true);

        // FIXED: Don't hide background content when payment modal is open
        // Show loading during initial start click, but NOT when payment modal is shown
        // This preserves the project configuration UI behind the payment modal
        const startingNow = !!isStartingWorkflow && !showPaymentModal;

        // CRITICAL: Keep loading screen during completion transition to prevent flash
        // When Stage B/C is complete, keep loading until terms table is ready to show
        const completionTransition = (status.status.includes('Stage B Complete') || 
                                    status.status.includes('Stage C Complete')) && 
                                    isStartingWorkflow;

        // Keep loader sticky during rapid A->B transition
        if (startLoadingStickyUntil && Date.now() < startLoadingStickyUntil) {
            return true;
        }
        return isProcessing || waitingForReview || startingNow || completionTransition;
    };

    const handleStageSubmission = async (
        stage: 'B' | 'C' | 'C1' | 'C2' | 'C3' | 'D', // Added C3 for three-stage grouping
        payload: any,
        maxRetries = 3
    ) => {
        console.log(`[ProjectDetail] 🚀 Starting ${stage} submission with payload:`, payload);
        
        // Enhanced debugging for Stage C2
        if (stage === 'C2') {
            console.log(`\n🔍 ===== STAGE C2 SUBMISSION DEBUG START =====`);
            console.log(`[ProjectDetail] Project ID: ${projectId}`);
            console.log(`[ProjectDetail] Current project state:`, {
                status: project?.status,
                reviewStage: project?.reviewStage,
                hasStageC2ResumeData: !!(project as any)?.stageC2ResumeData,
                stageC2ResumeDataKeys: project && (project as any).stageC2ResumeData ? 
                    Object.keys((project as any).stageC2ResumeData) : 'N/A'
            });
            
            // Check authentication token
            const token = localStorage.getItem('token');
            console.log(`[ProjectDetail] Auth token exists: ${!!token}`);
            if (token) {
                try {
                    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
                    const isExpired = tokenPayload.exp * 1000 < Date.now();
                    console.log(`[ProjectDetail] Token expired: ${isExpired}`);
                    if (isExpired) {
                        console.log(`[ProjectDetail] ⚠️ Token is expired, attempting refresh...`);
                        const newToken = await refreshAuthToken();
                        if (!newToken) {
                            console.error(`[ProjectDetail] ❌ Failed to refresh token`);
                        }
                    }
                } catch (tokenError) {
                    console.error(`[ProjectDetail] ❌ Error checking token:`, tokenError);
                }
            }
            
            // Validate frontend state before submission
            const hasC2ResumeData = !!(project as any)?.stageC2ResumeData;
            const isCorrectStage = project?.reviewStage === 'C2';
            
            console.log(`[ProjectDetail] Frontend validation:`);
            console.log(`  - Review stage is C2: ${isCorrectStage} (actual: ${project?.reviewStage})`);
            console.log(`  - Has stageC2ResumeData: ${hasC2ResumeData}`);
            
            if (hasC2ResumeData) {
                const resumeData = (project as any).stageC2ResumeData;
                console.log(`  - stageC2ResumeData details:`, {
                    standardizedTermsPath: resumeData.standardizedTermsPath,
                    availableTermsCount: resumeData.availableTerms?.length || 0,
                    duplicateDecisionsCount: resumeData.duplicateDecisions?.length || 0,
                    hasC1ParentMap: !!resumeData.c1ParentMap,
                    hasC1CrossRefMap: !!resumeData.c1CrossRefMap
                });
            }
            
            if (!isCorrectStage) {
                console.error(`[ProjectDetail] ❌ Frontend validation failed before API call`);
                console.error(`[ProjectDetail] Expected: reviewStage='C2'`);
                console.error(`[ProjectDetail] Actual: reviewStage='${project?.reviewStage}'`);
                console.log(`[ProjectDetail] ===== STAGE C2 SUBMISSION DEBUG END =====\n`);
                
                // Show user-friendly error message
                throw new Error(`Project state validation failed. Current state: reviewStage='${project?.reviewStage}'. Please refresh the page and ensure you're in Stage C2.`);
            }
            
            console.log(`[ProjectDetail] ✅ Frontend validation passed (removed stageC2ResumeData requirement)`);
            console.log(`[ProjectDetail] ===== STAGE C2 SUBMISSION DEBUG END =====\n`);
        }

        setIsSubmitting(true);
        setError(null);

        // Special handling for C2 -> C3 transition: Show loading state for Stage C3 immediately
        if (stage === 'C2') {
            setStatus(prev => prev ? {
                ...prev,
                status: 'Processing Stage C3: Final Duplicate Detection',
                reviewStage: undefined, // Clear review stage since we're now processing
                reviewData: undefined
            } : null);
        }
        
        // Special handling for C3 -> D transition: Show loading state for Stage D immediately
        if (stage === 'C3') {
            setStatus(prev => prev ? {
                ...prev,
                status: 'Processing Stage D: Collecting Context',
                reviewStage: undefined, // Clear review stage since we're now processing
                reviewData: undefined
            } : null);
        }

        try {
            let retryCount = 0;
            let lastError: Error | null = null;

            while (retryCount < maxRetries) {
                try {
                    console.log(`[ProjectDetail] 📡 Making API request (attempt ${retryCount + 1}/${maxRetries})`);
                    const { getApiUrl: ____getApiUrl } = await import('../client/lib/config');
                    const response = await makeAuthenticatedRequest(____getApiUrl(`/api/projects/${projectId}/submit-review/${stage}`), {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        console.error(`[ProjectDetail] ❌ Stage ${stage} submission failed with status ${response.status}:`, errorData);
                        
                        // Enhanced error handling for Stage C2
                        if (stage === 'C2' && response.status === 400) {
                            console.error(`[ProjectDetail] 🔍 Stage C2 400 Error Analysis:`);
                            console.error(`  Server error message: "${errorData.error}"`);
                            console.error(`  This suggests the server-side project state differs from frontend`);
                            console.error(`  Recommend: 1) Refresh page, 2) Check project state sync, 3) Verify C1 was completed`);
                            
                            // Provide specific guidance based on error message
                            if (errorData.error === 'Project not ready for Stage C2 review submission') {
                                throw new Error(
                                    `Stage C2 submission failed: ${errorData.error}. ` +
                                    `This usually means: 1) Stage C1 wasn't completed, 2) Frontend/backend state mismatch, or 3) Authentication issue. ` +
                                    `Please refresh the page and try again.`
                                );
                            }
                        }
                        
                        if (response.status === 401 || response.status === 403) {
                            // Authentication error - try refreshing token and retry
                            console.log(`[ProjectDetail] 🔄 Authentication error, refreshing token and retrying...`);
                            const newToken = await refreshAuthToken();
                            if (newToken) {
                                retryCount++;
                                continue; // Retry with new token
                            }
                        }
                        
                        throw new Error(`Stage ${stage} submission failed: ${errorData.error || 'Unknown error'}`);
                    }

                    const result = await response.json();
                    console.log(`[ProjectDetail] ✅ Stage ${stage} submission successful:`, result);
                    
                    // Handle special responses for state synchronization
                    if (result.redirectTo === "stage-d-complete") {
                        console.log(`[ProjectDetail] 🎉 Project is already complete - refreshing to show final state`);
                        setIsSubmitting(false);
                        window.location.reload(); // Refresh to show the completed state
                        return result;
                    }
                    
                    if (result.redirectTo === "stage-d") {
                        console.log(`[ProjectDetail] 🔄 Project is in Stage D - refreshing to show Stage D interface`);
                        setIsSubmitting(false);
                        window.location.reload(); // Refresh to show Stage D
                        return result;
                    }
                    
                    // Clear submission state and refresh project data
                    setIsSubmitting(false);
                    handleReviewComplete();
                    
                    return result;

                } catch (error: any) {
                    console.error(`[ProjectDetail] ❌ Attempt ${retryCount + 1} failed:`, error);
                    lastError = error;
                    retryCount++;
                    
                    // If not the last retry, wait before retrying
                    if (retryCount < maxRetries) {
                        console.log(`[ProjectDetail] ⏳ Waiting 1 second before retry...`);
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                }
            }

            // All retries exhausted
            throw lastError || new Error(`Stage ${stage} submission failed after ${maxRetries} attempts`);

        } catch (error: any) {
            console.error(`[ProjectDetail] ❌ Stage ${stage} submission failed:`, error.message);
            setIsSubmitting(false);
            
            // If this was a C2 -> D transition that failed, revert the status
            if (stage === 'C2') {
                console.log(`[ProjectDetail] Reverting status after failed C2 submission`);
                setStatus(prev => prev ? {
                    ...prev,
                    status: 'Pending Review C2',
                    reviewStage: 'C2',
                    reviewData: prev.reviewData // Restore review data
                } : null);
            }
            
            // Enhance error message with troubleshooting tips
            let userFriendlyError = error.message || "Failed to submit review";
            if (error.message?.includes('Authentication failed') || error.message?.includes('401') || error.message?.includes('403')) {
                userFriendlyError += " Try refreshing the page and logging in again.";
            } else if (error.message?.includes('Project not ready for Stage C2 review submission')) {
                userFriendlyError = "The project is not ready for Stage C2 submission. This may be due to a sync issue. Please refresh the page and try again.";
            }
            
            setError(userFriendlyError);
            throw error;
        }
    };

    const handleBackToProjects = async () => {
        try {
            // Auto-save any pending changes before navigating
            const hasUpdates = Object.keys(termUpdates).length > 0;
            const hasDeletions = deletedRows.length > 0;
            const hasAdditions = addedTerms.length > 0;
            
            if (hasUpdates || hasDeletions || hasAdditions) {
                console.log(`[ProjectDetail] Auto-saving changes before navigation: ${Object.keys(termUpdates).length} edits, ${deletedRows.length} deletions, ${addedTerms.length} additions`);
                await autoSaveAllChanges(termUpdates, deletedRows, addedTerms);
                
                // Clear the change states so beforeunload handler doesn't trigger
                setTermUpdates({});
                setDeletedRows([]);
                setAddedTerms([]);
                console.log('[ProjectDetail] Cleared change states after auto-save');
            }
            
            // Navigate to projects page
            navigate('/projects');
        } catch (error) {
            console.error('[ProjectDetail] Error saving changes before navigation:', error);
            // Still navigate even if save fails, but warn user
            const shouldContinue = window.confirm('Failed to save changes. Do you still want to leave? Unsaved changes will be lost.');
            if (shouldContinue) {
                // Clear states and navigate anyway
                setTermUpdates({});
                setDeletedRows([]);
                setAddedTerms([]);
                navigate('/projects');
            }
        }
    };

    const handleStartWorkflow = async () => {
        if (!projectId) return;
        
        // CRITICAL: Don't start workflow if we're in the middle of resetting status
        if (isResettingStatus) {
            console.log('💳 [WORKFLOW] Workflow start blocked - status reset in progress');
            return;
        }

        console.log('💳 [WORKFLOW] Starting workflow - isResettingStatus:', isResettingStatus);
        setIsStartingWorkflow(true);
        setError(null);

        try {
            const result = await projectService.startWorkflow(projectId, {
                startPage,
                endPage,
                endnotesEnabled,
                endnotesStartPage
            });
            
            if (result.success) {
                if (result.requiresPayment) {
                    // Payment required - show payment modal
                    console.log('💳 [PAYMENT] Payment required, showing payment modal');
                    console.log('💳 [PAYMENT] Current component state before modal:', {
                        hasProject: !!project,
                        hasStatus: !!status,
                        projectStatus: status?.status,
                        isLoading
                    });
                    
                    // CRITICAL: Set payment data and show modal WITHOUT changing other state
                    // This prevents the background from disappearing
                    setPaymentData({
                        paymentIntentId: result.paymentIntentId!,
                        paymentIntentClientSecret: result.paymentIntentClientSecret!,
                        costEstimate: result.costEstimate,
                        billingBreakdown: result.billingBreakdown
                    });
                    setShowPaymentModal(true);
                    
                    // CRITICAL: DO NOT set isStartingWorkflow to false here!
                    // The workflow is not complete - it's waiting for payment
                    // Setting it to false changes shouldShowUnifiedLoadingScreen() and breaks the UI
                    console.log('💳 [PAYMENT] Payment modal shown, keeping workflow state active until payment');
                    
                    // Return early - isStartingWorkflow stays true until payment completes or is canceled
                    return;
                } else {
                    // No payment needed - start processing immediately
                    console.log('🆓 [FREE] No payment needed, starting workflow');
                    // Immediately show a processing spinner to avoid blank state and prevent flicker
                    setStatus(prev => prev ? { ...prev, status: 'Processing Stage A: Extracting Terms', reviewStage: undefined } : prev);
                    // Keep unified loader sticky for a minimum window to cover A->B transition
                    setStartLoadingStickyUntil(Date.now() + 4000);
                    // Poll once for updated project and keep spinner until data arrives
                    await fetchProject(projectId);
                    
                    // Reset workflow button state since processing has started successfully
                    setIsStartingWorkflow(false);
                    console.log('🆓 [FREE] Free workflow started successfully, button spinner disabled');
                }
            } else {
                // Handle API error without affecting background UI
                const errorMessage = result.error || 'Failed to start AI workflow';
                console.error('💳 [WORKFLOW ERROR] API returned error:', errorMessage);
                console.log('💳 [WORKFLOW ERROR] Preserving background state during error display');
                
                setError(errorMessage);
                
                // CRITICAL: Ensure background UI remains stable during error state
                // Don't change project status, don't trigger navigation, just show error
                console.log('💳 [WORKFLOW ERROR] Background state preserved, error shown to user');
                
                // Reset workflow state since the API call failed
                setIsStartingWorkflow(false);
                console.log('💳 [WORKFLOW] API error occurred, button spinner disabled');
            }
        } catch (err: any) {
            // Handle network/exception errors without affecting background UI
            const errorMessage = err.message || 'Failed to start AI workflow';
            console.error('💳 [WORKFLOW ERROR] Exception occurred:', errorMessage, err);
            console.log('💳 [WORKFLOW ERROR] Preserving background state during exception handling');
            
            setError(errorMessage);
            
            // CRITICAL: Ensure background UI remains stable during error state
            console.log('💳 [WORKFLOW ERROR] Background state preserved, exception handled');
            
            // Reset workflow state only when there's an actual error
            setIsStartingWorkflow(false);
            console.log('💳 [WORKFLOW] Workflow start failed, button spinner disabled');
        }
        // REMOVED finally block - setIsStartingWorkflow(false) is now handled in specific cases
    };

    const handlePaymentSuccess = async () => {
        console.log('✅ [PAYMENT] Payment successful, starting workflow');
        
        // CRITICAL: Keep isStartingWorkflow=true to show loading screen during processing
        // Only reset it when the actual workflow processing begins
        console.log('✅ [PAYMENT] Keeping workflow state active to show loading screen');
        
        // Immediately show a processing spinner with proper status
        console.log('✅ [PAYMENT] Setting status to Processing Stage A');
        setStatus(prev => {
            const newStatus = prev ? { 
                ...prev, 
                status: 'Processing Stage A: Extracting Terms', 
                reviewStage: undefined 
            } : null;
            console.log('✅ [PAYMENT] Status updated to:', newStatus?.status);
            return newStatus;
        });
        // Keep unified loader sticky for a minimum window to cover A->B transition
        setStartLoadingStickyUntil(Date.now() + 4000);
        
        // Poll once for updated project - this will eventually reset isStartingWorkflow when processing actually starts
        if (projectId) {
            await fetchProject(projectId);
        }
    };

    // Add handler for finding additional groups
    const handleFindAdditionalGroups = async (currentDecisions: any[]) => {
        if (!projectId || groupingAttempts >= 3) return;
        
        try {
            setIsLoadingAdditionalGroups(true);
            setGroupingAttempts(prev => prev + 1);
            
            console.log(`[${projectId}] Sending current decisions for additional grouping:`, currentDecisions);
            
            // Filter out terms that have already been processed (merged or made subentries)
            const processedTerms = new Set<string>();
            currentDecisions.forEach(decision => {
                if (decision.action === 'process' && decision.relationships) {
                    // Add the main term
                    if (decision.main_term) {
                        processedTerms.add(decision.main_term.toLowerCase());
                    }

                    // Add terms that have been merged or made subentries
                    Object.entries(decision.relationships).forEach(([term, relationship]: [string, any]) => {
                        if (relationship === 'merge' || relationship === 'subentry') {
                            processedTerms.add(term.toLowerCase());
                        }
                    });
                }
            });
            
            console.log(`[${projectId}] Excluding ${processedTerms.size} already processed terms from additional grouping:`, Array.from(processedTerms));
            
            // Call API endpoint to run additional grouping
            const { getApiUrl: ___getApiUrl } = await import('../client/lib/config');
            const response = await fetch(___getApiUrl(`/api/projects/${projectId}/find-additional-groups`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('orca_auth_token')}`
                },
                body: JSON.stringify({ 
                    currentAttempt: groupingAttempts + 1,
                    currentDecisions: currentDecisions,
                    excludeTerms: Array.from(processedTerms) // Send excluded terms to backend
                })
            });

            if (!response.ok) {
                throw new Error('Failed to find additional groups');
            }
            
            const result = await response.json();
            console.log(`[${projectId}] Additional grouping result:`, result);
            
            // Refresh project data to get new grouping suggestions
            await fetchProject(projectId);
            
        } catch (error: any) {
            console.error('Error finding additional groups:', error);
            setError(error.message);
            // Revert attempt count on error
            setGroupingAttempts(prev => Math.max(0, prev - 1));
        } finally {
            setIsLoadingAdditionalGroups(false);
        }
    };

    // Optimized clear highlight handler to prevent memory accumulation
    const handleClearHighlight = () => {
        setHighlightedTerm('');
    };

    const handleTogglePdfMinimize = () => {
        setIsPdfMinimized(!isPdfMinimized);
    };

    // Add handler for continuing to next stage
    const handleContinueToStage = async (stage: 'C' | 'C1' | 'C2' | 'C3' | 'D' | 'E' | 'F') => { // Added C3 for three-stage grouping
        if (!projectId) return;
        
        try {
            setIsSubmitting(true);
            setError(null);
            
            // Auto-save any pending term edits before continuing
            const hasUpdates = Object.keys(termUpdates).length > 0;
            const hasDeletions = deletedRows.length > 0;
            const hasAdditions = addedTerms.length > 0;
            
            if (hasUpdates || hasDeletions || hasAdditions) {
                console.log(`[ProjectDetail] Auto-saving changes before stage transition: ${Object.keys(termUpdates).length} edits, ${deletedRows.length} deletions, ${addedTerms.length} additions`);
                await autoSaveAllChanges(termUpdates, deletedRows, addedTerms);
            }
            
            // IMMEDIATELY update the status to show the new processing stage with animated dots
            // This gives instant feedback to the user before the backend responds
            if (stage === 'C1') {
                setStatus(prev => prev ? {
                    ...prev,
                    status: 'Processing Stage C1: Duplicate Detection',
                    reviewStage: undefined, // Clear review stage since we're now processing
                    reviewData: undefined
                } : null);
            } else if (stage === 'C2') {
                setStatus(prev => prev ? {
                    ...prev,
                    status: 'Processing Stage C2: Relationship Analysis',
                    reviewStage: undefined, // Clear review stage since we're now processing
                    reviewData: undefined
                } : null);
            } else if (stage === 'C3') {
                setStatus(prev => prev ? {
                    ...prev,
                    status: 'Processing Stage C3: Final Duplicate Detection',
                    reviewStage: undefined, // Clear review stage since we're now processing
                    reviewData: undefined
                } : null);
            }
            
            console.log(`[ProjectDetail] ===== CONTINUE TO STAGE ${stage} DEBUG =====`);
            console.log(`[ProjectDetail] Project ID: ${projectId}`);
            console.log(`[ProjectDetail] Current status: ${status?.status}`);
            console.log(`[ProjectDetail] Current review stage: ${status?.reviewStage}`);
            console.log(`[ProjectDetail] Term updates count: ${Object.keys(termUpdates).length}`);
            
            const requestBody: any = {};
            
            // Include term updates if we're continuing from Stage B Complete to Stage C or C1
            if ((stage === 'C' || stage === 'C1') && Object.keys(termUpdates).length > 0) {
                requestBody.termUpdates = termUpdates;
                console.log(`[ProjectDetail] Including ${Object.keys(termUpdates).length} term updates:`, termUpdates);
            }
            
            // Include deleted rows and modified CSV data if we're continuing from Stage B Complete to Stage C or C1
            if ((stage === 'C' || stage === 'C1')) {
                // Get deleted rows from shared state (no longer using ref)
                if (deletedRows.length > 0) {
                    requestBody.deletedRows = deletedRows;
                    console.log(`[ProjectDetail] ===== DELETED ROWS DEBUG =====`);
                    console.log(`[ProjectDetail] Deleted rows count: ${deletedRows.length}`);
                    console.log(`[ProjectDetail] Deleted rows data:`, deletedRows);
                    console.log(`[ProjectDetail] ===== END DELETED ROWS DEBUG =====`);
                }
                
                // Include added terms from state
                if (addedTerms.length > 0) {
                    requestBody.addedTerms = addedTerms;
                    console.log(`[ProjectDetail] ===== ADDED TERMS DEBUG =====`);
                    console.log(`[ProjectDetail] Added terms count: ${addedTerms.length}`);
                    console.log(`[ProjectDetail] Added terms data:`, addedTerms);
                    console.log(`[ProjectDetail] ===== END ADDED TERMS DEBUG =====`);
                }
                
                // Get modified CSV data from the CSVViewer component
                if (getModifiedCSVDataRef.current) {
                    const modifiedCSVData = getModifiedCSVDataRef.current();
                    requestBody.modifiedCSVData = modifiedCSVData;
                    console.log(`[ProjectDetail] ===== MODIFIED CSV DATA DEBUG =====`);
                    console.log(`[ProjectDetail] Modified CSV data count: ${modifiedCSVData.length}`);
                    console.log(`[ProjectDetail] First 3 rows of modified CSV data:`, modifiedCSVData.slice(0, 3));
                    console.log(`[ProjectDetail] Last 3 rows of modified CSV data:`, modifiedCSVData.slice(-3));
                    
                    // Check for specific terms mentioned in the user's edits
                    const immermanRows = modifiedCSVData.filter(row => {
                        const term = row.term_standard || row.standardized_term || row.term || '';
                        return term.toLowerCase().includes('immerman');
                    });
                    console.log(`[ProjectDetail] Immerman rows in modified CSV data:`, immermanRows);
                    
                    // Check for any rows that have been edited (look for term_updated flag or differences)
                    const editedRows = modifiedCSVData.filter(row => row.term_updated === true);
                    console.log(`[ProjectDetail] Rows marked as edited:`, editedRows);
                    
                    // Sample a few random rows to see the data structure
                    const sampleRows = modifiedCSVData.slice(0, 5);
                    console.log(`[ProjectDetail] Sample row structures:`, sampleRows.map(row => ({
                        term: row.term,
                        term_standard: row.term_standard,
                        standardized_term: row.standardized_term,
                        page: row.page,
                        context: row.context,
                        term_updated: row.term_updated
                    })));
                    console.log(`[ProjectDetail] ===== END MODIFIED CSV DATA DEBUG =====`);
                }

                // Additional debug: Show what termUpdates contains vs what we expect
                console.log(`[ProjectDetail] ===== TERM UPDATES DETAILED DEBUG =====`);
                console.log(`[ProjectDetail] termUpdates object:`, termUpdates);
                console.log(`[ProjectDetail] termUpdates keys:`, Object.keys(termUpdates));
                console.log(`[ProjectDetail] termUpdates values:`, Object.values(termUpdates));
                console.log(`[ProjectDetail] Expected: Should show 3 edits but only showing ${Object.keys(termUpdates).length}`);
                console.log(`[ProjectDetail] ===== END TERM UPDATES DEBUG =====`);
            }
            
            // Final summary of what's being sent
            console.log(`[ProjectDetail] ===== FINAL REQUEST BODY SUMMARY =====`);
            console.log(`[ProjectDetail] termUpdates count: ${requestBody.termUpdates ? Object.keys(requestBody.termUpdates).length : 0}`);
            console.log(`[ProjectDetail] deletedRows count: ${requestBody.deletedRows ? requestBody.deletedRows.length : 0}`);
            console.log(`[ProjectDetail] addedTerms count: ${requestBody.addedTerms ? requestBody.addedTerms.length : 0}`);
            console.log(`[ProjectDetail] modifiedCSVData count: ${requestBody.modifiedCSVData ? requestBody.modifiedCSVData.length : 0}`);
            console.log(`[ProjectDetail] ===== END REQUEST BODY SUMMARY =====`);
            
            const { getApiUrl } = await import('../client/lib/config');
            const apiUrl = getApiUrl(`/api/projects/${projectId}/continue-to-stage/${stage}`);
            console.log(`[ProjectDetail] Making request to: ${apiUrl}`);
            console.log(`[ProjectDetail] Request body:`, requestBody);
            
            const response = await makeAuthenticatedRequest(apiUrl, {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });
            
            console.log(`[ProjectDetail] Response status: ${response.status}`);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`[ProjectDetail] Error response body:`, errorText);
                
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch {
                    errorData = { error: errorText };
                }
                
                console.error(`[ProjectDetail] Parsed error data:`, errorData);
                throw new Error(errorData.error || `Failed to continue to stage ${stage} (HTTP ${response.status})`);
            }
            
            const result = await response.json();
            console.log(`[ProjectDetail] Success response:`, result);
            
            // Show success message
            console.log(`[ProjectDetail] Successfully started transition to stage ${stage}`);
            
            // Clear all changes since they've been applied
            if ((stage === 'C' || stage === 'C1')) {
                if (Object.keys(termUpdates).length > 0) {
                    setTermUpdates({});
                    console.log(`[ProjectDetail] Cleared ${Object.keys(termUpdates).length} applied term updates`);
                }
                if (deletedRows.length > 0) {
                    setDeletedRows([]);
                    console.log(`[ProjectDetail] Cleared ${deletedRows.length} applied deletions`);
                }
                // Preserve added terms so newly created terms appear in subsequent stages
                // if (addedTerms.length > 0) {
                //     setAddedTerms([]);
                //     console.log(`[ProjectDetail] Cleared ${addedTerms.length} applied additions`);
                // }
            }
            
            // Refresh project data to get the actual backend status (this will update when processing completes)
            console.log(`[ProjectDetail] Refreshing project data...`);
            await fetchProject(projectId);
            console.log(`[ProjectDetail] Project data refreshed successfully`);
            
        } catch (error: any) {
            console.error(`[ProjectDetail] ===== CONTINUE TO STAGE ${stage} ERROR =====`);
            console.error(`[ProjectDetail] Error type: ${error.constructor.name}`);
            console.error(`[ProjectDetail] Error message: ${error.message}`);
            console.error(`[ProjectDetail] Error stack:`, error.stack);
            
            // Show user-friendly error message
            setError(`Failed to start ${stage === 'C' ? 'Collecting Terms' : stage === 'C1' ? 'Duplicate Detection' : stage === 'C2' ? 'Relationship Analysis' : `Stage ${stage}`} phase: ${error.message}`);
            
            // Revert status change on error
            if (stage === 'C1' || stage === 'C2') {
                setStatus(prev => prev ? {
                    ...prev,
                    status: 'Stage B Complete', // Revert to previous status
                    reviewStage: undefined,
                    reviewData: undefined
                } : null);
            }
        } finally {
            setIsSubmitting(false);
            console.log(`[ProjectDetail] ===== CONTINUE TO STAGE ${stage} COMPLETE =====`);
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    if (isLoading && !status) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                    <p className="text-foreground">Loading project...</p>
                    <p className="text-xs text-muted-foreground mt-2">Project ID: {projectId}</p>
                </div>
            </div>
        );
    }

    // Only show critical error page for non-network errors AND when we don't have any project data
    if (error && !status && !shouldShowProcessingStatus()) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Card className="w-full max-w-md">
                    <CardHeader>
                        <div className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-destructive" />
                            <CardTitle className="text-destructive">Error</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-foreground mb-4">{error}</p>
                        <p className="text-xs text-muted-foreground mb-4">Project ID: {projectId}</p>
                        <div className="flex gap-2">
                            <Button onClick={handleBackToProjects} variant="outline">
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Projects
                            </Button>
                            {projectId && (
                                <Button onClick={() => fetchProject(projectId)}>
                                    Try Again
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!status) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <p className="text-foreground">Project not found</p>
                    <p className="text-xs text-muted-foreground mt-2">Project ID: {projectId}</p>
                    <Button onClick={handleBackToProjects} variant="outline" className="mt-4">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Projects
                    </Button>
                </div>
            </div>
        );
    }

    // Helper function to convert plain object to Map (for data that was serialized as JSON)
    const convertObjectToMap = (obj: any): Map<string, string> | undefined => {
        if (!obj) return undefined;
        if (obj instanceof Map) return obj; // Already a Map
        if (typeof obj === 'object' && obj !== null) {
            // Convert plain object to Map
            const map = new Map<string, string>();
            Object.entries(obj).forEach(([key, value]) => {
                map.set(key, value as string);
            });
            return map;
        }
        return undefined;
    };

    // Helper function to convert plain object to Map<string, string[]> (for cross-references)
    const convertObjectToMapArray = (obj: any): Map<string, string[]> | undefined => {
        if (!obj) return undefined;
        if (obj instanceof Map) return obj; // Already a Map
        if (typeof obj === 'object' && obj !== null) {
            // Convert plain object to Map
            const map = new Map<string, string[]>();
            Object.entries(obj).forEach(([key, value]) => {
                map.set(key, Array.isArray(value) ? value : []);
            });
            return map;
        }
        return undefined;
    };

    // Extract relationship data from project status for the table
    const c1Decisions = (status as any).stageC1Decisions || [];
    const c2Decisions = (status as any).stageC2Decisions || [];
    const c3Decisions = (status as any).stageC3Decisions || [];
    const c1ParentMap = convertObjectToMap((status as any).stageC2ResumeData?.c1ParentMap);
    const c1CrossRefMap = convertObjectToMapArray((status as any).stageC2ResumeData?.c1CrossRefMap);

    // Debug relationship data
    console.log('[ProjectDetail] Relationship Data Debug:', {
        reviewStage: status.reviewStage,
        c1DecisionsLength: c1Decisions.length,
        c2DecisionsLength: c2Decisions.length,
        c3DecisionsLength: c3Decisions.length,
        c1ParentMapSize: c1ParentMap?.size || 0,
        c1CrossRefMapSize: c1CrossRefMap?.size || 0,
        stageC2ResumeData: (status as any).stageC2ResumeData,
        c1ParentMapRaw: (status as any).stageC2ResumeData?.c1ParentMap,
        c1CrossRefMapRaw: (status as any).stageC2ResumeData?.c1CrossRefMap
    });
    
    // More detailed debugging for the conversion
    if ((status as any).stageC2ResumeData?.c1ParentMap) {
        console.log('[ProjectDetail] c1ParentMap conversion details:');
        console.log('  Raw data type:', typeof (status as any).stageC2ResumeData.c1ParentMap);
        console.log('  Raw data:', (status as any).stageC2ResumeData.c1ParentMap);
        console.log('  Object.entries result:', Object.entries((status as any).stageC2ResumeData.c1ParentMap));
        console.log('  Converted Map size:', c1ParentMap?.size);
        if (c1ParentMap && c1ParentMap.size > 0) {
            console.log('  Map entries:', Array.from(c1ParentMap.entries()));
        }
    }
    
    if ((status as any).stageC2ResumeData?.c1CrossRefMap) {
        console.log('[ProjectDetail] c1CrossRefMap conversion details:');
        console.log('  Raw data type:', typeof (status as any).stageC2ResumeData.c1CrossRefMap);
        console.log('  Raw data:', (status as any).stageC2ResumeData.c1CrossRefMap);
        console.log('  Object.entries result:', Object.entries((status as any).stageC2ResumeData.c1CrossRefMap));
        console.log('  Converted Map size:', c1CrossRefMap?.size);
        if (c1CrossRefMap && c1CrossRefMap.size > 0) {
            console.log('  Map entries:', Array.from(c1CrossRefMap.entries()));
        }
    }

    // Check if we should show process modal

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto p-6">
                {/* Network Error Banner - non-blocking */}
                {networkError && (
                    <Card className="mb-6 border-orange-500/30 bg-orange-500/10">
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-foreground text-sm">{networkError}</p>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="mt-2 text-orange-500 border-orange-500/30 hover:bg-orange-500/20"
                                        onClick={() => {
                                            setNetworkError(null);
                                            setFirstNetworkErrorTime(null);
                                            if (projectId) fetchProject(projectId);
                                        }}
                                    >
                                        Try Reconnecting
                                    </Button>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setNetworkError(null)}
                                    className="text-orange-500 hover:text-orange-600 h-6 w-6 p-0"
                                >
                                    ×
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Transition Notification Banner */}
                {transitionNotification && (
                    <Card className={`mb-6 ${transitionNotification.type === 'info' ? 'border-primary/30 bg-primary/10' : 'border-green-600/30 bg-green-600/10'}`}>
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                                {transitionNotification.type === 'info' ? (
                                    <div className="text-primary mr-2">ℹ️</div>
                                ) : (
                                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1">
                                    <p className={`text-sm font-medium ${transitionNotification.type === 'info' ? 'text-foreground' : 'text-foreground'}`}>
                                        {transitionNotification.message}
                                    </p>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setTransitionNotification(null)}
                                    className={`h-6 w-6 p-0 ${transitionNotification.type === 'info' ? 'text-primary hover:text-primary/80' : 'text-green-600 hover:text-green-700'}`}
                                >
                                    ×
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Header */}
                <div className="mb-6">
                    <Button 
                        onClick={handleBackToProjects} 
                        variant="ghost" 
                        className="mb-4 text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to My Indexes
                    </Button>
                    
                    <div className="flex items-center gap-4 mb-2">
                        <div className="relative">
                            <div className="absolute inset-0 rounded-lg bg-purple-500 opacity-20 blur-md"></div>
                            <svg viewBox="0 0 120 120" className="w-12 h-12 relative z-10">
                                <defs>
                                    <linearGradient id="mainLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#8b5cf6" />
                                        <stop offset="50%" stopColor="#a855f7" />
                                        <stop offset="100%" stopColor="#3b82f6" />
                                    </linearGradient>
                                    <linearGradient id="mainBookGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#8b5cf6" />
                                        <stop offset="100%" stopColor="#a855f7" />
                                    </linearGradient>
                                </defs>
                                <rect x="20" y="30" width="80" height="60" rx="4" fill="url(#mainBookGradient)" className="drop-shadow-lg" />
                                <rect x="22" y="32" width="76" height="56" rx="2" fill="rgba(255,255,255,0.1)" />
                                <line x1="30" y1="42" x2="70" y2="42" stroke="white" strokeWidth="1.5" opacity="0.8" />
                                <line x1="30" y1="50" x2="85" y2="50" stroke="white" strokeWidth="1.5" opacity="0.8" />
                                <line x1="30" y1="58" x2="75" y2="58" stroke="white" strokeWidth="1.5" opacity="0.8" />
                                <circle cx="80" cy="40" r="12" fill="url(#mainLogoGradient)" className="drop-shadow-md" />
                                <circle cx="80" cy="40" r="8" fill="white" opacity="0.9" />
                                <circle cx="80" cy="40" r="4" fill="url(#mainLogoGradient)" />
                            </svg>
                        </div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-3xl font-bold text-foreground">
                                {project?.name || 'Index Project'}
                            </h1>
                        </div>
                    </div>
                    
                    {project?.description && (
                        <p className="text-muted-foreground mb-2">{project.description}</p>
                    )}
                </div>

                {/* Stage B Complete - Simple Review Interface */}
                {status.status === 'Stage B Complete' && (
                    <div className="hidden lg:block">
                        <ResizablePanels
                            leftPanel={
                                <div className="space-y-6 pr-3">
                                    <Card className="bg-muted/10">
                                        <CardHeader className="bg-muted/10 border-b border-border/20">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                                                        <div className="w-4 h-4 bg-primary rounded-full"></div>
                                                    </div>
                                                    <div>
                                                        <CardTitle className="text-foreground text-xl font-semibold">
                                                            Review Extracted Terms
                                                        </CardTitle>
                                                        <div className="text-sm text-muted-foreground mt-1">
                                                            {uniqueTermsCount} unique terms found
                                                        </div>
                                                    </div>
                                                    {/* Auto-save status indicator */}
                                                    {isSavingTermEdits && (
                                                        <div className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                                                            <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                                                            Saving...
                                                        </div>
                                                    )}
                                                    {!isSavingTermEdits && lastSaveTime && Object.keys(termUpdates).length > 0 && (
                                                        <div className="flex items-center gap-1 text-xs text-green-600 bg-green-500/10 px-2 py-1 rounded">
                                                            ✓ Auto-saved
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <CardDescription className="text-muted-foreground">
                                                Terms have been successfully extracted and standardized from your document. 
                                                Review the terms below, then proceed.
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            {/* Terms Table */}
                                            {shouldShowTermsTable(status) && (
                                                <div>
                                                    {(() => {
                                                        const latestCSV = getAppropriateCSV(status.files, status.status, status.reviewStage);
                                                        return latestCSV ? (
                                                            <div>
                                                                <CSVViewer 
                                                                    projectId={projectId || ''} 
                                                                    filename={latestCSV} 
                                                                    reviewStage={status.reviewStage} 
                                                                    reviewData={status.reviewData} 
                                                                    showHeader={true}
                                                                    relationshipData={relationshipData}
                                                                    onTermUpdate={setTermUpdatesWithDebug}
                                                                    onUniqueTermsCountChange={setUniqueTermsCount}
                                                                    onTermClick={handleRowClick}
                                                                    onScrollToTermReady={handleScrollToTermReady}
                                                                    currentStatus={status.status}
                                                                    allowDelete={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                    allowAdd={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                    onRowsDeleted={handleRowsDeleted}
                                                                    onRowAdded={handleRowAdded}
                                                                    pageRange={status.pageRange}
                                                                    onGetModifiedCSVData={handleGetModifiedCSVData}
                                                                    makeAuthenticatedRequest={makeAuthenticatedRequest}
                                                                    deletedRows={deletedRows}
                                                                    addedTerms={addedTerms}
                                                                    addedTermsDetails={addedTermsDetails}
                                                                    onDeletedRowsUpdate={handleDeletedRowsUpdate}
                                                                    onAddedTermsUpdate={handleAddedTermsUpdate}
                                                                    c1Decisions={c1Decisions}
                                                                    c2Decisions={c2Decisions}
                                                                    c3Decisions={c3Decisions}
                                                                    c1ParentMap={c1ParentMap}
                                                                    c1CrossRefMap={c1CrossRefMap}
                                                                    termUpdates={termUpdates}
                                                                />
                                                            </div>
                                                        ) : null;
                                                    })()}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                    
                                    {/* Continue Action Section */}
                                    <Card className="bg-muted/10">
                                        <CardContent className="pt-6">
                                            <div className="bg-muted/20 border border-border/30 rounded-lg p-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h4 className="font-medium text-foreground">Ready for Term Analysis (Part 1)</h4>
                                                        <p className="text-sm text-muted-foreground">
                                                            The AI will perform <strong>Duplicate Analysis</strong> and <strong>Relationship Analysis</strong> to identify duplicative terms and to suggest hierarchical groupings and cross-references.
                                                        </p>
                                                    </div>
                                                    <Button 
                                                        onClick={(e) => {
                                                            console.log('[ProjectDetail] Continue button clicked - event details:', {
                                                                type: e.type,
                                                                target: e.target,
                                                                currentTarget: e.currentTarget,
                                                                bubbles: e.bubbles,
                                                                defaultPrevented: e.defaultPrevented
                                                            });
                                                            console.log('[ProjectDetail] Continue button clicked');
                                                            console.log('[ProjectDetail] Current project status:', status?.status);
                                                            console.log('[ProjectDetail] Current project ID:', projectId);
                                                            console.log('[ProjectDetail] Auth token exists:', !!localStorage.getItem('orca_auth_token'));
                                                            console.log('[ProjectDetail] isSubmitting:', isSubmitting);
                                                            console.log('[ProjectDetail] Button disabled:', isSubmitting || !projectId);
                                                            
                                                            // Prevent any event issues
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            
                                                            handleContinueToStage('C1'); // Use new two-stage grouping system
                                                        }}
                                                        className="bg-blue-600 hover:bg-blue-700 text-white relative z-10"
                                                        disabled={isSubmitting || !projectId}
                                                        style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10 }}
                                                        onMouseEnter={() => {
                                                            console.log('[ProjectDetail] Continue button hover - disabled:', isSubmitting || !projectId, 'isSubmitting:', isSubmitting, 'projectId:', projectId);
                                                        }}
                                                        onMouseDown={(e) => {
                                                            console.log('[ProjectDetail] Continue button mousedown event');
                                                        }}
                                                        onMouseUp={(e) => {
                                                            console.log('[ProjectDetail] Continue button mouseup event');
                                                        }}
                                                    >
                                                        {isSubmitting ? (
                                                            <span className="loading-dots"></span>
                                                        ) : (
                                                            'Continue'
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            }
                            rightPanel={
                                !shouldShowUnifiedLoadingScreen() ? (
                                <AdvancedPDFViewer 
                                    projectId={projectId || ''} 
                                    filename={status.originalFilename} 
                                    highlightedTerm={highlightedTerm} 
                                    currentPage={currentPage} 
                                    title="Document Display"
                                    onClearHighlight={handleClearHighlight}
                                    termUpdates={termUpdates}
                                    isMinimized={isPdfMinimized}
                                    onToggleMinimize={handleTogglePdfMinimize}
                                />) : null
                            }
                            defaultLeftWidth={65}
                            minLeftWidth={40}
                            maxLeftWidth={80}
                            isRightPanelMinimized={isPdfMinimized}
                            onToggleRightPanel={handleTogglePdfMinimize}
                            stickyTopOffset={120}
                        />
                    </div>
                )}

                {/* Mobile view for Stage B Complete */}
                {status.status === 'Stage B Complete' && (
                    <div className="lg:hidden space-y-6">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <CardTitle>Review Extracted Terms</CardTitle>
                                    </div>
                                </div>
                                <CardDescription>
                                    Terms have been successfully extracted and standardized from your document. 
                                    Review the terms below, then proceed.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {/* Terms Table */}
                                {shouldShowTermsTable(status) && (
                                    <div>
                                        {(() => {
                                            const latestCSV = getAppropriateCSV(status.files, status.status, status.reviewStage);
                                            return latestCSV ? (
                                                <div>
                                                    <CSVViewer 
                                                        projectId={projectId || ''} 
                                                        filename={latestCSV} 
                                                        reviewStage={status.reviewStage} 
                                                        reviewData={status.reviewData} 
                                                        showHeader={true}
                                                        relationshipData={relationshipData}
                                                        onTermUpdate={setTermUpdatesWithDebug}
                                                        onUniqueTermsCountChange={setUniqueTermsCount}
                                                        onTermClick={handleRowClick}
                                                        onScrollToTermReady={handleScrollToTermReady}
                                                        currentStatus={status.status}
                                                        allowDelete={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                        allowAdd={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                        onRowsDeleted={handleRowsDeleted}
                                                        onRowAdded={handleRowAdded}
                                                        pageRange={status.pageRange}
                                                        onGetModifiedCSVData={handleGetModifiedCSVData}
                                                        makeAuthenticatedRequest={makeAuthenticatedRequest}
                                                        deletedRows={deletedRows}
                                                        addedTerms={addedTerms}
                                                        addedTermsDetails={addedTermsDetails}
                                                        onDeletedRowsUpdate={handleDeletedRowsUpdate}
                                                        onAddedTermsUpdate={handleAddedTermsUpdate}
                                                        c1Decisions={c1Decisions}
                                                        c2Decisions={c2Decisions}
                                                        c3Decisions={c3Decisions}
                                                        c1ParentMap={c1ParentMap}
                                                        c1CrossRefMap={c1CrossRefMap}
                                                        termUpdates={termUpdates}
                                                    />
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                        
                        {/* Continue Action Section */}
                        <Card>
                            <CardContent className="pt-6">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-medium text-blue-900">Ready for Term Analysis (Part 1)</h4>
                                            <p className="text-sm text-blue-700">
                                                The AI will perform <strong>Duplicate Analysis</strong> to identify identical terms, followed by <strong>Relationship Analysis</strong> to suggest hierarchical groupings and cross-references.
                                            </p>
                                        </div>
                                        <Button 
                                            onClick={(e) => {
                                                console.log('[ProjectDetail] Continue button clicked - event details:', {
                                                    type: e.type,
                                                    target: e.target,
                                                    currentTarget: e.currentTarget,
                                                    bubbles: e.bubbles,
                                                    defaultPrevented: e.defaultPrevented
                                                });
                                                console.log('[ProjectDetail] Continue button clicked');
                                                console.log('[ProjectDetail] Current project status:', status?.status);
                                                console.log('[ProjectDetail] Current project ID:', projectId);
                                                console.log('[ProjectDetail] Auth token exists:', !!localStorage.getItem('orca_auth_token'));
                                                console.log('[ProjectDetail] isSubmitting:', isSubmitting);
                                                console.log('[ProjectDetail] Button disabled:', isSubmitting || !projectId);
                                                
                                                // Prevent any event issues
                                                e.preventDefault();
                                                e.stopPropagation();
                                                
                                                handleContinueToStage('C1'); // Use new two-stage grouping system
                                            }}
                                            className="bg-blue-600 hover:bg-blue-700 text-white relative z-10"
                                            disabled={isSubmitting || !projectId}
                                            style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10 }}
                                            onMouseEnter={() => {
                                                console.log('[ProjectDetail] Continue button hover - disabled:', isSubmitting || !projectId, 'isSubmitting:', isSubmitting, 'projectId:', projectId);
                                            }}
                                            onMouseDown={(e) => {
                                                console.log('[ProjectDetail] Continue button mousedown event');
                                            }}
                                            onMouseUp={(e) => {
                                                console.log('[ProjectDetail] Continue button mouseup event');
                                            }}
                                        >
                                            {isSubmitting ? (
                                                <span className="loading-dots"></span>
                                            ) : (
                                                'Continue'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Document Display for Mobile */}
                        <div className="h-[60vh]">
                            {shouldShowUnifiedLoadingScreen() ? (
                                <div className="space-y-6">
                                    {renderUnifiedLoadingScreen(false)}
                                </div>
                            ) : (
                                <AdvancedPDFViewer 
                                    projectId={projectId || ''} 
                                    filename={status.originalFilename} 
                                    highlightedTerm={highlightedTerm} 
                                    currentPage={currentPage} 
                                    title="Document Display"
                                    onClearHighlight={handleClearHighlight}
                                    termUpdates={termUpdates}
                                    isMinimized={isPdfMinimized}
                                    onToggleMinimize={handleTogglePdfMinimize}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Start Workflow Section - show for pending projects OR when payment modal is open, but NOT during processing */}
                {(() => {
                    const shouldShow = (status.status === 'pending' || showPaymentModal) && !(status.status && status.status.includes('Processing'));
                    console.log('🎯 [RENDER] Project config section - shouldShow:', shouldShow, 'status:', status?.status, 'showPaymentModal:', showPaymentModal);
                    return shouldShow;
                })() && (
                    <div className="hidden lg:block mb-6">
                        <ResizablePanels
                            leftPanel={
                                <div className="space-y-4 pr-3">
                                    <div className="bg-card border border-border rounded-lg p-4">
                                        <h3 className="font-medium text-foreground mb-3">Configure Indexing</h3>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Specify which pages to include in the index. This helps exclude acknowledgments, 
                                            table of contents, bibliography, etc.
                                        </p>
                                        
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label htmlFor="start-page" className="block text-sm font-medium text-foreground mb-1">
                                                    Start Page
                                                </label>
                                                <input
                                                    id="start-page"
                                                    type="number"
                                                    min="1"
                                                    defaultValue="1"
                                                    className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                                                    onChange={(e) => setStartPage(parseInt(e.target.value) || 1)}
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="end-page" className="block text-sm font-medium text-foreground mb-1">
                                                    End Page
                                                </label>
                                                <input
                                                    id="end-page"
                                                    type="number"
                                                    min="1"
                                                    placeholder="Auto"
                                                    className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                                                    onChange={(e) => setEndPage(e.target.value ? parseInt(e.target.value) : undefined)}
                                                />
                                            </div>
                                             <div className="col-span-2 flex items-center gap-3">
                                                 <input
                                                     id="has-endnotes"
                                                     type="checkbox"
                                                     checked={endnotesEnabled}
                                                     onChange={(e) => setEndnotesEnabled(e.target.checked)}
                                                 />
                                                 <label htmlFor="has-endnotes" className="text-sm text-foreground">
                                                     Has Endnotes
                                                 </label>
                                                 <input
                                                     id="endnotes-start-page"
                                                     type="number"
                                                     min={1}
                                                     placeholder="Endnotes start page"
                                                     className="ml-4 w-40 px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                                                     disabled={!endnotesEnabled}
                                                     onChange={(e) => setEndnotesStartPage(e.target.value ? parseInt(e.target.value) : undefined)}
                                                 />
                                             </div>
                                        </div>
                                        
                                        {/* Cost Estimator */}
                                        <div className="mt-4">
                                            <CostEstimator
                                                projectId={projectId || ''}
                                                pageRange={{
                                                    startPage,
                                                    endPage,
                                                    endnotesEnabled,
                                                    endnotesStartPage
                                                }}
                                            />
                                        </div>
                                        
                                         <Button 
                                            onClick={handleStartWorkflow}
                                            disabled={isStartingWorkflow}
                                            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                                        >
                                            {isStartingWorkflow ? (
                                                <span className="loading-dots"></span>
                                            ) : (
                                                `Start AI Workflow (Pages ${startPage}${endPage ? ` - ${endPage}` : '+'})`
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            }
                            rightPanel={
                                !shouldShowUnifiedLoadingScreen() ? (
                                <AdvancedPDFViewer 
                                    projectId={projectId || ''} 
                                    filename={status.originalFilename} 
                                    highlightedTerm={highlightedTerm} 
                                    currentPage={currentPage} 
                                    title="Document Preview"
                                    onClearHighlight={handleClearHighlight}
                                    termUpdates={termUpdates}
                                    isMinimized={isPdfMinimized}
                                    onToggleMinimize={handleTogglePdfMinimize}
                                />) : null
                            }
                            defaultLeftWidth={40}
                            minLeftWidth={25}
                            maxLeftWidth={60}
                            isRightPanelMinimized={isPdfMinimized}
                            onToggleRightPanel={handleTogglePdfMinimize}
                            stickyTopOffset={120}
                        />
                    </div>
                )}

                {/* Mobile view for pending workflow - show for pending projects OR when payment modal is open, but NOT during processing */}
                {(status.status === 'pending' || showPaymentModal) && !(status.status && status.status.includes('Processing')) && (
                    <div className="lg:hidden mb-6 space-y-6">
                        <div className="bg-card border border-border rounded-lg p-4">
                            <h3 className="font-medium text-foreground mb-3">Configure Indexing</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Specify which pages to include in the index. This helps exclude acknowledgments, 
                                table of contents, bibliography, etc.
                            </p>
                            
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label htmlFor="start-page-mobile" className="block text-sm font-medium text-foreground mb-1">
                                        Start Page
                                    </label>
                                    <input
                                        id="start-page-mobile"
                                        type="number"
                                        min="1"
                                        defaultValue="1"
                                        className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                                        onChange={(e) => setStartPage(parseInt(e.target.value) || 1)}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="end-page-mobile" className="block text-sm font-medium text-foreground mb-1">
                                        End Page
                                    </label>
                                    <input
                                        id="end-page-mobile"
                                        type="number"
                                        min="1"
                                        placeholder="Auto"
                                        className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                                        onChange={(e) => setEndPage(e.target.value ? parseInt(e.target.value) : undefined)}
                                    />
                                </div>
                            </div>
                            
                            {/* Cost Estimator - Mobile */}
                            <div className="mt-4">
                                <CostEstimator
                                    projectId={projectId || ''}
                                    pageRange={{
                                        startPage,
                                        endPage,
                                        endnotesEnabled,
                                        endnotesStartPage
                                    }}
                                />
                            </div>
                            
                            <Button 
                                onClick={handleStartWorkflow}
                                disabled={isStartingWorkflow}
                                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                            >
                                {isStartingWorkflow ? (
                                    <span className="loading-dots"></span>
                                ) : (
                                    `Start AI Workflow (Pages ${startPage}${endPage ? ` - ${endPage}` : '+'})`
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Review Components */}

                {status.reviewStage === 'C' && status.reviewData && (
                    <div className="hidden lg:block">
                        <ResizablePanels
                            leftPanel={
                                <div className="space-y-6 pr-3">
                                    <div className="space-y-6 py-2">
                                        <Card key={`review-c-${projectId}`}>
                                            <CardHeader>
                                                <CardTitle>Term Grouping Review</CardTitle>
                                                <CardDescription>
                                                    Review the AI-suggested term groupings below. You can see all your original terms in the table at the bottom.
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                {/* Show retry button if no suggestions */}
                                                {(!status.reviewData.suggestions || status.reviewData.suggestions.length === 0) && (
                                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <h4 className="font-medium text-yellow-900">No Grouping Suggestions Found</h4>
                                                                <p className="text-sm text-yellow-700 mt-1">
                                                                    The AI may still be generating grouping suggestions, or there may be a loading issue.
                                                                    You can wait a moment and retry, or proceed without grouping.
                                                                </p>
                                                            </div>
                                                            <div className="flex flex-col gap-2 ml-4">
                                                                <Button 
                                                                    onClick={() => {
                                                                        console.log('[ProjectDetail] Manually retrying Stage C data fetch...');
                                                                        if (projectId) {
                                                                            fetchReviewData(projectId, 'C');
                                                                        }
                                                                    }}
                                                                    className="bg-yellow-600 hover:bg-yellow-700 text-white"
                                                                    size="sm"
                                                                >
                                                                    Retry Loading
                                                                </Button>
                                                                <Button 
                                                                    onClick={() => handleStageSubmission('C', { decisions: [] })}
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="text-yellow-700 border-yellow-300 hover:bg-yellow-50"
                                                                >
                                                                    Proceed Without Grouping
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                <StageC
                                                    stage="C1"
                                                    key={`stage-c-${projectId}`}
                                                    suggestions={status.reviewData.suggestions || []}
                                                    uniqueTerms={status.reviewData.uniqueTerms || []}
                                                    isLoading={status.reviewData.isLoading || false}
                                                    onSubmit={(decisions: any[]) => handleStageSubmission('C', { decisions })}
                                                    onScrollToTerm={enhancedScrollToTerm || undefined}
                                                    onDecisionsChange={setStageCDecisions}
                                                    projectId={projectId || ''}
                                                />
                                                
                                                {/* Debug info for review data */}
                                                {process.env.NODE_ENV === 'development' && (
                                                    <details className="mt-4 text-xs">
                                                        <summary className="cursor-pointer text-gray-500">Debug: Review Data</summary>
                                                        <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                                                            {JSON.stringify(status.reviewData, null, 2)}
                                                        </pre>
                                                    </details>
                                                )}
                                            </CardContent>
                                        </Card>
                                        
                                        {/* Terms section below manual review - now always shown and properly spaced */}
                                        <Card>
                                            <CardHeader>
                                                <CardTitle>All Terms ({uniqueTermsCount} unique)</CardTitle>
                                                <CardDescription>
                                                    All extracted terms for reference while making grouping decisions. Click any term to navigate to its location in the PDF.
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                {(() => {
                                                    const generatedFiles = getGeneratedFiles(status.files, status.originalFilename);
                                                    const latestCSV = getAppropriateCSV(status.files, status.status, status.reviewStage);
                                                    
                                                    return (
                                                        <div>
                                                            {latestCSV && (
                                                                <CSVViewer 
                                                                    projectId={projectId || ''} 
                                                                    filename={latestCSV} 
                                                                    reviewStage={status.reviewStage} 
                                                                    reviewData={status.reviewData} 
                                                                    showHeader={true}
                                                                    relationshipData={relationshipData}
                                                                    onUniqueTermsCountChange={setUniqueTermsCount}
                                                                    onTermClick={handleRowClick}
                                                                    onScrollToTermReady={handleScrollToTermReady}
                                                                    currentStatus={status.status}
                                                                    allowDelete={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                    allowAdd={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                    onRowsDeleted={handleRowsDeleted}
                                                                    onRowAdded={handleRowAdded}
                                                                    onGetModifiedCSVData={handleGetModifiedCSVData}
                                                                    deletedRows={deletedRows}
                                                                    addedTerms={addedTerms}
                                                                    addedTermsDetails={addedTermsDetails}
                                                                    onDeletedRowsUpdate={handleDeletedRowsUpdate}
                                                                    onAddedTermsUpdate={handleAddedTermsUpdate}
                                                                    c1Decisions={c1Decisions}
                                                                    c2Decisions={c2Decisions}
                                                                    c3Decisions={c3Decisions}
                                                                    c1ParentMap={c1ParentMap}
                                                                    c1CrossRefMap={c1CrossRefMap}
                                                                    termUpdates={termUpdates}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                </CardContent>
                            </Card>
                            
                            {/* Bottom padding to ensure content isn't cut off */}
                            <div className="pb-8"></div>
                        </div>
                    </div>
                            }
                            rightPanel={
                                !shouldShowUnifiedLoadingScreen() ? (
                                <AdvancedPDFViewer 
                                    projectId={projectId || ''} 
                                    filename={status.originalFilename} 
                                    highlightedTerm={highlightedTerm} 
                                    currentPage={currentPage} 
                                    title="Document Display"
                                    onClearHighlight={handleClearHighlight}
                                    termUpdates={termUpdates}
                                    isMinimized={isPdfMinimized}
                                    onToggleMinimize={handleTogglePdfMinimize}
                                />) : null
                            }
                            defaultLeftWidth={65}
                            minLeftWidth={40}
                            maxLeftWidth={80}
                            isRightPanelMinimized={isPdfMinimized}
                            onToggleRightPanel={handleTogglePdfMinimize}
                            stickyTopOffset={120}
                        />
                    </div>
                )} 
                {status.reviewStage === 'D' && status.reviewData && (
                    <div className="hidden lg:block">
                        <StageD
                            key={`stage-d-${projectId}`}
                            reviewData={{
                                termsWithContext: Array.isArray(status.reviewData.termsWithContext) ? status.reviewData.termsWithContext : [],
                                uniqueTerms: Array.isArray(status.reviewData.uniqueTerms) ? status.reviewData.uniqueTerms : [],
                                workflowId: status.reviewData.workflowId
                            }}
                            projectId={projectId || ''}
                            filename={status.originalFilename}
                            onSubmitReview={(decisions: any[]) => handleStageSubmission('D', { decisions })}
                            isSubmitting={isSubmitting}
                            pageRange={status.pageRange}
                        />
                    </div>
                )}

                {/* Mobile view for Stage D review */}
                {status.reviewStage === 'D' && status.reviewData && (
                    <div className="lg:hidden space-y-6">
                        <StageD
                            key={`stage-d-mobile-${projectId}`}
                            reviewData={{
                                termsWithContext: Array.isArray(status.reviewData.termsWithContext) ? status.reviewData.termsWithContext : [],
                                uniqueTerms: Array.isArray(status.reviewData.uniqueTerms) ? status.reviewData.uniqueTerms : [],
                                workflowId: status.reviewData.workflowId
                            }}
                            projectId={projectId || ''}
                            filename={status.originalFilename}
                            onSubmitReview={(decisions: any[]) => handleStageSubmission('D', { decisions })}
                            isSubmitting={isSubmitting}
                            pageRange={status.pageRange}
                        />
                    </div>
                )}

                {/* NEW: Stage C1 (Duplicate Detection) Review */}
                {status.reviewStage === 'C1' && status.reviewData && (
                    <div className="hidden lg:block">
                        <ResizablePanels
                            leftPanel={
                                <div className="space-y-6 pr-3">
                                    <div className="space-y-6 py-2">
                                        <Card key={`review-c1-${projectId}`} className="bg-muted/10">
                                            <CardHeader className="bg-muted/10 border-b border-border/20">
                                                <CardTitle className="text-foreground text-xl font-semibold">Duplicate Detection & Relationship Review</CardTitle>
                                                <CardDescription className="text-muted-foreground">
                                                    Review AI‑suggested duplicate terms and relationships. You can merge terms, keep them separate, assign subentries, or add cross‑references.
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <StageC
                                                    stage="C1"
                                                    key={`stage-c1-${projectId}`}
                                                    suggestions={status.reviewData.suggestions || []}
                                                    uniqueTerms={status.reviewData.availableTerms || []}
                                                    isLoading={status.reviewData.isLoading || false}
                                                    onSubmit={(decisions: any[]) => handleStageSubmission('C1', { decisions })}
                                                    onScrollToTerm={enhancedScrollToTerm || undefined}
                                                    onDecisionsChange={(decisions) => {
                                                        console.log(`[ProjectDetail] C1 decisions updated:`, decisions.length);
                                                        
                                                        // Only update state if decisions have actually changed to prevent unnecessary re-renders
                                                        setStageC1Decisions(prev => {
                                                            const prevString = JSON.stringify(prev);
                                                            const newString = JSON.stringify(decisions);
                                                            
                                                            if (prevString !== newString) {
                                                                console.log(`[ProjectDetail] C1 decisions actually changed, updating state`);
                                                                return decisions;
                                                            } else {
                                                                console.log(`[ProjectDetail] C1 decisions unchanged, skipping state update`);
                                                                return prev;
                                                            }
                                                        });
                                                    }}
                                                    projectId={projectId || ''}
                                                />

                                            </CardContent>
                                        </Card>
                                        
                                        {/* Terms reference section */}
                                        <Card className="bg-muted/5">
                                            <CardHeader className="bg-muted/5 border-b border-border/20">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                                                            <div className="w-4 h-4 bg-primary rounded-full"></div>
                                                        </div>
                                                        <div>
                                                            <CardTitle className="text-foreground text-xl font-semibold">
                                                                Review Updated Terms
                                                            </CardTitle>
                                                            <div className="text-sm text-muted-foreground mt-1">
                                                                {uniqueTermsCount} unique terms found
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="bg-muted/5">
                                                {(() => {
                                                    const latestCSV = getAppropriateCSV(status.files, status.status, status.reviewStage);
                                                    
                                                    return (
                                                        <div>
                                                            {latestCSV && (
                                                                <CSVViewer 
                                                                    projectId={projectId || ''} 
                                                                    filename={latestCSV} 
                                                                    reviewStage={status.reviewStage} 
                                                                    reviewData={status.reviewData} 
                                                                    showHeader={true}
                                                                    relationshipData={relationshipData}
                                                                    onUniqueTermsCountChange={setUniqueTermsCount}
                                                                    onTermClick={handleRowClick}
                                                                    onScrollToTermReady={handleScrollToTermReady}
                                                                    currentStatus={status.status}
                                                                    allowDelete={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                    allowAdd={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                    onRowsDeleted={handleRowsDeleted}
                                                                    onRowAdded={handleRowAdded}
                                                                    onGetModifiedCSVData={handleGetModifiedCSVData}
                                                                    deletedRows={deletedRows}
                                                                    addedTerms={addedTerms}
                                                                    addedTermsDetails={addedTermsDetails}
                                                                    onDeletedRowsUpdate={handleDeletedRowsUpdate}
                                                                    onAddedTermsUpdate={handleAddedTermsUpdate}
                                                                    c1Decisions={c1Decisions}
                                                                    c2Decisions={c2Decisions}
                                                                    c3Decisions={c3Decisions}
                                                                    c1ParentMap={relationshipData ? new Map(Object.entries(relationshipData.subentries)) : undefined}
                                                                    c1CrossRefMap={relationshipData ? new Map(Object.entries(relationshipData.crossReferences)) : undefined}
                                                                    termUpdates={termUpdates}
                                                                    pageRange={(() => {
                                                                        const pr = status?.pageRange;
                                                                        console.warn('[ProjectDetail] 🔍 PASSING PAGERANGE TO CSVVIEWER:', {
                                                                            statusPageRange: status?.pageRange,
                                                                            finalPageRange: pr,
                                                                            hasStatus: !!status,
                                                                            statusKeys: status ? Object.keys(status) : 'no status'
                                                                        });
                                                                        return pr;
                                                                    })()}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </CardContent>
                                        </Card>
                                        
                                        {/* Continue Action Section */}
                                        <Card className="bg-muted/10">
                                            <CardContent className="pt-6 bg-muted/10">
                                                <div className="bg-muted/20 border border-border/30 rounded-lg p-4">
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                                        <div className="flex-1">
                                                            <h4 className="font-medium text-foreground">Ready for Term Analysis (Part 2)</h4>
                                                            <p className="text-sm text-muted-foreground">
                                                            The AI will perform <strong>Duplicate Analysis</strong> and <strong>Relationship Analysis</strong> to identify duplicative terms and to suggest hierarchical groupings and cross-references.
                                                            </p>
                                                            {Object.keys(termUpdates).length > 0 && (
                                                                <p className="text-xs text-muted-foreground mt-1">
                                                                    {Object.keys(termUpdates).length} term edit(s) will be included.
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="flex-shrink-0">
                                                            <Button 
                                                                onClick={async () => {
                                                                    console.log('[ProjectDetail] Search for Additional Duplicates and Relationships clicked');
                                                                    console.log('[ProjectDetail] Current C1 decisions from state:', stageC1Decisions);
                                                                    
                                                                    // Get the latest decisions directly from the StageC component
                                                                    // This ensures we have the most current state, even if React state is delayed
                                                                    let decisionsToSubmit = stageC1Decisions;
                                                                    
                                                                    // Try to get fresher data from localStorage as backup
                                                                    try {
                                                                        const storageKey = `stagec_decisions_${projectId}_C1_v2`;
                                                                        const stored = localStorage.getItem(storageKey);
                                                                        if (stored) {
                                                                            const parsedData = JSON.parse(stored);
                                                                            if (parsedData.decisions && parsedData.decisions.length > 0) {
                                                                                console.log('[ProjectDetail] Using fresher decisions from localStorage:', parsedData.decisions.length);
                                                                                decisionsToSubmit = parsedData.decisions;
                                                                            }
                                                                        }
                                                                    } catch (error) {
                                                                        console.warn('[ProjectDetail] Failed to get fresh decisions from localStorage:', error);
                                                                    }
                                                                    
                                                                    console.log('[ProjectDetail] Final decisions to submit:', decisionsToSubmit.length);
                                                                    
                                                                    // IMMEDIATELY transition to C2 interface with loading state
                                                                    setStatus(prev => prev ? {
                                                                        ...prev,
                                                                        status: 'Processing Stage C2: Relationship Analysis',
                                                                        reviewStage: 'C2', // Immediately show C2 interface
                                                                        reviewData: {
                                                                            suggestions: [], // Empty initially
                                                                            availableTerms: [], // Will be populated when AI completes
                                                                            isLoading: true // Flag to show loading state
                                                                        }
                                                                    } : null);
                                                                    
                                                                    if (decisionsToSubmit.length > 0) {
                                                                        // Submit the C1 decisions, which will automatically trigger C1 → C2 transition
                                                                        await handleStageSubmission('C1', { decisions: decisionsToSubmit });
                                                                    } else {
                                                                        console.warn('[ProjectDetail] No C1 decisions available, submitting empty array');
                                                                        // Submit empty decisions if none are available
                                                                        await handleStageSubmission('C1', { decisions: [] });
                                                                    }
                                                                }}
                                                                className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                                                                disabled={isSubmitting || !projectId}
                                                            >
                                                                {isSubmitting ? (
                                                                    <span className="loading-dots"></span>
                                                                ) : (
                                                                    'Search for Additional Duplicates and Relationships'
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                        
                                        {/* Bottom padding */}
                                        <div className="pb-8"></div>
                                    </div>
                                </div>
                            }
                            rightPanel={
                                !shouldShowUnifiedLoadingScreen() ? (
                                <AdvancedPDFViewer 
                                    projectId={projectId || ''} 
                                    filename={status.originalFilename} 
                                    highlightedTerm={highlightedTerm} 
                                    currentPage={currentPage} 
                                    title="Document Display"
                                    onClearHighlight={handleClearHighlight}
                                    termUpdates={termUpdates}
                                    isMinimized={isPdfMinimized}
                                    onToggleMinimize={handleTogglePdfMinimize}
                                />) : null
                            }
                            defaultLeftWidth={65}
                            minLeftWidth={40}
                            maxLeftWidth={80}
                            isRightPanelMinimized={isPdfMinimized}
                            onToggleRightPanel={handleTogglePdfMinimize}
                            stickyTopOffset={120}
                        />
                    </div>
                )}

                {/* NEW: Stage C2 (Relationship Analysis) Review */}
                {status.reviewStage === 'C2' && status.reviewData && !shouldShowUnifiedLoadingScreen() && (
                     <div className="hidden lg:block">
                         <ResizablePanels
                             leftPanel={
                                 <div className="space-y-6 pr-3">
                                     <div className="space-y-6 py-2">
                                         <Card key={`review-c2-${projectId}`} className="bg-muted/10">
                                             <CardHeader className="bg-muted/10 border-b border-border/20">
                                                 <CardTitle className="text-foreground text-xl font-semibold">Duplicate Detection & Relationship Review</CardTitle>
                                                 <CardDescription className="text-muted-foreground">
                                                     Review AI‑suggested duplicate terms and relationships. You can merge terms, keep them separate, assign subentries, or add cross‑references.
                                                 </CardDescription>
                                             </CardHeader>
                                             <CardContent>
                                                                                                     <StageC
                                                         stage="C2"
                                                         key={`stage-c2-${projectId}`}
                                                         suggestions={(status.reviewStage === 'C2' && Array.isArray(status.reviewData?.suggestions)) ? (status.reviewData!.suggestions as any[]) : []}
                                                         uniqueTerms={Array.isArray(status.reviewData?.availableTerms) ? (status.reviewData!.availableTerms as any[]) : []}
                                                         isLoading={!!status.reviewData?.isLoading && status.status?.includes('Processing Stage C2')}
                                                         onSubmit={(decisions: any[]) => handleStageSubmission('C2', { decisions })}
                                                         onScrollToTerm={enhancedScrollToTerm || undefined}
                                                         onDecisionsChange={(decisions) => {
                                                         console.log(`[ProjectDetail] C2 decisions updated:`, decisions.length);
                                                         
                                                         // Only update state if decisions have actually changed to prevent unnecessary re-renders
                                                         setStageC2Decisions(prev => {
                                                             const prevString = JSON.stringify(prev);
                                                             const newString = JSON.stringify(decisions);
                                                             
                                                             if (prevString !== newString) {
                                                                 console.log(`[ProjectDetail] C2 decisions actually changed, updating state`);
                                                                 return decisions;
                                                             } else {
                                                                 console.log(`[ProjectDetail] C2 decisions unchanged, skipping state update`);
                                                                 return prev;
                                                             }
                                                         });
                                                     }}
                                                     projectId={projectId || ''}
                                                 />

                                             </CardContent>
                                         </Card>
                                         
                                         {/* Terms reference section - only show when analysis is complete */}
                                         {!status.reviewData.isLoading && !status.status.includes('Processing Stage C2') && (
                                             <Card className="bg-muted/5">
                                                 <CardHeader className="bg-muted/5 border-b border-border/20">
                                                     <div className="flex items-center justify-between">
                                                         <div className="flex items-center gap-3">
                                                             <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                                                                 <div className="w-4 h-4 bg-primary rounded-full"></div>
                                                             </div>
                                                             <div>
                                                                 <CardTitle className="text-foreground text-xl font-semibold">
                                                                     Review Updated Terms
                                                                 </CardTitle>
                                                                 <div className="text-sm text-muted-foreground mt-1">
                                                                     {uniqueTermsCount} unique terms found
                                                                 </div>
                                                             </div>
                                                         </div>
                                                     </div>
                                                 </CardHeader>
                                                 <CardContent className="bg-muted/5">
                                                     {(() => {
                                                         const latestCSV = getAppropriateCSV(status.files, status.status, status.reviewStage);
                                                         
                                                         return (
                                                             <div>
                                                                 {latestCSV && (
                                                                     <CSVViewer 
                                                                         projectId={projectId || ''} 
                                                                         filename={latestCSV} 
                                                                         reviewStage={status.reviewStage} 
                                                                         reviewData={status.reviewData} 
                                                                         showHeader={true}
                                                                         relationshipData={relationshipData}
                                                                         onUniqueTermsCountChange={setUniqueTermsCount}
                                                                         onTermClick={handleRowClick}
                                                                         onScrollToTermReady={handleScrollToTermReady}
                                                                         currentStatus={status.status}
                                                                         allowDelete={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                         allowAdd={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                         onRowsDeleted={handleRowsDeleted}
                                                                         onRowAdded={handleRowAdded}
                                                                         onGetModifiedCSVData={handleGetModifiedCSVData}
                                                                         deletedRows={deletedRows}
                                                                         addedTerms={addedTerms}
                                                                         addedTermsDetails={addedTermsDetails}
                                                                         onDeletedRowsUpdate={handleDeletedRowsUpdate}
                                                                         onAddedTermsUpdate={handleAddedTermsUpdate}
                                                                         c1Decisions={c1Decisions}
                                                                         c2Decisions={c2Decisions}
                                                                         c3Decisions={c3Decisions}
                                                                         c1ParentMap={relationshipData ? new Map(Object.entries(relationshipData.subentries)) : undefined}
                                                                         c1CrossRefMap={relationshipData ? new Map(Object.entries(relationshipData.crossReferences)) : undefined}
                                                                         termUpdates={termUpdates}
                                                                     />
                                                                 )}
                                                             </div>
                                                         );
                                                     })()}
                                                 </CardContent>
                                             </Card>
                                         )}
                                         
                                         {/* Continue Action Section - only show when not loading */}
                                         {!status.reviewData.isLoading && (
                                             <Card className="bg-muted/10">
                                                 <CardContent className="pt-6 bg-muted/10">
                                                     <div className="bg-muted/20 border border-border/30 rounded-lg p-4">
                                                         <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                                             <div className="flex-1">
                                                                 <h4 className="font-medium text-foreground">Ready for Term Analysis (Part 3)</h4>
                                                                 <p className="text-sm text-muted-foreground">
                                                                 The AI will perform <strong>Duplicate Analysis</strong> and <strong>Relationship Analysis</strong> to identify duplicative terms and to suggest hierarchical groupings and cross-references.
                                                                 </p>
                                                             </div>
                                                             <div className="flex-shrink-0">
                                                                 <Button 
                                                                     onClick={() => {
                                                                         console.log('[ProjectDetail] Continue to Final Duplicate Detection clicked');
                                                                         console.log('[ProjectDetail] Current C2 decisions:', stageC2Decisions);
                                                                         
                                                                         if (stageC2Decisions.length > 0) {
                                                                             // Submit the C2 decisions
                                                                             handleStageSubmission('C2', { decisions: stageC2Decisions });
                                                                         } else {
                                                                             console.warn('[ProjectDetail] No C2 decisions available, submitting empty array');
                                                                             // Submit empty decisions if none are available
                                                                             handleStageSubmission('C2', { decisions: [] });
                                                                         }
                                                                     }}
                                                                     className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                                                                     disabled={isSubmitting || !projectId}
                                                                 >
                                                                     {isSubmitting ? (
                                                                         <span className="loading-dots"></span>
                                                                     ) : (
                                                                         'Search for Additional Duplicates and Relationships'
                                                                     )}
                                                                 </Button>
                                                             </div>
                                                         </div>
                                                     </div>
                                                 </CardContent>
                                             </Card>
                                         )}
                                         
                                         {/* Bottom padding */}
                                         <div className="pb-8"></div>
                                     </div>
                                 </div>
                             }
                             rightPanel={
                                 !shouldShowUnifiedLoadingScreen() ? (
                                 <AdvancedPDFViewer 
                                     projectId={projectId || ''} 
                                     filename={status.originalFilename} 
                                     highlightedTerm={highlightedTerm} 
                                     currentPage={currentPage} 
                                     title="Document Display"
                                     onClearHighlight={handleClearHighlight}
                                     termUpdates={termUpdates}
                                     isMinimized={isPdfMinimized}
                                     onToggleMinimize={handleTogglePdfMinimize}
                                 />) : null
                             }
                             defaultLeftWidth={65}
                             minLeftWidth={40}
                             maxLeftWidth={80}
                             isRightPanelMinimized={isPdfMinimized}
                             onToggleRightPanel={handleTogglePdfMinimize}
                             stickyTopOffset={120}
                         />
                     </div>
                 )}

                {/* NEW: Stage C3 (Final Duplicate Detection) Review */}
                {status.reviewStage === 'C3' && status.reviewData && (
                    <div className="hidden lg:block">
                        <ResizablePanels
                            leftPanel={
                                <div className="space-y-6 pr-3">
                                    <div className="space-y-6 py-2">
                                        <Card key={`review-c3-${projectId}`} className="bg-muted/10">
                                            <CardHeader className="bg-muted/10 border-b border-border/20">
                                                <CardTitle className="text-foreground text-xl font-semibold">Duplicate Detection & Relationship Review</CardTitle>
                                                <CardDescription className="text-muted-foreground">
                                                    Review AI‑suggested duplicate terms and relationships. You can merge terms, keep them separate, assign subentries, or add cross‑references.
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                                                                    <StageC
                                                        stage="C3"
                                                        key={`stage-c3-${projectId}`}
                                                        suggestions={(status.reviewStage === 'C3' && Array.isArray(status.reviewData?.suggestions)) ? (status.reviewData!.suggestions as any[]) : []}
                                                        uniqueTerms={Array.isArray(status.reviewData?.availableTerms) ? (status.reviewData!.availableTerms as any[]) : []}
                                                        isLoading={!!status.reviewData?.isLoading && status.status?.includes('Processing Stage C3')}
                                                        onSubmit={(decisions: any[]) => handleStageSubmission('C3', { decisions })}
                                                        onScrollToTerm={enhancedScrollToTerm || undefined}
                                                        onDecisionsChange={(decisions) => {
                                                            console.log(`[ProjectDetail] C3 decisions updated:`, decisions.length);
                                                            
                                                            // Only update state if decisions have actually changed to prevent unnecessary re-renders
                                                            setStageC3Decisions(prev => {
                                                                const prevString = JSON.stringify(prev);
                                                                const newString = JSON.stringify(decisions);
                                                                
                                                                if (prevString !== newString) {
                                                                    console.log(`[ProjectDetail] C3 decisions actually changed, updating state`);
                                                                    return decisions;
                                                                } else {
                                                                    console.log(`[ProjectDetail] C3 decisions unchanged, skipping state update`);
                                                                    return prev;
                                                                }
                                                            });
                                                        }}
                                                        projectId={projectId || ''}
                                                    />

                                            </CardContent>
                                        </Card>
                                        
                                        {/* Terms reference section - only show when analysis is complete */}
                                        {!status.reviewData.isLoading && !status.status.includes('Processing Stage C3') && (
                                            <Card className="bg-muted/5">
                                                <CardHeader className="bg-muted/5 border-b border-border/20">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                                                                <div className="w-4 h-4 bg-primary rounded-full"></div>
                                                            </div>
                                                            <div>
                                                                <CardTitle className="text-foreground text-xl font-semibold">
                                                                    Review Updated Terms
                                                                </CardTitle>
                                                                <div className="text-sm text-muted-foreground mt-1">
                                                                    {uniqueTermsCount} unique terms found
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="bg-muted/5">
                                                    {(() => {
                                                        const latestCSV = getAppropriateCSV(status.files, status.status, status.reviewStage);
                                                        
                                                        return (
                                                            <div>
                                                                {latestCSV && (
                                                                    <CSVViewer 
                                                                        projectId={projectId || ''} 
                                                                        filename={latestCSV} 
                                                                        reviewStage={status.reviewStage} 
                                                                        reviewData={status.reviewData} 
                                                                        showHeader={true}
                                                                        onUniqueTermsCountChange={setUniqueTermsCount}
                                                                        onTermClick={handleRowClick}
                                                                        onScrollToTermReady={handleScrollToTermReady}
                                                                        currentStatus={status.status}
                                                                        allowDelete={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                        allowAdd={status.status === 'Stage B Complete' || status.status === 'completed'}
                                                                        onRowsDeleted={handleRowsDeleted}
                                                                        onRowAdded={handleRowAdded}
                                                                        onGetModifiedCSVData={handleGetModifiedCSVData}
                                                                        deletedRows={deletedRows}
                                                                        addedTerms={addedTerms}
                                                                        addedTermsDetails={addedTermsDetails}
                                                                        onDeletedRowsUpdate={handleDeletedRowsUpdate}
                                                                        onAddedTermsUpdate={handleAddedTermsUpdate}
                                                                        c1Decisions={c1Decisions}
                                                                        c2Decisions={c2Decisions}
                                                                        c3Decisions={c3Decisions}
                                                                        c1ParentMap={relationshipData ? new Map(Object.entries(relationshipData.subentries)) : undefined}
                                                                        c1CrossRefMap={relationshipData ? new Map(Object.entries(relationshipData.crossReferences)) : undefined}
                                                                        termUpdates={termUpdates}
                                                                    />
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </CardContent>
                                            </Card>
                                        )}
                                        
                                        {/* Continue Action Section - only show when not loading */}
                                        {!status.reviewData.isLoading && (
                                            <Card className="bg-muted/10">
                                                <CardContent className="pt-6">
                                                    <div className="bg-muted/30 border border-border/50 rounded-lg p-4">
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                                            <div className="flex-1">
                                                                <h4 className="font-medium text-foreground">Ready for Subentry Generation</h4>
                                                                <p className="text-sm text-muted-foreground">
                                                                    The AI will now generate subentries for each term to create the final index.
                                                                </p>
                                                            </div>
                                                            <div className="flex-shrink-0">
                                                                <Button 
                                                                    onClick={() => {
                                                                        console.log('[ProjectDetail] Generate Subentries clicked');
                                                                        console.log('[ProjectDetail] Current C3 decisions:', stageC3Decisions);
                                                                        
                                                                        if (stageC3Decisions.length > 0) {
                                                                            // Submit the C3 decisions
                                                                            handleStageSubmission('C3', { decisions: stageC3Decisions });
                                                                        } else {
                                                                            console.warn('[ProjectDetail] No C3 decisions available, submitting empty array');
                                                                            // Submit empty decisions if none are available
                                                                            handleStageSubmission('C3', { decisions: [] });
                                                                        }
                                                                    }}
                                                                    className="bg-primary hover:bg-primary/90 text-primary-foreground whitespace-nowrap"
                                                                    disabled={isSubmitting || !projectId}
                                                                >
                                                                    {isSubmitting ? (
                                                                        <span className="loading-dots"></span>
                                                                    ) : (
                                                                        'Generate Subentries'
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )}
                                        
                                        {/* Bottom padding */}
                                        <div className="pb-8"></div>
                                    </div>
                                </div>
                            }
                            rightPanel={
                                !shouldShowUnifiedLoadingScreen() ? (
                                <AdvancedPDFViewer 
                                    projectId={projectId || ''} 
                                    filename={status.originalFilename} 
                                    highlightedTerm={highlightedTerm} 
                                    currentPage={currentPage} 
                                    title="Document Display"
                                    onClearHighlight={handleClearHighlight}
                                    termUpdates={termUpdates}
                                    isMinimized={isPdfMinimized}
                                    onToggleMinimize={handleTogglePdfMinimize}
                                />) : null
                            }
                            defaultLeftWidth={65}
                            minLeftWidth={40}
                            maxLeftWidth={80}
                            isRightPanelMinimized={isPdfMinimized}
                            onToggleRightPanel={handleTogglePdfMinimize}
                            stickyTopOffset={120}
                        />
                    </div>
                )}

                {/* UNIFIED LOADING SCREENS - All processing stages use this simplified system */}
                {shouldShowUnifiedLoadingScreen() && status.reviewStage !== 'D' && (
                    <div className="hidden lg:block">
                        {renderUnifiedLoadingScreen(true)}
                    </div>
                )}

                {/* Mobile view for processing stages */}
                {shouldShowUnifiedLoadingScreen() && (status.reviewStage !== 'D') && (
                    <div className="lg:hidden mb-6 space-y-6">
                        {renderUnifiedLoadingScreen(false)}
                    </div>
                )}

                {/* Stage D Error Recovery */}
                {status.reviewStage === 'D' && !shouldShowUnifiedLoadingScreen() && (status.error || status.status?.includes('Error')) && (
                    <div className="hidden lg:block">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <span className="text-amber-600">⚠️</span>
                                    Stage D Processing Issue
                                </CardTitle>
                                <CardDescription>
                                    There was an issue during subentry generation. This can be resolved by retrying the processing.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                        <div className="flex-1">
                                            <h4 className="font-medium text-amber-900">Subentry Generation Failed</h4>
                                            <p className="text-sm text-amber-700">
                                                The AI subentry generation process encountered an error. Click below to retry the processing.
                                            </p>
                                            {status.error && (
                                                <p className="text-xs text-amber-600 mt-2">
                                                    Error: {status.error}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex-shrink-0">
                                            <Button 
                                                onClick={async () => {
                                                    console.log('[ProjectDetail] Retrying Stage D processing...');
                                                    
                                                    // Set loading state
                                                    setStatus(prev => prev ? {
                                                        ...prev,
                                                        status: 'Processing Stage D: Collecting Context',
                                                        error: undefined
                                                    } : null);
                                                    
                                                    try {
                                                        const response = await makeAuthenticatedRequest(
                                                            `/api/projects/${projectId}/retry-stage-d`,
                                                            { method: 'POST', body: JSON.stringify({}) }
                                                        );
                                                        
                                                        if (!response.ok) {
                                                            const errorData = await response.json();
                                                            throw new Error(errorData.error || `Server responded with ${response.status}`);
                                                        }
                                                        
                                                        const result = await response.json();
                                                        console.log('[ProjectDetail] Stage D retry successful:', result);
                                                        
                                                        // Poll for status update
                                                        setTimeout(() => {
                                                            window.location.reload(); // Refresh to show updated Stage D
                                                        }, 2000);
                                                        
                                                    } catch (error: any) {
                                                        console.error('[ProjectDetail] Stage D retry failed:', error);
                                                        setStatus(prev => prev ? {
                                                            ...prev,
                                                            status: 'Error Stage D Retry',
                                                            error: `Retry failed: ${error.message}`
                                                        } : null);
                                                    }
                                                }}
                                                className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                                                disabled={isSubmitting || !projectId}
                                            >
                                                {isSubmitting ? (
                                                    <span className="loading-dots"></span>
                                                ) : (
                                                    'Retry Subentry Generation'
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Stage D Review */}
            </div>
            
            {/* Payment Modal */}
            {showPaymentModal && paymentData && (
                <PaymentModal
                    isOpen={showPaymentModal}
                    onClose={(wasSuccessful = false) => {
                        console.log('💳 [PAYMENT MODAL] Closing modal - user canceled or dismissed');
                        console.log('💳 [PAYMENT MODAL] Current project state before close:', { 
                            hasProject: !!project, 
                            hasStatus: !!status, 
                            projectId,
                            statusValue: status?.status,
                            isLoading,
                            isStartingWorkflow,
                            wasSuccessful
                        });
                        
                        // CRITICAL: Close modal and reset payment state first
                        setShowPaymentModal(false);
                        setPaymentData(null);
                        
                        // CRITICAL: Only reset workflow state when payment was canceled (not successful)
                        if (!wasSuccessful) {
                            console.log('💳 [PAYMENT MODAL] Payment was canceled - resetting workflow state');
                            setIsStartingWorkflow(false);
                        } else {
                            console.log('💳 [PAYMENT MODAL] Payment was successful - keeping workflow state active');
                        }
                        
                        // CRITICAL: Only reset project status when payment was canceled (not successful)
                        // This prevents race conditions with polling/fetchProject calls
                        if (projectId && !wasSuccessful) {
                            const resetProjectStatus = async () => {
                                console.log('💳 [PAYMENT MODAL] Starting status reset process');
                                setIsResettingStatus(true); // Prevent fetchProject from overriding status
                                
                                try {
                                    const { getApiUrl } = await import('../client/lib/config');
                                    const response = await fetch(getApiUrl(`/api/projects/${projectId}/reset-to-pending`), {
                                        method: 'POST',
                                        headers: {
                                            'Authorization': `Bearer ${localStorage.getItem('orca_auth_token')}`,
                                            'Content-Type': 'application/json'
                                        }
                                    });
                                    
                                    if (response.ok) {
                                        console.log('💳 [PAYMENT MODAL] Successfully reset project status to pending on server');
                                        // CRITICAL: Only update local state AFTER server confirms reset
                                        setStatus(prev => prev ? { ...prev, status: 'pending', reviewStage: undefined } : prev);
                                    } else {
                                        console.warn('💳 [PAYMENT MODAL] Failed to reset project status on server:', response.status);
                                        // Fallback: Still set local state even if server fails
                                        setStatus(prev => prev ? { ...prev, status: 'pending', reviewStage: undefined } : prev);
                                    }
                                } catch (error) {
                                    console.error('💳 [PAYMENT MODAL] Error resetting project status:', error);
                                    // Fallback: Still set local state even if server fails
                                    setStatus(prev => prev ? { ...prev, status: 'pending', reviewStage: undefined } : prev);
                                } finally {
                                    console.log('💳 [PAYMENT MODAL] Completing status reset process');
                                    setIsResettingStatus(false); // Re-enable fetchProject status updates
                                }
                            };
                            
                            resetProjectStatus();
                        } else if (!wasSuccessful) {
                            // Fallback: If no projectId but payment was canceled, just set local state
                            setStatus(prev => prev ? { ...prev, status: 'pending', reviewStage: undefined } : prev);
                        }
                        
                        console.log('💳 [PAYMENT MODAL] Payment modal closed, state preserved based on payment outcome');
                        console.log('💳 [PAYMENT MODAL] Final state after close:', { 
                            hasProject: !!project, 
                            hasStatus: !!status, 
                            projectId,
                            statusValue: status?.status // Current status after modal close
                        });
                    }}
                    projectId={projectId || ''}
                    paymentIntentId={paymentData.paymentIntentId}
                    paymentIntentClientSecret={paymentData.paymentIntentClientSecret}
                    costEstimate={paymentData.costEstimate}
                    billingBreakdown={paymentData.billingBreakdown}
                    onPaymentSuccess={handlePaymentSuccess}
                />
            )}
        </div>
    );
}

export default ProjectDetail; 